import { useState, useEffect, useMemo } from "react";
import { Modal, Button } from "react-bootstrap";
import toast from "react-hot-toast";
import { CUENTAS_ER } from "../../constants/clasificacionContable";
import { ESTADOS_COTIZABLE } from "../../constants/estadosEdificio";
import { puedeForzarVinculacion } from "../../constants/permisos";
import { ROLES } from "../../constants/roles";
import { useAuthContext } from "../../context/auth";
import { actualizarActivo, getActivos } from "../../utils/activos";
import { brutoCapex, monedaLocalDePais } from "../../utils/brutoCapex";
import { saldarCapexTargets, reversarCapexTargets } from "../../utils/capex/saldarCapexTargets";
import { getClientesActivos, crearClienteMinimal } from "../../utils/clientes";
import { parsearSwiftDesdePdf } from "../../utils/cobro/parseSwiftPdf";
import { cuotasDisponiblesDesdeLibro } from "../../utils/cobro/resolverSaldo";
import {
  desvincularComisionIngreso,
  getComisionesIngresoNeteadas,
  getFacturasComisionIngresoDisponibles,
  vincularComisionIngreso,
} from "../../utils/comisionesIngresoNeteadas";
import {
  getOcurrenciasRecurrentesPendientes,
  conciliarOcurrenciasRecurrentes,
} from "../../utils/conciliacionCostoRecurrente";
import { getFacturasEmitidas, getFacturasRecibidas } from "../../utils/conciliacionSII";
import { getCreditosConCuotasPendientes, vincularMovimientoACuota } from "../../utils/creditos";
import { resolverDocumentoFiscalId } from "../../utils/documentosFiscales";
import { esNotaCredito } from "../../utils/dte";
import { getEmpleados, getRemuneracionesEmpleado } from "../../utils/empleados";
import {
  getSaldosPorFacturaProveedor,
  getSaldosRecibidasPorFactura,
  TOLERANCIA_REDONDEO_LOCAL,
  RESET_BANCARIO,
  revertirCuotasFacturaPorMovimiento,
} from "../../utils/estadoCuenta";
import { FECHA_CORTE } from "../../utils/estadoCuenta/saldoInicial";
import {
  convertirAMonedaLocal,
  getDolarValue,
  getUFValue,
  getUTMValue,
  loadExchangeRates,
  loadUTM,
} from "../../utils/exchangeRateService";
import { actualizarOperacionFactoring, getGiroEfectivo, getOperacionesFactoring } from "../../utils/factoring";
import { crearMovimiento } from "../../utils/flujoCaja";
import { getEdificiosSaldoPendiente } from "../../utils/getEdificiosSaldoPendiente";
import { getPendientesRemuneracion } from "../../utils/getPendientesRemuneracion";
import { getLineaIdPorPosicion } from "../../utils/lineasFacturacion/getLineaId";
import { liquidoBonoSplit } from "../../utils/liquidoBonoSplit";
import { getCostosLotesFuturos, getLotesFobSaldado } from "../../utils/loteCostos";
import { getLotes } from "../../utils/lotesCompra";
import { confirmarMovimiento, getFacturasYaVinculadas } from "../../utils/movimientosBancarios";
import {
  crearPagosEmpleado,
  eliminarPagosPorMovimiento,
  getEmpleadoIdsConFiniquito,
  getHaberesEmpleado,
  getHaberesEmpleadoMes,
  getPagosEmpleadoMes,
  getPagosEmpleados,
  TIPOS_PAGO_EMPLEADO,
  TIPOS_REQUIEREN_PERIODO,
  TOLERANCIA_SALDO_LIQUIDO,
} from "../../utils/pagosEmpleados";
import { getPlanCobros, getPlanPagos, getPlanPagosPorDocumento } from "../../utils/planCobros";
import { getCuotasActivosFuturas } from "../../utils/planPagosActivos";
import { getProveedores, crearProveedor } from "../../utils/proveedores";
import { remuneracionVigenteParaMes } from "../../utils/remuneracionVigente";
import { normalizarRut } from "../../utils/rut";
import { client } from "../../utils/supabase";
import { useCurrency } from "../../utils/useCurrency";
import {
  evaluarCuadreAplicaciones,
  montoAplicadoFactura,
  ejecutarVinculacionFacturasCuotas,
  ejecutarVinculacionExterna,
  ejecutarVinculacionBoletas,
  escribirEventosSaldo,
} from "../../utils/vincularFacturasCuotas";
import { FacturaExternaForm, useFacturaExternaForm } from "../FacturaExternaModal";
import ProveedorSelector from "./ProveedorSelector";
import "./VincularMovimientoModal.scss";

const ROLES_PAGO_EMPLEADO = [ROLES.GERENCIA, ROLES.ADMINISTRATIVO, ROLES.SUPERADMIN];

// Tolerancias del cuadre al vincular facturas EXTERNAS a un movimiento bancario (gate `extCuadra`).
// Sensibles a la moneda:
// - Cross-currency convertible (banco CLP/PEN, factura USD): 15%. Absorbe el spread cambiario del
//   banco (convierte USD→CLP con SU propio TC, no el del sistema); el peor caso legítimo observado
//   fue 8.6% (Screenfeed marzo). Sigue frenando descuadres gruesos (mes equivocado, monto mal
//   cargado → >15%).
// - Same-currency (misma moneda): 1% — INTACTO. Misma moneda debe cuadrar casi exacto.
const TOLERANCIA_EXT_CROSS_CURRENCY = 0.15;
const TOLERANCIA_EXT_SAME_CURRENCY = 0.01;
// Badge (label + color) por tipo de ítem saldable de la Sección A del path CapEx.
const CAPEX_ITEM_BADGE = {
  cuota: { label: "CUOTA", color: "#2c3e50" },
  comp: { label: "COMPONENTE", color: "#8e44ad" },
  activo: { label: "ACTIVO", color: "#1565c0" },
  fob: { label: "FOB", color: "#00695c" },
};

// ─── Buscador de listas del modal (reutilizable, CapEx-IVA Fase 6) ────────────
// Mismo patrón/look que el buscador SII: filtra por substring en `textos` (nombre/concepto/folio) y
// por dígitos en `montos` (el query se reduce a dígitos y se compara como substring del monto). Vacío
// = no filtra. Regla 10.1 (una sola lógica de filtrado para las listas nuevas).
const coincideBusqueda = (query, { textos = [], montos = [] } = {}) => {
  const q = (query || "").trim().toLowerCase();
  if (!q) return true;
  for (const t of textos) if (t != null && String(t).toLowerCase().includes(q)) return true;
  const dig = q.replace(/\D/g, "");
  if (dig) for (const m of montos) if (m != null && String(m).includes(dig)) return true;
  return false;
};

// Input de búsqueda compartido: mismo look que el resto (form-control-sm). El texto lo gobierna el
// consumidor; al cambiar de sección se resetea (resetBusquedas).
const BuscadorInput = ({ value, onChange, placeholder }) => (
  <input
    type="text"
    className="form-control form-control-sm"
    placeholder={placeholder}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    style={{ marginBottom: 8 }}
  />
);

// Path "Pago institucional": pagos a entidades sin DTE (PREVIRED, SII, banco, etc.).
// cuenta_er solo se setea para 'banco' — los pagos previsionales y tributarios son
// pasivos, no gastos operativos del Estado de Resultados.
const INSTITUCIONES = [
  {
    value: "previred",
    label: "PREVIRED",
    categoria_pago: "cotizaciones_previsionales",
    cuenta_er: null,
    requierePeriodo: true,
  },
  {
    value: "sii_declaracion",
    label: "SII — Declaración mensual",
    categoria_pago: "pago_impuesto",
    cuenta_er: null,
    requierePeriodo: true,
  },
  { value: "tesoreria", label: "Tesorería", categoria_pago: "pago_impuesto", cuenta_er: null, requierePeriodo: false },
  {
    value: "banco",
    label: "Banco — comisión/mantención",
    categoria_pago: "pago_banco",
    cuenta_er: "gastos_bancarios",
    requierePeriodo: false,
  },
  { value: "otro", label: "Otro", categoria_pago: "otro", cuenta_er: null, requierePeriodo: false },
];

const MESES = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

const parseFechaLocal = (str) => {
  if (!str) return new Date();
  const [y, m, d] = str.split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
};

const nuevaLineaComponente = (movimiento, tipoDefault = "sueldo") => {
  const fecha = parseFechaLocal(movimiento?.fecha);
  return {
    tipo: tipoDefault,
    periodo_año: fecha.getFullYear(),
    periodo_mes: fecha.getMonth() + 1,
    monto: "",
    notas: "",
  };
};

// Selector reutilizable de "comisión neteada" (factoring o transferencia recibida). Maneja su
// propia carga vía loaders inyectados; presentación tipo lista + chips vinculados con "×".
// destacar(f) → resalta/ordena primero (≈ comisión por monto, o fecha cercana). NO bloquea nada.
const ComisionNeteadaSelector = ({
  titulo,
  cargarVinculadas,
  cargarDisponibles,
  vincular,
  desvincular,
  destacar,
  formatAmount,
  formatFecha,
  moneda,
  onCambio,
  mostrarDteAnioAnterior,
  setMostrarDteAnioAnterior,
}) => {
  const [vinculadas, setVinculadas] = useState([]);
  const [disponibles, setDisponibles] = useState([]);
  const [busqueda, setBusqueda] = useState("");
  const [loading, setLoading] = useState(false);
  // Fix 2: el click sobre una fila SELECCIONA (highlight); un botón explícito confirma el vínculo.
  const [facturaSelId, setFacturaSelId] = useState(null);

  const recargar = async () => {
    try {
      const [v, d] = await Promise.all([cargarVinculadas(), cargarDisponibles()]);
      setVinculadas(v || []);
      setDisponibles(d || []);
    } catch {
      // silencioso: el panel solo informa
    }
  };

  useEffect(() => {
    recargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleVincular = async (facturaId) => {
    setLoading(true);
    try {
      await vincular(facturaId);
      await recargar();
      setFacturaSelId(null);
      toast.success("Comisión vinculada ✓");
      onCambio?.(); // refresca "Por pagar" en el padre (FlujoCajaV2)
    } catch (err) {
      toast.error("Error al vincular: " + (err.message || ""));
    } finally {
      setLoading(false);
    }
  };

  const handleDesvincular = async (filaId) => {
    setLoading(true);
    try {
      await desvincular(filaId);
      await recargar();
      toast.success("Comisión desvinculada");
      onCambio?.();
    } catch (err) {
      toast.error("Error al desvincular: " + (err.message || ""));
    } finally {
      setLoading(false);
    }
  };

  // añoActualPI vive en el modal padre y no está en scope acá → año actual local para el corte.
  const añoActualCN = new Date().getFullYear();
  const ordenadas = [...disponibles]
    // "Mostrar DTE AA" apagado (default): ocultar DTEs de años anteriores al actual (sin fecha → conservar).
    .filter(
      (f) => mostrarDteAnioAnterior || !f.fecha_emision || parseInt(f.fecha_emision.substring(0, 4), 10) >= añoActualCN
    )
    .filter((f) => {
      if (!busqueda.trim()) return true;
      const q = busqueda.trim().toLowerCase();
      return (
        String(f.folio).includes(q) || String(Math.round(Number(f.monto_total) || 0)).includes(q.replace(/\D/g, ""))
      );
    })
    .sort((a, b) => {
      const sa = destacar?.(a) ? 0 : 1;
      const sb = destacar?.(b) ? 0 : 1;
      if (sa !== sb) return sa - sb;
      return (b.fecha_emision || "").localeCompare(a.fecha_emision || "");
    });

  return (
    <div style={{ marginTop: 12, borderTop: "1px solid #e6dcc0", paddingTop: 10 }}>
      <div style={{ fontSize: "0.8rem", fontWeight: 700, marginBottom: 6 }}>{titulo}</div>

      {vinculadas.length > 0 ? (
        <div style={{ marginBottom: 8 }}>
          {vinculadas.map((c) => (
            <div
              key={c.id}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "4px 10px",
                marginBottom: 3,
                borderRadius: 4,
                background: "#f0f9f4",
                border: "1px solid #198754",
                fontSize: "0.78rem",
              }}
            >
              <span style={{ color: "#198754", fontWeight: 600 }}>
                ✓ Folio {c.factura?.folio ?? c.factura_sii_recibida_id}
                {c.factura?.monto_total != null ? ` · ${formatAmount(c.factura.monto_total, moneda)}` : ""}
              </span>
              <button
                type="button"
                onClick={() => handleDesvincular(c.id)}
                disabled={loading}
                style={{
                  border: "1px solid #c62828",
                  background: "none",
                  borderRadius: 4,
                  color: "#c62828",
                  fontSize: "0.72rem",
                  padding: "1px 7px",
                  cursor: loading ? "not-allowed" : "pointer",
                }}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ fontSize: "0.78rem", color: "#c0392b", fontStyle: "italic", marginBottom: 6 }}>pendiente</div>
      )}

      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
        <input
          type="text"
          className="form-control form-control-sm"
          placeholder="Buscar folio o monto..."
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          style={{ flex: 3, fontSize: "0.8rem" }}
        />
        <label
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            gap: 6,
            cursor: "pointer",
            whiteSpace: "nowrap",
            fontSize: 13,
          }}
        >
          <input
            type="checkbox"
            checked={mostrarDteAnioAnterior}
            onChange={(e) => setMostrarDteAnioAnterior(e.target.checked)}
          />
          Mostrar DTE AA
        </label>
      </div>
      <div style={{ maxHeight: 160, overflowY: "auto", border: "1px solid #dee2e6", borderRadius: 4 }}>
        {ordenadas.length === 0 ? (
          <div style={{ padding: 10, textAlign: "center", color: "#999", fontSize: "0.76rem" }}>
            Sin facturas disponibles
          </div>
        ) : (
          ordenadas.map((f) => {
            const sug = !!destacar?.(f);
            const sel = facturaSelId === f.id;
            return (
              <div
                key={f.id}
                onClick={() => !loading && setFacturaSelId(sel ? null : f.id)}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "5px 10px",
                  borderBottom: "1px solid #f0f0f0",
                  fontSize: "0.76rem",
                  cursor: loading ? "not-allowed" : "pointer",
                  background: sel ? "#e8f4fd" : sug ? "#f0f9f4" : "transparent",
                  fontWeight: sel ? 600 : 400,
                }}
              >
                <span>
                  {sel ? "✓ " : ""}
                  {formatFecha(f.fecha_emision)} · Folio {f.folio}
                  {sug && (
                    <span style={{ marginLeft: 5, color: "#198754", fontSize: "0.68rem", fontWeight: 600 }}>
                      ✓ sugerida
                    </span>
                  )}
                </span>
                <span style={{ fontWeight: 600, color: "#c0392b" }}>{formatAmount(f.monto_total, moneda)}</span>
              </div>
            );
          })
        )}
      </div>

      {facturaSelId != null && (
        <button
          type="button"
          onClick={() => handleVincular(facturaSelId)}
          disabled={loading}
          style={{
            marginTop: 8,
            width: "100%",
            padding: "6px 0",
            border: "none",
            borderRadius: 4,
            background: "#198754",
            color: "#fff",
            fontSize: "0.8rem",
            fontWeight: 600,
            cursor: loading ? "not-allowed" : "pointer",
          }}
        >
          {loading ? "Vinculando..." : "Vincular comisión"}
        </button>
      )}
    </div>
  );
};

const VincularMovimientoModal = ({ show, onClose, movimiento, onSuccess, onRefreshFuturos, comisionGen = null }) => {
  const { pais, rol, id: usuarioId } = useAuthContext();
  const enteFiscal = pais === "Chile" ? "SII" : "SUNAT";
  const { formatAmount } = useCurrency();
  const puedePagarEmpleado = ROLES_PAGO_EMPLEADO.includes(rol);

  const [tipoVinculacion, setTipoVinculacion] = useState(movimiento?.tipo === "egreso" ? "pago" : "cobro");
  const [facturas, setFacturas] = useState([]);
  const [facturaSeleccionada, setFacturaSeleccionada] = useState(null);
  // Selección múltiple. Cuando length===1, contiene la misma que facturaSeleccionada
  // (single mode preservado). Cuando length>=2, activa modo múltiple (sin cuotas, mismo
  // proveedor/cliente, suma exacta). Persistencia: tabla movimiento_bancario_facturas.
  const [facturasSeleccionadas, setFacturasSeleccionadas] = useState([]);
  // Flujo unificado (mov → N facturas): plan de cuotas PENDIENTES por factura seleccionada
  // ({ [facturaId]: [cuotas] }); cada factura de facturasSeleccionadas lleva su `cuotasSeleccionadas`.
  const [planPorFactura, setPlanPorFactura] = useState({});
  // B2 boletas: plan de cuotas DISPONIBLES por boleta seleccionada ({ [boletaId]: [cuotas] }), espejo
  // de `planPorFactura`. Cada boleta de boletasSeleccionadas lleva su `cuotasSeleccionadas`. Vacío para
  // boletas sin plan → cae al input de monto libre (comportamiento previo a B2).
  const [planPorBoleta, setPlanPorBoleta] = useState({});
  // "Forzar" aparece SOLO al intentar confirmar con saldo sin asignar (no mientras se arma el reparto).
  const [intentoForzarConfirm, setIntentoForzarConfirm] = useState(false);
  // Propina de restorán (sin DTE): el usuario marca que el exceso del débito sobre la
  // factura SII es propina. Misma forma que los Gastos OUR (regla 10.79) pero independiente
  // y activado a mano. Se resetea al cambiar de factura o de movimiento.
  const [esPropina, setEsPropina] = useState(false);
  // Vinculación forzosa por faltante (10.108 invertida): el débito pagó MENOS que la Σ de facturas.
  // Solo gerente (1 CHI / 4 PER). Fase 2: presentación del toggle; el write llega en Fase 3.
  const [esForzado, setEsForzado] = useState(false);
  // Path "Préstamo": créditos activos con cuotas pendientes + selección actual.
  const [creditosDisponibles, setCreditosDisponibles] = useState([]);
  const [creditoPrestamo, setCreditoPrestamo] = useState(null);
  const [cuotaPrestamo, setCuotaPrestamo] = useState(null);
  const [busqueda, setBusqueda] = useState("");
  // Buscadores de las listas CapEx (Fase 6): Sección A (proyecciones) y Sección B (activos a ejecutar).
  const [capexBusqueda, setCapexBusqueda] = useState("");
  const [capexActivoBusqueda, setCapexActivoBusqueda] = useState("");
  const [factoringBusqueda, setFactoringBusqueda] = useState(""); // buscador de operaciones de factoring
  const [loading, setLoading] = useState(false);
  // Checkbox "Mostrar DTE AA": apagado por defecto oculta de TODOS los listados de facturas los
  // DTEs cuyo año de fecha_emision sea menor al año actual (corte que se mueve solo con el año).
  // Estado único a nivel del modal, replicado en los 3 rows de buscador (SII, externas, comisión).
  const [mostrarDteAnioAnterior, setMostrarDteAnioAnterior] = useState(false);

  // Modo "Factura externa": "existente" (lista de pendientes con multi-select) o "nueva" (form de carga).
  const [facturasExtPendientes, setFacturasExtPendientes] = useState([]);
  // Saldo FIFO pendiente por factura (Map factura_id → saldoDisplay), del helper canónico. Solo
  // incluye facturas con saldo > 0.01 (las totalmente amortizadas por pagos a cuenta quedan fuera).
  const [saldosExtMap, setSaldosExtMap] = useState(() => new Map());
  const [externasSeleccionadas, setExternasSeleccionadas] = useState([]);
  const [busquedaExt, setBusquedaExt] = useState("");
  const [modoExterno, setModoExterno] = useState("existente");

  // Boletas de honorarios (F2a): documentos de honorario del proveedor, seleccionables en el MISMO
  // listado de "Pago a proveedor" que las facturas SII. `boletasHonorarios` guarda solo las
  // pendientes (con saldo FIFO en el ledger), cada una con `_saldoPendiente`. `boletasSeleccionadas`
  // es el array multi-select de boletas tildadas (espeja `facturasSeleccionadas`): cada una lleva su
  // `montoAsignado` (editable, pre-cargado a min(saldoPendiente, remanente del débito)). Excluyente
  // con `facturasSeleccionadas` — tildar una boleta limpia las facturas y viceversa.
  const [boletasHonorarios, setBoletasHonorarios] = useState([]);
  const [boletasSeleccionadas, setBoletasSeleccionadas] = useState([]);

  const [empleados, setEmpleados] = useState([]);
  const [empleadoId, setEmpleadoId] = useState(null);
  const [empleadoBusqueda, setEmpleadoBusqueda] = useState("");
  const [componentes, setComponentes] = useState([]);
  // Desglose del líquido por período (solo Chile, solo períodos CON bono de vigencia): triple-check
  // read-only cerca de "Total componentes". Array de { anio, mes, liquidoSinBono, bonoNeto, liquidoConBono }.
  // Líneas PENDIENTES (sueldo + bono, todos los períodos con saldo) del empleado — misma fuente que
  // Futuros/RRHH (getPendientesRemuneracion). SOLO LECTURA (Fase 1); el picker/auto-fill es Fase 2.
  const [pendientesRemuneracion, setPendientesRemuneracion] = useState([]);

  // Sub-modo en "Pago a empleado": "remuneracion" (sueldo/bono/transporte/comisión), "adelanto"
  // (movimiento completo imputado a un período) o "caja_chica" (entrega de fondos). Los tres viven
  // acá como sub-modo, no como tab de nivel 1.
  const [modoEmpleado, setModoEmpleado] = useState("remuneracion");
  // Sub-modo "Adelanto": período (mes/año) al que se imputa. El monto es el movimiento COMPLETO (sin
  // input, mismo patrón que Caja Chica). Persiste en pagos_empleados (tipo='adelanto'), NO en
  // movimientos_caja. Excluyente: no se mezcla con Remuneración ni Caja Chica.
  const [adelantoMes, setAdelantoMes] = useState(null);
  const [adelantoAño, setAdelantoAño] = useState(null);
  // Modo de compra en "Pago a proveedor": "sii" (factura SII) o "externa" (doc manual extranjero)
  const [modoCompra, setModoCompra] = useState("sii");
  // Modo en "Cobro de cliente": "sii" (factura SII emitida) o "a_cuenta" (cobro a cuenta corriente del proveedor)
  const [modoCobro, setModoCobro] = useState("sii");
  const [proveedores, setProveedores] = useState([]);

  // Selector inline de entidad cuando la factura SII no tiene cliente_id (cobro) o proveedor_id (pago).
  // Sin esta entidad no se puede crear el asiento en movimientos_caja (Opción A).
  const [entidadFallbackOptions, setEntidadFallbackOptions] = useState([]);
  const [entidadFallbackId, setEntidadFallbackId] = useState(null);
  const [entidadFallbackBusqueda, setEntidadFallbackBusqueda] = useState("");
  // ¿La entidad resuelta a mano se CREÓ recién (insert nuevo) o se ASIGNÓ (existente/seleccionada)?
  // Alimenta el verbo de la notificación al vincular una boleta huérfana (creado vs asignado).
  const [entidadFallbackCreada, setEntidadFallbackCreada] = useState(false);

  // Limpia TODOS los buscadores de listas. Se llama al cambiar de sección/subsección (tab, modo de
  // compra/cobro/empleado, tipo de factoring) para que el filtro de una sección no arrastre a otra (Fase 6).
  const resetBusquedas = () => {
    setBusqueda("");
    setBusquedaExt("");
    setEmpleadoBusqueda("");
    setEntidadFallbackBusqueda("");
    setCapexBusqueda("");
    setCapexActivoBusqueda("");
    setFactoringBusqueda("");
  };

  // Modo "Sin factura": gastos operativos legítimos sin DTE (café con cliente, taxi, propinas).
  const [sfConcepto, setSfConcepto] = useState("");
  const [sfCuentaER, setSfCuentaER] = useState("");
  const [sfProveedorId, setSfProveedorId] = useState(null);
  const [sfProveedorNombre, setSfProveedorNombre] = useState("");
  const [acProveedorId, setAcProveedorId] = useState(null);
  const [acProveedorNombre, setAcProveedorNombre] = useState("");
  const [acNota, setAcNota] = useState("");
  const [acBucket, setAcBucket] = useState(""); // 'factura' | 'credito'
  // Modo "Costo recurrente": concilia ocurrencias mensuales de un recurrente sin factura
  // (alquiler/GGCC de oficina) contra este bancario. Ver conciliacionCostoRecurrente.js.
  const [recProveedorId, setRecProveedorId] = useState(null);
  const [recProveedorNombre, setRecProveedorNombre] = useState("");
  const [recCuentaER, setRecCuentaER] = useState("");
  const [recOcurrencias, setRecOcurrencias] = useState([]); // pendientes del proveedor
  const [recLoadingOcs, setRecLoadingOcs] = useState(false);
  // Selección: Map key→monto asignado (key = `${año}-${mes}-${costo_asociado_id ?? "main"}`).
  const [recSeleccion, setRecSeleccion] = useState(() => new Map());
  // Path "A cuenta" en cobro: cobro a cuenta corriente del proveedor (devolución / anticipo retornado)
  const [acCobroProveedorId, setAcCobroProveedorId] = useState(null);
  const [acCobroProveedorNombre, setAcCobroProveedorNombre] = useState("");
  const [acCobroNota, setAcCobroNota] = useState("");
  const [acCobroBucket, setAcCobroBucket] = useState(""); // 'factura' | 'credito'

  // Cobro de exportación — "Costos del giro" (modelo 7.18). Franco copia del SWIFT el monto
  // instruido (InstdAmt) y la comisión del banco internacional (Citibank); el resto se deriva.
  // Campos opcionales del SWIFT (colapsables) para trazabilidad / dedup futura.
  const [giroInstdAmt, setGiroInstdAmt] = useState("");
  const [giroCitibank, setGiroCitibank] = useState("");
  const [giroSantanderRecibido, setGiroSantanderRecibido] = useState("");
  const [giroSwiftOpen, setGiroSwiftOpen] = useState(false);
  // Wizard del cobro de exportación: 3 pasos (1 Facturas · 2 Costos del giro · 3 Comisión Santander).
  // Solo aplica en modo export; el cobro normal queda en una sola pantalla.
  const [pasoExport, setPasoExport] = useState(1);
  const [cargandoSwift, setCargandoSwift] = useState(false);
  const [giroUetr, setGiroUetr] = useState("");
  const [giroOrdenante, setGiroOrdenante] = useState("Place Exchange Inc");
  const [giroOrdenanteBic, setGiroOrdenanteBic] = useState("");
  const [giroFechaValor, setGiroFechaValor] = useState("");

  // Crear proveedor desde el selector inline del path Pago SII cuando el RUT no existe en la tabla
  // proveedores (mismo patrón que "+ Nuevo proveedor" del path Factura externa).
  const [creandoProvSII, setCreandoProvSII] = useState(false);
  const [provSIINombre, setProvSIINombre] = useState("");
  const [provSIIRut, setProvSIIRut] = useState("");

  // Crear cliente desde el selector inline del path Cobro SII (simétrico al "+ Nuevo proveedor").
  const [creandoClienteSII, setCreandoClienteSII] = useState(false);
  const [clienteSIIEmpresa, setClienteSIIEmpresa] = useState("");
  const [clienteSIIRut, setClienteSIIRut] = useState("");

  // Modo "Pago institucional": PREVIRED, SII, Tesorería, banco, otros.
  const añoActualPI = new Date().getFullYear();
  const añosPI = [añoActualPI - 1, añoActualPI, añoActualPI + 1];
  const [piInstitucion, setPiInstitucion] = useState("");
  const [piPeriodoMes, setPiPeriodoMes] = useState(null);
  const [piPeriodoAño, setPiPeriodoAño] = useState(añoActualPI);
  const [piNotas, setPiNotas] = useState("");

  // Modo "Pago a edificio": pago de comisión a uno o varios edificios. Multi-edificio con
  // cuadre EXACTO: Σ montos asignados === abs(monto del movimiento). FASE 1 = solo UI/cuadre,
  // sin escritura a movimientos_caja (eso llega en Fase 2).
  // referencia: [{edificioId, edificioNombre, saldoPendiente, moneda}]
  const [edificiosSaldo, setEdificiosSaldo] = useState([]);
  // asignaciones tipeadas: [{edificioId, edificioNombre, proveedorId, monto}]
  const [edificiosAsignaciones, setEdificiosAsignaciones] = useState([]);
  const [edificioSelectorId, setEdificioSelectorId] = useState(""); // edificio elegido en el dropdown antes de "Agregar"
  // Acordeón "Adjuntar factura de soporte (opcional)" en modo edificio: cerrado por defecto.
  // La factura nunca es requisito; la lista solo se renderiza si el usuario abre esto.
  const [mostrarFacturaEdificio, setMostrarFacturaEdificio] = useState(false);

  // Path "Compra de activo" (D.2): vincula este egreso a un activo planificado (edificio) →
  // el activo pasa a 'activo' con fecha_compra = fecha del movimiento y sale del CapEx.
  // ejecutado_sin_factura + planificados con plan: [{...activo, edificioNombre, _tienePlan}]
  const [activosCapex, setActivosCapex] = useState([]);
  // Activos CapEx planificado SIN plan de cuotas (mismo set que muestra Futuros como línea única):
  // proyecciones saldables de Sección A, en bruto. Separado de activosCapex para NO filtrarse a la
  // Sección B (activo entero contra DTE). CapEx-IVA Fase 3.
  const [activosSinPlan, setActivosSinPlan] = useState([]);
  const [activoCapexSel, setActivoCapexSel] = useState(null); // id del activo elegido
  const [facturaActivoSel, setFacturaActivoSel] = useState(null); // id de la factura recibida (DTE) elegida
  // Plan de pagos: cuotas pendientes por activo (path caja pura, sin ejecutar el activo).
  const [cuotasActivoPorId, setCuotasActivoPorId] = useState({}); // { activo_id: [cuotas pendientes ordenadas] }
  // Componentes de costo (landed cost) pendientes de lotes — otro target caja-pura del path CapEx.
  const [componentesLotePendientes, setComponentesLotePendientes] = useState([]);
  // Lotes de compra planificados cuyo FOB todavía NO está saldado → saldables en Sección A (CapEx-IVA F5).
  const [lotesFobPendiente, setLotesFobPendiente] = useState([]);
  // Selección caja-pura MULTI-target: un pago puede cubrir N proyecciones mezcladas (cuotas de activo +
  // componentes de lote). Array de claves compuestas `cuota:{id}` / `comp:{id}`. La caja es UNA fila
  // consolidada (respeta la UNIQUE viva sobre movimiento_bancario_id); el "quién pagó qué" vive en el
  // movimiento_bancario_id de cada target. Ver ARQUITECTURA 4.2.nn / 4.2.oo / 10.100.
  const [capexSel, setCapexSel] = useState([]);
  // Fase 3b-i: override del "monto a aplicar" por ítem (modo B, reparto multi-ítem). { [key]: string }.
  // Vacío = usar el default automático (modo A). Se limpia al deseleccionar el ítem.
  const [capexMontos, setCapexMontos] = useState({});

  // Tab "Factoring" (solo ingresos): vincula este movimiento al adelanto o al excedente
  // de una operación de factoring activa. No crea movimientos_caja — la operación de
  // factoring ya tiene su propio asiento contable; sólo registra la FK del banco.
  const [operacionesFactoring, setOperacionesFactoring] = useState([]);
  const [factoringOpIds, setFactoringOpIds] = useState([]);
  const [factoringTipoVinc, setFactoringTipoVinc] = useState("giro");
  // Si este movimiento ya está vinculado a una operación de factoring, guardamos el objeto
  // de la op acá. Cuando es != null, el modal pasa a modo "ya vinculado" con opción de
  // desvincular en lugar de crear vinculación nueva.
  const [yaVinculadoFactoring, setYaVinculadoFactoring] = useState(null);

  // Filas existentes en movimiento_bancario_facturas para este movimiento. Si hay >=1,
  // el modal muestra una sección "Vinculación actual" con botón para desvincular cada
  // factura SII (single o múltiple).
  const [vinculacionesSII, setVinculacionesSII] = useState([]);

  // Fase 4 (regularizar con NC): si este movimiento tiene un forzado por faltante PENDIENTE
  // (fila en vinculaciones_forzadas), se guarda { id, faltante, facturas, proveedorId }. Habilita
  // el botón "Regularizar con NC" en el panel "Vinculación actual". null = sin forzado pendiente.
  const [forzadoPendiente, setForzadoPendiente] = useState(null);
  const [mostrarPickerNC, setMostrarPickerNC] = useState(false);
  const [ncCandidatas, setNcCandidatas] = useState([]);
  const [ncSeleccionada, setNcSeleccionada] = useState(null);
  const [cargandoNC, setCargandoNC] = useState(false);

  // Pagos a empleado vinculados a este movimiento (filas en pagos_empleados con
  // movimiento_bancario_id = mov.id). Tercera vía de detección de "ya vinculado":
  // empleado no escribe en movimiento_bancario_facturas ni en movimientos_caja.
  const [pagosEmpleadoVinculados, setPagosEmpleadoVinculados] = useState([]);

  const monto = Number(movimiento?.monto || 0);
  const moneda = movimiento?.moneda || (pais === "Perú" ? "PEN" : "CLP");
  const esCobro = tipoVinculacion === "cobro";
  const esEmpleado = tipoVinculacion === "empleado";
  const esEgreso = movimiento?.tipo === "egreso";
  const esPagoExterna = tipoVinculacion === "pago" && modoCompra === "externa";
  const esPagoSinFactura = tipoVinculacion === "pago" && modoCompra === "sin_factura";
  // P3: un proveedor que emite factura (es_extranjero) NO puede cerrarse como "Sin factura" —
  // debe cargarse en "Factura externa". Comparación por id (regla 10.7). Los locales sin DTE (Uber,
  // cafés, taxis, propinas) son es_extranjero=false → siguen usando "Sin factura" normal.
  const sfProveedorEsExtranjero = !!proveedores.find((p) => p.id === Number(sfProveedorId))?.es_extranjero;
  const esPagoACuenta = tipoVinculacion === "pago" && modoCompra === "a_cuenta";
  const esPagoRecurrente = tipoVinculacion === "pago" && modoCompra === "recurrente";
  // Clave local de una ocurrencia dentro de la selección del modo recurrente.
  const keyOcurrencia = (o) => `${o.periodo_año}-${o.periodo_mes}-${o.costo_asociado_id ?? "main"}`;
  const esCobroACuenta = tipoVinculacion === "cobro" && modoCobro === "a_cuenta";
  const esCobroExportacion = tipoVinculacion === "cobro" && modoCobro === "exportacion";
  const esFactoring = tipoVinculacion === "factoring";
  const esCajaChica = tipoVinculacion === "empleado" && modoEmpleado === "caja_chica";
  const esRemuneracion = tipoVinculacion === "empleado" && modoEmpleado === "remuneracion";
  const esAdelanto = tipoVinculacion === "empleado" && modoEmpleado === "adelanto";
  const esTransporte = tipoVinculacion === "empleado" && modoEmpleado === "transporte";

  // Form de Factura externa: estado + lógica viven en hook compartido (modo bancario porque
  // VincularMov siempre se abre con un movimiento). El padre lo dispara desde Modal.Footer.
  const facturaExt = useFacturaExternaForm({
    pais,
    moneda,
    movimiento,
    proveedores,
    onProveedorCreado: (nuevo) =>
      setProveedores((prev) => [...prev, nuevo].sort((a, b) => a.nombre.localeCompare(b.nombre))),
    onSuccess,
    onClose,
  });
  const esPagoInstitucional = tipoVinculacion === "institucional";
  const esPagoEdificio = tipoVinculacion === "edificio";
  const esPagoActivo = tipoVinculacion === "activo";
  const esPrestamo = tipoVinculacion === "prestamo";

  // Comisión por transferencia internacional (Santander): el débito CLP incluye los "Gastos OUR"
  // (cobro del banco corresponsal) que NO llevan IVA y NO se incluyen en el DTE → la factura SII
  // recibida es MENOR que el débito bancario. Es un sobrepago legítimo y esperado, no un error.
  // Cuando esto es true: (1) no se bloquea el sobrepago, (2) se muestra el desglose de Gastos OUR,
  // (3) mbf.monto = monto_total de la factura (no el débito) para no distorsionar el FIFO. Ver 10.67.
  const esComisionTransferenciaInternacional =
    /transferencia internacional/i.test(movimiento?.descripcion_raw || "") &&
    (movimiento?.tipo === "egreso" || Number(movimiento?.monto || 0) < 0) &&
    tipoVinculacion === "pago" &&
    modoCompra === "sii";
  // Panel de desglose Gastos OUR: solo cuando hay sobrepago real sobre la factura seleccionada.
  const mostrarDesgloseOUR =
    esComisionTransferenciaInternacional &&
    !!facturaSeleccionada &&
    Math.abs(monto) > Number(facturaSeleccionada?.monto_total || 0);
  // Guardrails visuales (NO bloquean el confirmar): advierten cuando la factura seleccionada
  // probablemente NO corresponde al débito. Solo aplican dentro del caso OUR con sobrepago.
  const gastosOUR = mostrarDesgloseOUR ? Math.abs(monto) - Number(facturaSeleccionada.monto_total || 0) : 0;
  const ourPorcentaje = mostrarDesgloseOUR && Math.abs(monto) > 0 ? gastosOUR / Math.abs(monto) : 0;
  // OUR > 15% del débito → probablemente la factura no es la correcta.
  const warnOURExcesivo = mostrarDesgloseOUR && ourPorcentaje > 0.15;
  // Fecha de la factura vs fecha del débito desalineadas por más de 1 día.
  const diasDesalineados =
    mostrarDesgloseOUR && facturaSeleccionada?.fecha_emision && movimiento?.fecha
      ? Math.abs(new Date(movimiento.fecha) - new Date(facturaSeleccionada.fecha_emision)) / (1000 * 60 * 60 * 24)
      : 0;
  const warnFechaDesalineada = mostrarDesgloseOUR && diasDesalineados > 1;

  // Propina de restorán (sin DTE): el débito supera la factura SII por la propina, que no genera
  // documento. Misma forma que los Gastos OUR (10.79) pero rama paralela e independiente: el usuario
  // la marca con un toggle, y es mutuamente excluyente con OUR (el toggle se oculta cuando es OUR).
  // Base de la propina = Σ de TODAS las facturas seleccionadas (multi-select), NO solo la última tildada
  // (facturaSeleccionada). Con 1 factura, la suma == esa factura → el caso single sigue idéntico. Ver 10.79.
  const totalFacturasSeleccionadas =
    facturasSeleccionadas.length > 0
      ? facturasSeleccionadas.reduce((s, f) => s + Number(f.monto_total || 0), 0)
      : Number(facturaSeleccionada?.monto_total || 0);
  const puedeMarcarPropina =
    tipoVinculacion === "pago" &&
    modoCompra === "sii" &&
    (movimiento?.tipo === "egreso" || Number(movimiento?.monto || 0) < 0) &&
    !!facturaSeleccionada &&
    Math.abs(monto) > totalFacturasSeleccionadas &&
    !esComisionTransferenciaInternacional;
  const mostrarDesglosePropina = esPropina && !!facturaSeleccionada && Math.abs(monto) > totalFacturasSeleccionadas;
  const montoPropina = mostrarDesglosePropina ? Math.abs(monto) - totalFacturasSeleccionadas : 0;
  // Guardrail blando propina (NO bloquea): propina > 13% de la base → ¿factura correcta?
  // A diferencia de OUR (% sobre el débito), la base acá es la suma de facturas.
  const propinaPorcentaje =
    mostrarDesglosePropina && totalFacturasSeleccionadas > 0 ? montoPropina / totalFacturasSeleccionadas : 0;
  // Bypass de sobrepago unificado (regla 10.67): OUR y propina son ambos excesos legítimos sin DTE.
  const excesoSinDte = esComisionTransferenciaInternacional || esPropina;

  // Faltante (10.108 invertida): el débito bancario pagó MENOS que la Σ de facturas seleccionadas.
  // Espejo exacto de puedeMarcarPropina con el signo dado vuelta (Σ facturas > |débito| + tolerancia).
  // Sirve para single (Σ = la única factura, vía totalFacturasSeleccionadas) y multi. Mutuamente
  // excluyente con propina por construcción (propina exige |débito| > Σ). Ver 10.160 (no toca el motor).
  const montoFaltante = totalFacturasSeleccionadas - Math.abs(monto);
  // Cuadre UNIFICADO del flujo mov → N facturas (single o múltiple, con o sin cuotas). El débito se
  // reparte por factura: Σ cuotas tildadas (derivado) o monto manual (sin plan). Reusa el motor puro
  // evaluarCuadreAplicaciones (que a su vez reusa evaluarCuadreCuotas, 10.103), multi-moneda por la
  // FECHA del movimiento (10.185). Cierra el salteo histórico "NO aplica con cuota" (mislinks Blackim).
  const cuadreSII = evaluarCuadreAplicaciones({ facturasSeleccionadas, movimiento, esCobro, pais });
  const hayCuotasSel = facturasSeleccionadas.some((f) => (f.cuotasSeleccionadas || []).length > 0);

  // Cuadre de boletas de honorarios (Σ monto aplicado vs |débito|) reusando EL MISMO motor puro que las
  // facturas SII (evaluarCuadreAplicaciones, 10.1/10.19/10.27). `montoAplicadoFactura` cae al montoAsignado
  // libre (boleta sin plan) o a la Σ de cuotas tildadas (B2, boleta con plan). Tolerancia = 10 por boleta.
  const cuadreBoletas = evaluarCuadreAplicaciones({
    facturasSeleccionadas: boletasSeleccionadas,
    movimiento,
    esCobro,
    pais,
  });

  // Gate de gerente (forma estricta país-pineada): la cuenta de un país no opera en el dominio del
  // otro. usuarioId debe ser 1 (Chile) / 4 (Perú), ADEMÁS de estar en FORZAR_VINCULACION_IDS. Gobierna
  // tanto crear el forzado (puedeForzar) como regularizarlo con NC (puedeRegularizarForzado, Fase 4).
  const esGerente = puedeForzarVinculacion(usuarioId) && usuarioId === (pais === "Chile" ? 1 : 4);
  // El exceso sin DTE (propina/OUR) es sobrepago legítimo (débito > Σ facturas): no exige cuadre ni forzar.
  const excesoLegitimoSII = excesoSinDte && cuadreSII.sinAsignar > 0;
  // Forzado disponible SOLO tras un intento de confirmar con saldo sin asignar (no mientras se arma el
  // reparto) y siendo gerente. Se reutiliza el MISMO toggle/gate (10.1); el descuadre NO lo dispara solo.
  const puedeForzar = intentoForzarConfirm && esGerente && !cuadreSII.cuadra && !excesoLegitimoSII;
  // Regularizar con NC (F4.2): forzado en estado 'pendiente' + gerente.
  const puedeRegularizarForzado = forzadoPendiente?.estado === "pendiente" && esGerente;
  // Deshacer regularización (F4.3, espejo inverso 10.121): forzado ya 'resuelto' + gerente.
  const puedeDeshacerRegularizacion = forzadoPendiente?.estado === "resuelto" && esGerente;

  // La propina/forzado se re-evalúan por movimiento: resetear al cambiar de factura o de movimiento.
  useEffect(() => {
    setEsPropina(false);
    setEsForzado(false);
  }, [facturaSeleccionada, movimiento?.id]);

  // Transferencia internacional RECIBIDA (ingreso USD): Santander netea su comisión de recepción
  // de los USD entrantes antes de acreditar. La factura SII de esa comisión queda huérfana en
  // "Por pagar"; este modal permite vincularla al ingreso vía comisiones_ingreso_neteadas.
  const esTransferenciaRecibida =
    movimiento?.tipo === "ingreso" &&
    movimiento?.moneda === "USD" &&
    /rec\.? ?or\.? ?de pago|liquidaci[oó]n orden de pago|orden de pago/i.test(movimiento?.descripcion_raw || "");

  useEffect(() => {
    if (!show || !pais) return;
    setFacturaSeleccionada(null);
    setFacturasSeleccionadas([]);
    setBoletasSeleccionadas([]);
    setPlanPorFactura({});
    setPlanPorBoleta({});
    setIntentoForzarConfirm(false);
    setEsForzado(false);
    setCreditoPrestamo(null);
    setCuotaPrestamo(null);
    setBusqueda("");
    setCapexBusqueda("");
    setCapexActivoBusqueda("");
    setFactoringBusqueda("");
    setMostrarDteAnioAnterior(false);
    setEmpleadoId(null);
    setEmpleadoBusqueda("");
    // Sin auto-semilla: el desglose arranca VACÍO (cero componentes, cero cards, Total $0). Se puebla
    // solo cuando el usuario tilda un pendiente (togglePendiente) o agrega uno manual ("+ Componente").
    setComponentes([]);
    setModoEmpleado("remuneracion");
    // Sub-modo Adelanto: default de período al mes/año del movimiento (parseFechaLocal maneja el formato).
    {
      const fMov = parseFechaLocal(movimiento?.fecha);
      setAdelantoMes(fMov.getMonth() + 1);
      setAdelantoAño(fMov.getFullYear());
    }
    setModoCompra("sii");
    setEntidadFallbackId(null);
    setEntidadFallbackBusqueda("");
    setEntidadFallbackOptions([]);
    setEntidadFallbackCreada(false);
    setSfConcepto("");
    setSfCuentaER("");
    setSfProveedorId(null);
    setSfProveedorNombre("");
    setAcProveedorId(null);
    setAcProveedorNombre("");
    setAcNota("");
    setAcBucket("");
    setRecProveedorId(null);
    setRecProveedorNombre("");
    setRecCuentaER("");
    setRecOcurrencias([]);
    setRecSeleccion(new Map());
    setModoCobro("sii");
    setAcCobroProveedorId(null);
    setAcCobroProveedorNombre("");
    setAcCobroNota("");
    setAcCobroBucket("");
    setCreandoProvSII(false);
    setProvSIINombre("");
    setProvSIIRut("");
    setCreandoClienteSII(false);
    setClienteSIIEmpresa("");
    setClienteSIIRut("");
    setPiInstitucion("");
    setPiPeriodoMes(null);
    setPiPeriodoAño(añoActualPI);
    setPiNotas("");
    setEdificiosAsignaciones([]);
    setEdificioSelectorId("");
    setMostrarFacturaEdificio(false);
    setActivoCapexSel(null);
    setActivosCapex([]);
    setActivosSinPlan([]);
    setFacturaActivoSel(null);
    setCuotasActivoPorId({});
    setComponentesLotePendientes([]);
    setLotesFobPendiente([]);
    setCapexSel([]);
    setCapexMontos({});
    setFactoringOpIds([]);
    setFactoringTipoVinc("giro");
    setYaVinculadoFactoring(null);
    setVinculacionesSII([]);
    setForzadoPendiente(null);
    setMostrarPickerNC(false);
    setNcCandidatas([]);
    setNcSeleccionada(null);
    setPagosEmpleadoVinculados([]);
    setExternasSeleccionadas([]);
    setBusquedaExt("");
    setModoExterno("existente");
    setFacturasExtPendientes([]);
    if (movimiento?.id) {
      // MBF + enriquecimiento de las filas SII emitidas con datos de la factura. mbf.monto guarda
      // el monto_total CLP fiscal (correcto para la cobertura), pero el display debe mostrar el
      // monto en su moneda real: para DTE 110 export el valor económico es monto_usd (US$), no el
      // CLP con la etiqueta del movimiento. factura_id es polimórfico → 2ª query por id (no embed).
      (async () => {
        const { data: mbfRows } = await client
          .from("movimiento_bancario_facturas")
          .select("id, factura_tipo, factura_id, monto")
          .eq("movimiento_bancario_id", movimiento.id);
        const filas = mbfRows || [];
        const idsEmitidas = filas.filter((f) => f.factura_tipo === "sii_emitida").map((f) => f.factura_id);
        let facturasById = {};
        if (idsEmitidas.length > 0) {
          const { data: facs } = await client
            .from("facturas_sii_emitidas")
            .select("id, folio, tipo_dte, monto_usd, monto_total")
            .in("id", idsEmitidas);
          facturasById = Object.fromEntries((facs || []).map((f) => [f.id, f]));
        }
        // F2b: enriquecer las filas de boleta con su folio (para la sección "Vinculación actual").
        // Se adosa `_boletaFolio` — NO se usa `factura` (que dispara formatMontoFacturaSII y espera
        // monto_total/monto_usd de una SII); el monto de la boleta se muestra con formatAmount(v.monto).
        const idsBoletas = filas.filter((f) => f.factura_tipo === "boleta_honorario").map((f) => f.factura_id);
        let foliosBoleta = {};
        if (idsBoletas.length > 0) {
          const { data: bhs } = await client.from("boletas_honorarios").select("id, folio").in("id", idsBoletas);
          foliosBoleta = Object.fromEntries((bhs || []).map((b) => [b.id, b.folio]));
        }
        setVinculacionesSII(
          filas.map((f) => {
            if (f.factura_tipo === "sii_emitida" && facturasById[f.factura_id])
              return { ...f, factura: facturasById[f.factura_id] };
            if (f.factura_tipo === "boleta_honorario" && foliosBoleta[f.factura_id] != null)
              return { ...f, _boletaFolio: foliosBoleta[f.factura_id] };
            return f;
          })
        );
      })();

      // Fase 4: detectar un forzado por faltante de este movimiento CUALQUIERA SEA SU ESTADO. Con
      // estado='pendiente' habilita "Regularizar con NC" (F4.2); con 'resuelto' habilita "Deshacer
      // regularización" (F4.3, espejo inverso 10.121). Silencioso: sin fila → null. proveedor_id no
      // vive en la fila (guarda facturas [{factura_tipo, factura_id, monto_total}]) → se resuelve desde
      // la primera factura recibida, para filtrar las NC candidatas del mismo proveedor.
      (async () => {
        const { data: fRow } = await client
          .from("vinculaciones_forzadas")
          .select("id, faltante, facturas, estado, nc_factura_id")
          .eq("movimiento_bancario_id", movimiento.id)
          .maybeSingle();
        if (!fRow) {
          setForzadoPendiente(null);
          return;
        }
        const primeraRec = (fRow.facturas || []).find((f) => f.factura_tipo === "sii_recibida");
        let proveedorId = null;
        if (primeraRec?.factura_id != null) {
          const { data: facRow } = await client
            .from("facturas_sii_recibidas")
            .select("proveedor_id")
            .eq("id", primeraRec.factura_id)
            .maybeSingle();
          proveedorId = facRow?.proveedor_id ?? null;
        }
        setForzadoPendiente({
          id: fRow.id,
          faltante: Number(fRow.faltante),
          facturas: fRow.facturas || [],
          proveedorId,
          estado: fRow.estado,
          ncFacturaId: fRow.nc_factura_id ?? null,
        });
      })();

      // Detectar pagos a empleado vinculados a este movimiento (tercera vía de detección).
      client
        .from("pagos_empleados")
        .select("id, empleado_id, tipo, periodo_año, periodo_mes, monto, moneda, empleado:empleados(nombre, apellido)")
        .eq("movimiento_bancario_id", movimiento.id)
        .then(({ data }) => setPagosEmpleadoVinculados(data || []));
    }
    // Path "Préstamo": créditos activos con cuotas pendientes (solo aplica a egresos).
    if (movimiento?.tipo === "egreso") {
      getCreditosConCuotasPendientes(pais)
        .then(setCreditosDisponibles)
        .catch(() => setCreditosDisponibles([]));
    } else {
      setCreditosDisponibles([]);
    }
    // Cargar facturas externas pendientes para el modo "Vincular existente" del tab Externa.
    // Default inteligente: si hay pendientes → "existente"; si no → "nueva" (form de carga).
    //
    // GATE DE EXTERNAS = FIFO, NO el libro (decisión C1 / R3.2b, NO wirear a resolverSaldo). El `.eq`
    // de abajo es solo un PRE-FILTRO grueso (excluye anuladas/ya-vinculadas); el "¿está pendiente de
    // verdad?" lo decide el SALDO FIFO por factura (`saldosExtMap`, abajo). Las externas de proveedores
    // recurrentes con pagos por transferencia genérica (sin atribución documento-a-documento; caso
    // Ignacio Mailhos) el libro las da pendientes pero el FIFO las amortiza oldest-first → deben quedar
    // FUERA del picker. El plan R3.4 original ("wirear P5 a resolverSaldo") quedó OBSOLETO: haría
    // reaparecer esas externas ya pagadas como seleccionables. `getSaldosPorFacturaProveedor` mantiene
    // EXT en FIFO aun con ctxLibro (candado en ledgerProveedor.test.js). Ver docs/r3-...caja-corte §5.
    client
      .from("facturas_externas")
      .select(
        "id, proveedor_id, proveedor_nombre, fecha_emision, monto_total, moneda, numero_documento, concepto, cuenta_er, periodo_mes"
      )
      .eq("pais", pais)
      .is("movimiento_bancario_id", null)
      .eq("estado", "pendiente")
      .order("fecha_emision", { ascending: false })
      .then(async ({ data }) => {
        const pendientes = data || [];
        setFacturasExtPendientes(pendientes);
        // Saldo FIFO real por factura (helper canónico, regla 10.1/10.106). Selector país-wide →
        // se llama una vez por proveedor distinto del listado (hoy 2) y se mergea. Fuera del render.
        const provIds = [...new Set(pendientes.map((f) => f.proveedor_id).filter(Boolean))];
        const saldoMap = new Map();
        await Promise.all(
          provIds.map(async (pid) => {
            try {
              const sp = await getSaldosPorFacturaProveedor(pid, pais);
              for (const [key, { saldoDisplay }] of sp) {
                if (key.startsWith("EXT:")) saldoMap.set(Number(key.slice(4)), saldoDisplay);
              }
            } catch (e) {
              // eslint-disable-next-line no-console
              console.error("Error computando saldos FIFO por factura externa:", e);
            }
          })
        );
        setSaldosExtMap(saldoMap);
        // "existente" solo si hay facturas con saldo FIFO pendiente; si todas están amortizadas → "nueva".
        const conSaldo = pendientes.filter((f) => saldoMap.has(f.id)).length;
        setModoExterno(conSaldo > 0 ? "existente" : "nueva");
      });
    if (movimiento?.tipo === "ingreso") {
      // Sin filtro de estado: un movimiento puede estar vinculado a una operación LIQUIDADA
      // (no solo activa). Filtrar por "activa" hacía que esos movimientos no se detectaran como
      // ya vinculados y abrieran el form en blanco. Las candidatas para vincular NUEVO igual se
      // acotan abajo por ausencia de FK (giro/remanente), así que incluir liquidadas no agrega ruido.
      getOperacionesFactoring(pais)
        .then((data) => {
          setOperacionesFactoring(data);
          const yaGiro = data.find((op) => op.movimiento_bancario_adelanto_id === movimiento.id);
          const yaExc = data.find((op) => op.movimiento_bancario_remanente_id === movimiento.id);
          if (yaGiro || yaExc) {
            setTipoVinculacion("factoring");
            setFactoringTipoVinc(yaGiro ? "giro" : "excedente");
            setFactoringOpIds([(yaGiro || yaExc).id]);
            setYaVinculadoFactoring(yaGiro || yaExc);
          }
        })
        .catch(() => setOperacionesFactoring([]));
    }
    if (tipoVinculacion === "empleado" || tipoVinculacion === "caja_chica") cargarEmpleados();
    else cargarFacturas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, tipoVinculacion, pais, movimiento?.id]);

  // Cargar proveedores al entrar por primera vez en cualquier path con selector de proveedor:
  // pago (externa, sin_factura, a_cuenta) o cobro (a_cuenta).
  useEffect(() => {
    if (!show || !pais) return;
    const necesitaProveedores =
      (tipoVinculacion === "pago" &&
        (modoCompra === "externa" ||
          modoCompra === "sin_factura" ||
          modoCompra === "a_cuenta" ||
          modoCompra === "recurrente")) ||
      (tipoVinculacion === "cobro" && modoCobro === "a_cuenta");
    if (!necesitaProveedores) return;
    if (proveedores.length > 0) return;
    (async () => {
      try {
        const data = await getProveedores(pais);
        setProveedores(data);
      } catch {
        toast.error("Error cargando proveedores");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, pais, tipoVinculacion, modoCompra, modoCobro]);

  // Modo "Costo recurrente": al elegir proveedor, cargar sus ocurrencias pendientes (no conciliadas).
  useEffect(() => {
    if (!esPagoRecurrente || !recProveedorId || !pais) {
      setRecOcurrencias([]);
      return;
    }
    let cancelado = false;
    (async () => {
      setRecLoadingOcs(true);
      try {
        const { ocurrencias } = await getOcurrenciasRecurrentesPendientes(pais, Number(recProveedorId));
        if (!cancelado) setRecOcurrencias(ocurrencias);
      } catch (err) {
        if (!cancelado) {
          setRecOcurrencias([]);
          toast.error("Error cargando ocurrencias recurrentes: " + (err.message || ""));
        }
      } finally {
        if (!cancelado) setRecLoadingOcs(false);
      }
    })();
    return () => {
      cancelado = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [esPagoRecurrente, recProveedorId, pais]);

  // Path "Préstamo": auto-selección de crédito (si hay uno solo) y de la cuota cuya fecha de
  // vencimiento sea la más cercana a la fecha del movimiento bancario.
  useEffect(() => {
    if (!esPrestamo) return;
    let credito = creditoPrestamo;
    if (!credito && creditosDisponibles.length === 1) {
      credito = creditosDisponibles[0];
      setCreditoPrestamo(credito);
    }
    if (credito && !cuotaPrestamo && credito.cuotas?.length) {
      const ref = new Date(movimiento?.fecha || "2000-01-01").getTime();
      const closest = credito.cuotas.reduce((best, q) => {
        const dist = Math.abs(new Date(q.fecha_vencimiento).getTime() - ref);
        return best == null || dist < best.dist ? { q, dist } : best;
      }, null);
      if (closest) setCuotaPrestamo(closest.q);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [esPrestamo, creditosDisponibles, creditoPrestamo, cuotaPrestamo, movimiento?.fecha]);

  // Modo "Pago a edificio": listar TODOS los edificios operativos del país con su saldo
  // pendiente al lado como referencia. El saldo viene de getEdificiosSaldoPendiente (mismo
  // helper que FlujoCajaV2; fechaInicio = corte vigente FECHA_CORTE, monedaLocal = moneda del
  // movimiento), PERO ese helper solo devuelve los edificios con saldo > 0. Para no ocultar
  // ninguno (regla del usuario: "elijo el que quiera"), traemos la lista completa de edificios
  // operativos y le hacemos merge del saldo (0 cuando no aparece en el helper).
  useEffect(() => {
    if (!show || !pais || !esPagoEdificio) return;
    if (edificiosSaldo.length > 0) return;
    (async () => {
      try {
        const [saldoData, { data: todosEdificios }] = await Promise.all([
          getEdificiosSaldoPendiente({ pais, fechaInicio: FECHA_CORTE, monedaLocal: moneda }),
          client
            .from("edificios")
            .select("id, edificio, proveedor_id")
            .eq("pais", pais)
            .eq("eliminada", false)
            .in("estado", ESTADOS_COTIZABLE)
            .order("edificio", { ascending: true }),
        ]);
        const saldoPorId = Object.fromEntries((saldoData || []).map((e) => [e.edificioId, e]));
        const merged = (todosEdificios || []).map((ed) => {
          const s = saldoPorId[ed.id];
          return {
            edificioId: Number(ed.id),
            edificioNombre: ed.edificio || `Edificio #${ed.id}`,
            proveedorId: ed.proveedor_id != null ? Number(ed.proveedor_id) : null,
            saldoPendiente: s?.saldoPendiente || 0,
            moneda: s?.moneda || moneda,
          };
        });
        setEdificiosSaldo(merged);
      } catch {
        toast.error("Error cargando edificios");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, pais, esPagoEdificio]);

  // Path "Compra de activo": listar los activos vinculables. Dos grupos:
  //  · estado='ejecutado_sin_factura' → ejecución ENTERA contra el DTE (D.2 PASO 2, sin cambios).
  //  · estado='planificado' CON plan de cuotas pendientes → pago de CUOTA (caja pura, el activo sigue
  //    planificado). Se anota `_tienePlan` y se guardan sus cuotas pendientes por activo.
  useEffect(() => {
    if (!show || !pais || !esPagoActivo) return;
    if (activosCapex.length > 0) return;
    (async () => {
      try {
        const [
          esperandoFactura,
          planificados,
          cuotasActivos,
          componentesLote,
          lotesPlan,
          fobSaldadoSet,
          { data: edificiosLista },
        ] = await Promise.all([
          getActivos(pais, { estado: "ejecutado_sin_factura" }),
          getActivos(pais, { estado: "planificado" }),
          getCuotasActivosFuturas(pais),
          getCostosLotesFuturos(pais),
          getLotes(pais, { estado: "planificado" }),
          getLotesFobSaldado(pais),
          client.from("edificios").select("id, edificio").eq("pais", pais),
        ]);
        const nombrePorId = Object.fromEntries((edificiosLista || []).map((e) => [Number(e.id), e.edificio]));
        // Cuotas pendientes agrupadas por activo (ya vienen ordenadas por fecha_vencimiento).
        const porActivo = {};
        for (const c of cuotasActivos || []) {
          if (!porActivo[c.activo_id]) porActivo[c.activo_id] = [];
          porActivo[c.activo_id].push(c);
        }
        const conPlanIds = new Set(Object.keys(porActivo).map(Number));
        const conNombre = (a) => ({ ...a, edificioNombre: nombrePorId[a.destino_id] || `Edificio #${a.destino_id}` });
        // Planificados con cuotas pendientes primero (pago de cuota), luego los que esperan factura.
        const planConPlan = (planificados || [])
          .filter((a) => a.destino_tipo === "edificio" && conPlanIds.has(a.id))
          .map((a) => ({ ...conNombre(a), _tienePlan: true }));
        const esperando = (esperandoFactura || [])
          .filter((a) => a.destino_tipo === "edificio")
          .map((a) => ({ ...conNombre(a), _tienePlan: false }));
        setActivosCapex([...planConPlan, ...esperando]);
        // Planificado SIN plan (mismo criterio que Futuros/mapCapex: planificado, lote_id null, sin cuotas).
        // Son la línea única de CapEx que Futuros proyecta y que hasta ahora el modal no ofrecía saldar.
        const sinPlan = (planificados || [])
          .filter((a) => a.destino_tipo === "edificio" && a.lote_id == null && !conPlanIds.has(a.id))
          .map(conNombre);
        setActivosSinPlan(sinPlan);
        setCuotasActivoPorId(porActivo);
        // Componentes de lote pendientes (ya vienen ordenados por fecha_pago). La selección arranca vacía:
        // es multi-target y el usuario elige qué ítems cubre el pago.
        setComponentesLotePendientes(componentesLote || []);
        // FOB del lote saldable: lotes planificados con fecha, cuyo FOB NO esté TOTALMENTE saldado (3b-ii:
        // un FOB parcial se sigue ofreciendo para top-up). `_saldoRestante` viene del Map (saldo del target
        // fob), fuente única con Futuros; sin fila fob → undefined (montoNeto cae al objetivo completo).
        const fobPend = (lotesPlan || [])
          .filter((l) => l.fecha_planificada && !fobSaldadoSet.get(l.id)?.saldada)
          .map((l) => ({ ...l, _saldoRestante: fobSaldadoSet.get(l.id)?.saldoRestante }));
        setLotesFobPendiente(fobPend);
      } catch {
        toast.error("Error cargando activos para vincular");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, pais, esPagoActivo]);

  // Path SII: ¿la factura seleccionada ya tiene la entidad cargada por el auto-match de sii-sync?
  // (cliente_id si esCobro, proveedor_id si pago SII). Si no, hace falta el selector inline.
  // Solo aplica en modo single (1 factura) — en múltiple las validaciones del toggle ya
  // exigen entidad cargada en todas.
  const esModoMultiple = facturasSeleccionadas.length >= 2;
  // Boleta de honorarios huérfana (sin proveedor) actualmente seleccionada: necesita la MISMA
  // resolución inline de proveedor que una factura SII sin proveedor_id → reusa banner/gate/handler.
  // Primera boleta tildada: alimenta el banner de proveedor inline y la precarga nombre/RUT. Una boleta
  // huérfana (_sinProveedor) se selecciona SOLA (ver handleSeleccionarBoleta), así que si hay alguna
  // huérfana es la única del array. boletaNecesitaProveedor = hay al menos una huérfana tildada.
  const boletaSeleccionada = !esCobro && boletasSeleccionadas.length > 0 ? boletasSeleccionadas[0] : null;
  const boletaNecesitaProveedor = !esCobro && boletasSeleccionadas.some((b) => b._sinProveedor);
  const facturaTieneEntidad =
    facturaSeleccionada && (esCobro ? facturaSeleccionada.cliente_id : facturaSeleccionada.proveedor_id);
  const necesitaSelectorEntidad =
    (!esCobro && !esEmpleado && !esPagoExterna && !esModoMultiple && facturaSeleccionada && !facturaTieneEntidad) ||
    boletaNecesitaProveedor;
  // Entidad resuelta a mano: para la factura sale de su FK; para la boleta (facturaSeleccionada null)
  // cae directo a entidadFallbackId (el proveedor elegido/creado en el banner).
  const entidadResueltaId =
    (esCobro ? facturaSeleccionada?.cliente_id : facturaSeleccionada?.proveedor_id) || entidadFallbackId;

  // Gate del Confirmar para el flujo SII unificado (single/múltiple, con o sin cuotas): habilita cuando
  // Sin asignar es cero (cuadreSII); excepciones exceso sin DTE (propina/OUR). Los pagos exigen entidad
  // resuelta (proveedor de la 1ª factura o el elegido a mano). Al GERENTE no se le deshabilita por
  // descuadre: necesita que el click llegue a handleConfirmarSII para revelar el toggle "forzar" (el
  // handler lo bloquea igual hasta que lo tilde). El no-gerente sí queda deshabilitado.
  const siiConfirmarDisabled =
    facturasSeleccionadas.length === 0 ||
    (!esCobro && !(facturasSeleccionadas[0]?.proveedor_id || entidadResueltaId)) ||
    (!cuadreSII.cuadra && !excesoLegitimoSII && !esGerente);

  // Detección de vinculación de caja al abrir: usa el embed que ya viene en el prop.
  // Cubre los badges G/A/P/D que no tienen MBF (y donde vinculacionesSII queda vacío).
  // Para V/C/X la caja también está, pero el panel se muestra vía vinculacionesSII (sección A)
  // y la sección B (esta) queda suprimida para evitar duplicar.
  const tieneVinculacionCaja = !!(movimiento?.movimiento_caja_id && movimiento?.movimiento_caja?.categoria_pago);
  const categoriaCajaActual = movimiento?.movimiento_caja?.categoria_pago || null;
  const entidadCajaActual = movimiento?.movimiento_caja?.entidad_nombre || null;
  // Si el movimiento ya tiene alguna vinculación (MBF o caja), el form de revincular
  // queda oculto: la única acción permitida es "Desvincular" desde el panel arriba.
  // Para revincular hay que primero deshacer.
  const yaEstaVinculado = vinculacionesSII.length > 0 || tieneVinculacionCaja || pagosEmpleadoVinculados.length > 0;

  // Modo múltiple: suma de los montos seleccionados y diferencia contra el monto del banco.
  // Pieza B: la suma que cuadra el asiento usa el monto ASIGNADO por factura (parcial editable). Sin
  // editar, montoAsignado = monto_total → Σ idéntica al multi previo (cuadre exacto). Ver 10.66/10.67.
  const sumaMontosSeleccionados = facturasSeleccionadas.reduce(
    (s, f) => s + Number(f.montoAsignado != null ? f.montoAsignado : f.monto_total || 0),
    0
  );
  const diferenciaMontos = monto - sumaMontosSeleccionados;
  // Exportación (DTE 110): la verdad económica del cobro es USD. Para el DISPLAY de suma/diferencia
  // se usa monto_usd (todas las seleccionables lo tienen) y el monto del movimiento, ya en USD. Esto
  // NO alimenta el cuadre del asiento (sumaCuadraExacto), que queda intacto y exento en exportación.
  const sumaUsdSeleccionadas = facturasSeleccionadas.reduce((s, f) => s + Number(f.monto_usd || 0), 0);
  const diferenciaUsd = monto - sumaUsdSeleccionadas;

  // "Costos del giro" (cobro export, modelo 7.18). Franco carga del SWIFT el monto instruido
  // (InstdAmt) y la comisión del banco internacional (Citibank), y del aviso Santander el monto
  // recibido (IntrBkSttlmAmt). Con el recibido como dato INDEPENDIENTE el cuadre es real; sin él
  // cae a "modo parcial" (solo bloquea negativos). `monto` (del movimiento) es el disponible final.
  const giroFacturado = sumaUsdSeleccionadas;
  const giroInstdAmtNum = parseFloat(giroInstdAmt);
  const giroInstdValido = !isNaN(giroInstdAmtNum) && giroInstdAmtNum > 0;
  const giroCitibankNum = parseFloat(giroCitibank) || 0;
  const giroSantanderRecibidoNum = parseFloat(giroSantanderRecibido);
  const giroRecibidoValido = !isNaN(giroSantanderRecibidoNum) && giroSantanderRecibidoNum > 0;
  // Place = facturado − instruido (siempre por diferencia: no hay dato independiente del take).
  const giroComisionPlace = giroInstdValido ? giroFacturado - giroInstdAmtNum : 0;
  // Santander: con recibido cargado sale del dato INDEPENDIENTE (recibido − disponible); sin él
  // cae al despeje (instruido − Citibank − disponible) = el viejo modo tautológico.
  const giroComisionSantander = giroRecibidoValido
    ? giroSantanderRecibidoNum - monto
    : giroInstdValido
      ? giroInstdAmtNum - giroCitibankNum - monto
      : 0;
  // Cuadre REAL (solo con recibido): dos validaciones que ahora SÍ pueden fallar.
  //  (a) escalón intermedio: instruido − Citibank == recibido Santander (SWIFT vs aviso).
  //  (b) cascada completa: facturado − Place − Citibank − Santander == disponible.
  // Numéricamente (a)≡(b) cuando Santander sale del recibido, pero ya NO es tautológico: ambas
  // fallan juntas si el SWIFT y el aviso no concuerdan (recibido es un dato independiente).
  const giroCuadreA = giroInstdAmtNum - giroCitibankNum - giroSantanderRecibidoNum;
  const giroCuadreB = giroFacturado - giroComisionPlace - giroCitibankNum - giroComisionSantander - monto;
  const giroCuadreAOk = Math.abs(giroCuadreA) < 0.5;
  const giroCuadreBOk = Math.abs(giroCuadreB) < 0.5;
  const giroCuadreReal = giroInstdValido && giroRecibidoValido && giroCuadreAOk && giroCuadreBOk;
  const giroModoParcial = giroInstdValido && !giroRecibidoValido;
  // Gate del botón: instruido + ≥1 factura + componentes no negativos. Con recibido exige además
  // el cuadre real; sin recibido (modo parcial) solo bloquea negativos.
  const costosGiroValidos =
    giroInstdValido &&
    facturasSeleccionadas.length > 0 &&
    giroComisionPlace >= -0.5 &&
    giroComisionSantander >= -0.5 &&
    (giroModoParcial || giroCuadreReal);

  const sumaCuadraExacto = esCobroExportacion
    ? Math.abs(diferenciaMontos) / monto < 0.15
    : Math.abs(diferenciaMontos) < 0.5;

  // Pieza B (parcial-por-factura): reparto válido cuando cada montoAsignado ∈ (0, saldo de su factura]
  // y la suma NO excede el monto del movimiento. El sobrante (monto − Σ) queda A CUENTA (FIFO), NO es
  // propina. Sin editar (montoAsignado = monto_total, Σ = monto) coincide con el cuadre exacto. No aplica
  // a exportación (flujo USD/giro aparte). Regla 10.66 (N→1 / 1→N) y 10.67 (cuadre). Ledger acumula Σ mbf.
  const cadaAsignadoValido = facturasSeleccionadas.every((f) => {
    const a = Number(f.montoAsignado != null ? f.montoAsignado : f.monto_total || 0);
    return a > 0.005 && a <= Number(f.monto_total || 0) + 0.5;
  });
  const reparteParcialValido =
    esModoMultiple && !esCobroExportacion && cadaAsignadoValido && sumaMontosSeleccionados <= monto + 0.5;

  // Wizard export: arrancar siempre en el Paso 1 al abrir el modal o cambiar de movimiento/modo.
  useEffect(() => {
    setPasoExport(1);
  }, [movimiento?.id, modoCobro]);

  // "Cargar SWIFT (PDF)": parsea el PDF en memoria (sin subirlo) y autocompleta los inputs del
  // Paso 2. El cuadre (✓/✗) valida solo, como si Franco hubiera tipeado. Si falla el parseo o el
  // PDF no es un SWIFT, mensaje claro y los inputs quedan como estaban (carga manual disponible).
  const handleCargarSwift = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // permite re-subir el mismo archivo
    if (!file) return;
    setCargandoSwift(true);
    try {
      const r = await parsearSwiftDesdePdf(file);
      if (r.instdAmt != null) setGiroInstdAmt(String(r.instdAmt));
      if (r.citibank != null) setGiroCitibank(String(r.citibank));
      if (r.recibidoSantander != null) setGiroSantanderRecibido(String(r.recibidoSantander));
      if (r.uetr) setGiroUetr(r.uetr);
      if (r.ordenante) setGiroOrdenante(r.ordenante);
      if (r.bic) setGiroOrdenanteBic(r.bic);
      if (r.fechaValor) setGiroFechaValor(r.fechaValor);
      if (r.uetr || r.ordenante || r.bic || r.fechaValor) setGiroSwiftOpen(true);
      toast.success("SWIFT cargado — verificá el cuadre");
    } catch {
      toast.error("No se pudo leer el SWIFT — verificá que sea el PDF correcto");
    } finally {
      setCargandoSwift(false);
    }
  };

  // Cargar opciones de entidad fallback (clientes para cobro, proveedores para pago SII)
  useEffect(() => {
    if (!necesitaSelectorEntidad) return;
    if (entidadFallbackOptions.length > 0) return;
    (async () => {
      try {
        const data = esCobro ? await getClientesActivos(pais) : await getProveedores(pais);
        setEntidadFallbackOptions(data);
      } catch {
        toast.error("Error cargando opciones de entidad");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [necesitaSelectorEntidad, esCobro, pais]);

  const cargarFacturas = async () => {
    setLoading(true);
    try {
      const [data, vinculadas] = await Promise.all([
        esCobro ? getFacturasEmitidas(pais) : getFacturasRecibidas(pais),
        getFacturasYaVinculadas(pais),
      ]);
      let lista;
      if (esCobro) {
        // COBRO (emitidas): estado cobrada/pendiente por `getCobroReal` (chokepoint, regla 10.192) y
        // saldo parcial por Σ MBF. Sin cambios respecto del comportamiento previo (el lado cobro ya
        // usa el criterio completo).
        const yaVinculadas = vinculadas.emitidas;
        const montosAcum = vinculadas.montosAcumEmitidas;
        lista = data
          .filter((f) => !yaVinculadas.has(f.id))
          .map((f) => {
            // Factura parcialmente cobrada por vínculos previos (N movimientos → 1 factura):
            // mostrar el saldo restante en vez del total, conservando el original aparte.
            const yaVinculado = montosAcum?.get(f.id) || 0;
            return yaVinculado > 0
              ? {
                  ...f,
                  monto_total_original: f.monto_total,
                  monto_total: Number(f.monto_total) - yaVinculado,
                  _parcial: true,
                }
              : f;
          })
          .filter((f) => Number(f.monto_total) > 0.01);
      } else {
        // Marca una factura recibida con su saldo restante (parcial), conservando el total original.
        const marcarParcial = (f, saldoRestante) =>
          saldoRestante < Number(f.monto_total) - 0.01
            ? { ...f, monto_total_original: f.monto_total, monto_total: saldoRestante, _parcial: true }
            : f;
        // PAGO (recibidas): estado pendiente/saldada y SALDO restante desde el ledger de proveedor
        // (mismo motor canónico construirLedgerProveedor, regla 10.1/10.106/10.223). El FIFO resuelve los
        // cuatro casos que el criterio liviano MBF/cuotas dejaba pasar: pagos a cuenta sin MBF, NC
        // (incluida la exclusión anula_error, que NO debe incidir fuera de Compras; el resto de NC SÍ
        // reduce el saldo, tal como en Caja), saldo inicial y tolerancia de redondeo. Una factura ausente
        // del map (clave "SII:<id>") está saldada → no se ofrece.
        //
        // Fetch UNA sola vez para TODOS los proveedores (getSaldosRecibidasPorFactura) — el resultado por
        // factura es idéntico a llamar getSaldosPorFacturaProveedor por proveedor (mismo motor e inputs),
        // pero evita el fan-out de re-fetchear el universo country-wide una vez por cada proveedor del
        // listado (85 en Chile ≈ ~595 queries). Deuda de perf P2.66 resuelta.
        let saldoRecMap;
        try {
          saldoRecMap = await getSaldosRecibidasPorFactura(pais);
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error("Error computando saldos FIFO por factura recibida:", e);
          saldoRecMap = new Map();
        }
        // CON proveedor_id: solo las que el ledger deja con saldo pendiente (clave "SII:<id>").
        const conProveedor = data
          .filter((f) => f.proveedor_id && saldoRecMap.has(f.id))
          .map((f) => marcarParcial(f, saldoRecMap.get(f.id)));
        // SIN proveedor_id: no hay ledger por proveedor → se conserva el criterio puente (Σ MBF),
        // igual que las boletas sin proveedor. No se pueden descartar en silencio (fallo silencioso, 10.177).
        const sinProveedor = data
          .filter((f) => !f.proveedor_id && !vinculadas.recibidas.has(f.id))
          .map((f) => marcarParcial(f, Number(f.monto_total) - (vinculadas.montosAcumRecibidas?.get(f.id) || 0)));
        lista = [...conProveedor, ...sinProveedor].filter((f) => Number(f.monto_total) > 0.01);
      }

      // Enriquecer facturas de exportación (DTE 110) con el nombre del cliente de la venta
      // vinculada (factura → venta_ref_id → sales.cliente). UN solo fetch para todos los
      // venta_ref_id, nunca una query por fila. Solo para display en el sub-tab Exportación.
      const refIds = [
        ...new Set(
          lista
            .filter((f) => f.tipo_dte === 110 && f.venta_tipo === "sales" && f.venta_ref_id != null)
            .map((f) => f.venta_ref_id)
        ),
      ];
      let clientePorRef = {};
      if (refIds.length > 0) {
        const { data: ventas } = await client.from("sales").select("id, cliente").in("id", refIds);
        clientePorRef = (ventas || []).reduce((acc, v) => ({ ...acc, [v.id]: v.cliente }), {});
      }

      setFacturas(
        lista.map((f) =>
          f.tipo_dte === 110 && f.venta_ref_id != null && clientePorRef[f.venta_ref_id]
            ? { ...f, _clienteNombre: clientePorRef[f.venta_ref_id] }
            : f
        )
      );

      // Boletas de honorarios (F2a): SOLO en el flujo "Pago a proveedor" (no cobro). Se traen las
      // computa=true del país. El saldo pendiente sale por DOS vías según la boleta tenga proveedor o no:
      //  · CON proveedor_id → saldo FIFO del ledger (getSaldosPorFacturaProveedor, claves "BHE:<id>"),
      //    una llamada por proveedor distinto (mismo patrón que el saldo de externas). Las saldadas
      //    (sin clave BHE:) no se muestran como pendientes.
      //  · SIN proveedor_id → no hay ledger por proveedor, así que el saldo es monto_bruto − Σ mbf.monto
      //    (pagos directos por el puente boleta_honorario), reusando `montosAcumBoletas` de
      //    getFacturasYaVinculadas (10.1 — la MISMA fuente del vínculo, sin duplicar el cálculo). Antes
      //    estas boletas se descartaban en silencio (provIds.filter(Boolean) → nunca tenían clave BHE:) y
      //    quedaban invisibles e impagables: fallo silencioso prohibido por 10.177. Se marcan
      //    `_sinProveedor` para el gate de resolución inline del proveedor (fase siguiente).
      // Carga aislada: NO entran a `facturas` ni a `facturasSeleccionadas`, viven en su propio state.
      if (!esCobro) {
        const { data: boletasRaw } = await client
          .from("boletas_honorarios")
          .select("id, proveedor_id, folio, emisor_nombre, emisor_rut, fecha_emision, monto_bruto, computa")
          .eq("pais", pais)
          .eq("computa", true);
        const boletas = boletasRaw || [];
        const provIds = [...new Set(boletas.map((b) => b.proveedor_id).filter(Boolean))];
        const saldoBoletaMap = new Map();
        await Promise.all(
          provIds.map(async (pid) => {
            try {
              const sp = await getSaldosPorFacturaProveedor(pid, pais);
              for (const [key, { saldoDisplay }] of sp) {
                if (key.startsWith("BHE:")) saldoBoletaMap.set(Number(key.slice(4)), saldoDisplay);
              }
            } catch (e) {
              // eslint-disable-next-line no-console
              console.error("Error computando saldo FIFO por boleta de honorarios:", e);
            }
          })
        );
        // CON proveedor: saldo FIFO (las saldadas quedan fuera del map). SIN proveedor: monto_bruto − Σ mbf.
        const conProveedor = boletas
          .filter((b) => b.proveedor_id && saldoBoletaMap.has(b.id))
          .map((b) => ({ ...b, _saldoPendiente: saldoBoletaMap.get(b.id) }));
        const montosAcumBoletas = vinculadas.montosAcumBoletas || new Map();
        const sinProveedor = boletas
          .filter((b) => !b.proveedor_id)
          .map((b) => ({
            ...b,
            _saldoPendiente: Number(b.monto_bruto || 0) - (montosAcumBoletas.get(b.id) || 0),
            _sinProveedor: true,
          }))
          .filter((b) => b._saldoPendiente > 0.01);
        setBoletasHonorarios([...conProveedor, ...sinProveedor]);
      } else {
        setBoletasHonorarios([]);
      }
    } catch {
      toast.error("Error cargando facturas");
    } finally {
      setLoading(false);
    }
  };

  const cargarEmpleados = async () => {
    setLoading(true);
    try {
      const [data, finiquitados] = await Promise.all([getEmpleados(pais), getEmpleadoIdsConFiniquito(pais)]);
      const fechaMov = parseFechaLocal(movimiento?.fecha);
      // Activos (sin fin_contrato o aún vigente a la fecha del movimiento) siempre se muestran.
      // Egresados se muestran solo si son recientes (egreso_fecha >= FECHA_CORTE) y todavía no
      // tienen un pago de finiquito registrado — para poder vincularles ese finiquito.
      const visibles = data
        .map((e) => ({
          ...e,
          _esEgresado: !!e.fin_contrato && parseFechaLocal(e.fin_contrato) < fechaMov,
        }))
        .filter((e) => {
          if (!e._esEgresado) return true;
          return e.egreso_fecha && e.egreso_fecha >= FECHA_CORTE && !finiquitados.has(e.id);
        });
      setEmpleados(visibles);
      const desc = (movimiento?.descripcion_raw || "").toLowerCase();
      const match = visibles.find((e) => {
        const nombre = (e.nombre || "").toLowerCase();
        const apellido = (e.apellido || "").toLowerCase();
        return nombre && apellido && desc.includes(nombre) && desc.includes(apellido);
      });
      if (match) setEmpleadoId(match.id);
    } catch {
      toast.error("Error cargando empleados");
    } finally {
      setLoading(false);
    }
  };

  // Toggle de selección. Click en factura ya seleccionada la quita; click en otra la agrega.
  // Modo múltiple (length>=2): valida que todas sean del mismo proveedor/cliente y tengan
  // entidad cargada (no NULL). En modo single se preserva el flujo actual con cuotas.
  const handleSeleccionarFactura = async (f) => {
    const yaEsta = facturasSeleccionadas.some((x) => x.id === f.id);
    // Selección de factura EXCLUYENTE con las boletas: limpiar las boletas tildadas.
    setBoletasSeleccionadas([]);
    setPlanPorBoleta({});

    // Exportación: solo las facturas con snapshot USD (monto_usd) son cobrables en este flujo USD.
    // Las sin USD se muestran deshabilitadas; este guard es defensa en profundidad (bloquea AGREGAR,
    // no QUITAR). Ver display en la lista del sub-tab Exportación.
    if (!yaEsta && esCobroExportacion && f.monto_usd == null) return;

    // El reparto cambió → ocultar el toggle "forzar" (reaparece solo al re-intentar confirmar).
    setIntentoForzarConfirm(false);
    setEsForzado(false);

    // QUITAR
    if (yaEsta) {
      const restantes = facturasSeleccionadas.filter((x) => x.id !== f.id);
      setFacturasSeleccionadas(restantes);
      setPlanPorFactura((prev) => {
        const next = { ...prev };
        delete next[f.id];
        return next;
      });
      setFacturaSeleccionada(restantes.length > 0 ? restantes.at(-1) : null);
      return;
    }

    // CANDADO anula_error (ER v2 Fase 3): una factura recibida ANULADA por una NC 'anula_error' NO
    // admite pagos — el pago pertenece a la factura reemitida, no a la anulada. Solo lectura, bloquea
    // AGREGAR. NO aplica a 'ajuste_comercial' (esas siguen contando normal). El vínculo NC→factura es
    // reverso (NC.documento_referenciado_id = f.id), así que se consulta por ese lado. Solo pagos
    // (recibidas): en cobros (esCobro) la factura es emitida y no aplica.
    if (!esCobro) {
      const { data: ncsAnula, error: ncErr } = await client
        .from("facturas_sii_recibidas")
        .select("folio")
        .eq("documento_referenciado_id", f.id)
        .eq("tipo_relacion_nc", "anula_error");
      if (ncErr) {
        return toast.error("No se pudo verificar si la factura fue anulada por una NC. No se agregó.");
      }
      if (ncsAnula && ncsAnula.length > 0) {
        const folios = ncsAnula.map((n) => n.folio).join(", ");
        return toast.error(
          `La factura folio ${f.folio} fue anulada por la NC folio ${folios} (anula_error). ` +
            "No se puede imputar un pago acá — el pago va a la factura reemitida."
        );
      }
    }

    // AGREGAR — validación de misma entidad (proveedor/cliente), o mismo receptor en exportación.
    if (facturasSeleccionadas.length > 0) {
      // Exportación (DTE 110): cliente_id es el anunciante de fondo y varía por campaña, pero el
      // cobro USD lo liquida un único receptor fiscal (intermediario). Validar igualdad de receptor
      // en vez de cliente. Se usa razon_social_receptor porque rut_receptor es el genérico
      // 55555555-5 para todo receptor extranjero y no discrimina entre intermediarios distintos.
      if (esCobroExportacion) {
        const receptorEsperado = facturasSeleccionadas[0].razon_social_receptor;
        if (receptorEsperado && f.razon_social_receptor !== receptorEsperado) {
          return toast.error("Todas las facturas deben ser del mismo receptor (intermediario).");
        }
      } else {
        const entidadCampo = esCobro ? "cliente_id" : "proveedor_id";
        const entidadEsperada = facturasSeleccionadas[0][entidadCampo];
        if (!f[entidadCampo] && !esCobro) {
          return toast.error(
            "La factura no tiene proveedor asociado. Seleccionala sola primero para asignarle entidad."
          );
        }
        if (entidadEsperada && f[entidadCampo] !== entidadEsperada) {
          return toast.error(`Todas las facturas deben ser del mismo ${esCobro ? "cliente" : "proveedor"}.`);
        }
      }
    }

    // Cargar el plan de cuotas DISPONIBLES de la factura (no aplica a exportación). FASE 5: la
    // disponibilidad y el monto pendiente de cada cuota se DERIVAN del libro `aplicaciones_saldo`
    // (Σ eventos aplicados a esa cuota), no del flag `estado` escalar — así una cuota parcialmente
    // pagada aparece con su saldo real (caso MMS: queda $415.326), no como "pagada". El `monto_cuota`
    // que devuelve el helper YA es el remanente (lo usan el tilde, el cuadre y el dual-write).
    let planPend = [];
    if (!esCobroExportacion) {
      try {
        const plan = esCobro ? await getPlanCobros(f.id) : await getPlanPagos(f.id);
        const { data: eventosLibro } = await client
          .from("aplicaciones_saldo")
          .select("cuota_id, monto_documento")
          .eq("documento_tipo", esCobro ? "sii_emitida" : "sii_recibida")
          .eq("documento_id", f.id);
        planPend = cuotasDisponiblesDesdeLibro(plan, eventosLibro || []);
      } catch {
        planPend = [];
      }
    }

    // Con plan → el monto se deriva de las cuotas tildadas (sin montoAsignado). Sin plan → pre-cargar
    // montoAsignado = min(saldo, remanente del movimiento) para el campo editable (10.66/10.67).
    const saldoFactura = Number(f.monto_total || 0);
    const yaAsignado = facturasSeleccionadas.reduce((s, x) => s + montoAplicadoFactura(x), 0);
    const remanenteMov = Math.abs(monto) - yaAsignado;
    const montoAsignado = planPend.length > 0 ? undefined : Math.max(0, Math.min(saldoFactura, remanenteMov));

    setPlanPorFactura((prev) => ({ ...prev, [f.id]: planPend }));
    setFacturasSeleccionadas([
      ...facturasSeleccionadas,
      { ...f, cuotasSeleccionadas: [], ...(montoAsignado != null ? { montoAsignado } : {}) },
    ]);
    setFacturaSeleccionada(f);
    setEntidadFallbackId(null);
    setEntidadFallbackBusqueda("");
    setCreandoProvSII(false);
    setProvSIINombre("");
    setProvSIIRut("");
    setCreandoClienteSII(false);
    setClienteSIIEmpresa("");
    setClienteSIIRut("");
  };

  // Tildar/destildar una cuota de una factura con plan. El monto aplicado a esa factura se deriva de
  // lo tildado (no editable). Cambiar el reparto oculta el toggle "forzar".
  const handleToggleCuota = (facturaId, cuota) => {
    setIntentoForzarConfirm(false);
    setEsForzado(false);
    setFacturasSeleccionadas((prev) =>
      prev.map((f) => {
        if (f.id !== facturaId) return f;
        const sel = f.cuotasSeleccionadas || [];
        const yaEsta = sel.some((c) => c.id === cuota.id);
        return { ...f, cuotasSeleccionadas: yaEsta ? sel.filter((c) => c.id !== cuota.id) : [...sel, cuota] };
      })
    );
  };

  // Editar el monto manual de una factura SIN plan. Clamp a [0, saldo]. Cambiar el reparto oculta "forzar".
  const handleMontoAsignado = (facturaId, valor) => {
    setIntentoForzarConfirm(false);
    setEsForzado(false);
    setFacturasSeleccionadas((prev) =>
      prev.map((f) => {
        if (f.id !== facturaId) return f;
        const saldo = Number(f.monto_total || 0);
        const limpio = Number(String(valor).replace(/[^\d.-]/g, ""));
        const val = isNaN(limpio) ? 0 : Math.max(0, Math.min(saldo, limpio));
        return { ...f, montoAsignado: val };
      })
    );
  };

  // Toggle de selección para facturas externas pendientes. Misma regla que SII:
  // todas las facturas seleccionadas deben ser del mismo proveedor (comparación por ID).
  const handleSeleccionarExterna = (f) => {
    const yaEsta = externasSeleccionadas.some((x) => x.id === f.id);
    if (yaEsta) {
      setExternasSeleccionadas(externasSeleccionadas.filter((x) => x.id !== f.id));
      return;
    }
    if (externasSeleccionadas.length > 0 && f.proveedor_id !== externasSeleccionadas[0].proveedor_id) {
      return toast.error("Todas las facturas deben ser del mismo proveedor.");
    }
    setExternasSeleccionadas([...externasSeleccionadas, f]);
  };

  // Crea un proveedor desde el selector inline del path Pago SII y lo deja seleccionado.
  // El nombre y el RUT se pre-cargan desde la factura SII para minimizar fricción.
  const handleCrearProveedorDesdeSII = async () => {
    if (!provSIINombre.trim()) return toast.error("Ingresá el nombre del proveedor");
    try {
      const nuevo = await crearProveedor({
        pais,
        nombre: provSIINombre.trim(),
        rut_ruc: provSIIRut.trim() || null,
        habilitado: true,
      });
      // Ya existe uno con ese RUT (guard anti-duplicado): usar el existente en vez de crear.
      const entidad = nuevo?.yaExiste ? nuevo.proveedor : nuevo;
      if (!entidad?.id) throw new Error("No se pudo crear el proveedor");
      setEntidadFallbackOptions((prev) =>
        prev.some((p) => p.id === entidad.id)
          ? prev
          : [...prev, entidad].sort((a, b) => (a.nombre || "").localeCompare(b.nombre || ""))
      );
      setEntidadFallbackId(entidad.id);
      // Creado de verdad SOLO si no cayó en el guard yaExiste (insert nuevo). Si ya existía, es asignado.
      setEntidadFallbackCreada(!nuevo?.yaExiste);
      setCreandoProvSII(false);
      setProvSIINombre("");
      setProvSIIRut("");
      if (nuevo?.yaExiste) {
        toast(`Ya existe un proveedor con RUT ${entidad.rut_ruc || ""}: ${entidad.nombre}. Se usó el existente.`);
      } else {
        toast.success("Proveedor creado y seleccionado");
      }
    } catch (err) {
      toast.error("Error creando proveedor: " + (err.message || ""));
    }
  };

  // Simétrico al anterior pero para clientes (path Cobro SII).
  const handleCrearClienteDesdeSII = async () => {
    if (!clienteSIIEmpresa.trim()) return toast.error("Ingresá la razón social del cliente");
    try {
      const nuevo = await crearClienteMinimal({
        pais,
        empresa: clienteSIIEmpresa.trim(),
        nombre: clienteSIIEmpresa.trim(),
        rut: clienteSIIRut.trim() || null,
      });
      // Ya existe uno con ese RUT (guard anti-duplicado): usar el existente en vez de crear.
      const entidad = nuevo?.yaExiste ? nuevo.cliente : nuevo;
      if (!entidad?.id) throw new Error("No se pudo crear el cliente");
      setEntidadFallbackOptions((prev) =>
        prev.some((c) => c.id === entidad.id)
          ? prev
          : [...prev, entidad].sort((a, b) => (a.empresa || a.nombre || "").localeCompare(b.empresa || b.nombre || ""))
      );
      setEntidadFallbackId(entidad.id);
      setCreandoClienteSII(false);
      setClienteSIIEmpresa("");
      setClienteSIIRut("");
      if (nuevo?.yaExiste) {
        toast(
          `Ya existe un cliente con RUT ${entidad.rut || ""}: ${entidad.empresa || entidad.nombre}. Se usó el existente.`
        );
      } else {
        toast.success("Cliente creado y seleccionado");
      }
    } catch (err) {
      toast.error("Error creando cliente: " + (err.message || ""));
    }
  };

  const actualizarComponente = (idx, campo, valor) => {
    setComponentes((prev) => prev.map((c, i) => (i === idx ? { ...c, [campo]: valor } : c)));
  };
  const agregarComponente = () => {
    const restante = monto - totalComponentes;
    setComponentes((prev) => [
      ...prev,
      { ...nuevaLineaComponente(movimiento, "bono"), monto: restante > 0 ? restante : "" },
    ]);
  };
  const eliminarComponente = (idx) => {
    setComponentes((prev) => prev.filter((_, i) => i !== idx));
  };

  // Clave estable de una línea pendiente (para linkear el componente tildado y derivar el checkbox).
  const keyPendiente = (p) => `${p.tipo}-${p.periodo_año}-${p.periodo_mes}`;
  // Tildar/destildar una línea de "Pendientes del empleado": crea (con tipo+período+monto EXACTOS del
  // motor, no se tipea) o saca el componente correspondiente. Al agregar la primera, reemplaza el
  // placeholder inicial intacto (una sola línea, sin origen, con el monto del movimiento). El componente
  // tildado lleva `origen_pendiente` → el candado (10.231) lo saltea (es correcto por construcción).
  const togglePendiente = (p) => {
    const key = keyPendiente(p);
    setComponentes((prev) => {
      if (prev.some((c) => c.origen_pendiente === key)) {
        return prev.filter((c) => c.origen_pendiente !== key);
      }
      return [
        ...prev,
        {
          tipo: p.tipo,
          periodo_año: p.periodo_año,
          periodo_mes: p.periodo_mes,
          monto: p.monto,
          notas: "",
          origen_pendiente: key,
        },
      ];
    });
  };

  // Absorbe la diferencia de redondeo bancario (motor vs monto real del movimiento, ej. Franco $70)
  // en la ÚLTIMA línea tildada, para que Σ componentes cuadre exacto contra el movimiento. Solo tiene
  // sentido si la diferencia es de redondeo (≤ TOLERANCIA_SALDO_LIQUIDO) y hay al menos una línea tildada.
  const ajustarRedondeo = () => {
    setComponentes((prev) => {
      let idx = -1;
      for (let i = prev.length - 1; i >= 0; i--) {
        if (prev[i].origen_pendiente) {
          idx = i;
          break;
        }
      }
      if (idx === -1) return prev;
      const total = prev.reduce((s, c) => s + Number(c.monto || 0), 0);
      const falta = Number(monto) - total; // lo que hay que sumarle a esa línea para cuadrar
      return prev.map((c, i) => (i === idx ? { ...c, monto: Number(c.monto || 0) + falta } : c));
    });
  };

  const totalComponentes = useMemo(() => componentes.reduce((s, c) => s + Number(c.monto || 0), 0), [componentes]);
  const diferencia = monto - totalComponentes;
  const cuadra = Math.abs(diferencia) < 0.5;
  // Puede ofrecerse el botón "ajustar redondeo": no cuadra por una diferencia de redondeo bancario
  // (≤ tolerancia) y hay al menos una línea que vino de un pendiente tildado (donde absorberla).
  const puedeAjustarRedondeo =
    !cuadra && Math.abs(diferencia) <= TOLERANCIA_SALDO_LIQUIDO && componentes.some((c) => c.origen_pendiente);

  // Filtro de VISUALIZACIÓN del picker (NO toca `getPendientesRemuneracion` — Futuros lo necesita
  // completo en Fase 3): se listan los períodos ATRASADOS (saldo de un mes ya pasado = plata que se
  // debe de verdad, sin límite hacia atrás) + desde el mes actual, máximo 3 meses (actual + 2
  // siguientes). Como todo atrasado es `< mes actual ≤ límite`, alcanza con `período ≤ límite`.
  const pendientesVisibles = useMemo(() => {
    const hoy = new Date();
    const limite = new Date(hoy.getFullYear(), hoy.getMonth() + 2, 1);
    const limiteYm = `${limite.getFullYear()}-${String(limite.getMonth() + 1).padStart(2, "0")}`;
    return pendientesRemuneracion.filter(
      (p) => `${p.periodo_año}-${String(p.periodo_mes).padStart(2, "0")}` <= limiteYm
    );
  }, [pendientesRemuneracion]);

  // Pendientes del empleado (sueldo + bono, todos los períodos con saldo) para el listado read-only.
  // Reusa getPendientesRemuneracion — MISMA fuente que Futuros/RRHH (10.1). Solo Chile. Se computa al
  // elegir empleado (no depende de los componentes tipeados).
  useEffect(() => {
    if (!show || pais !== "Chile" || !empleadoId) {
      setPendientesRemuneracion([]);
      return undefined;
    }
    const empleado = empleados.find((e) => String(e.id) === String(empleadoId));
    if (!empleado) {
      setPendientesRemuneracion([]);
      return undefined;
    }
    let activo = true;
    (async () => {
      try {
        const [remuneraciones, haberes, pagos] = await Promise.all([
          getRemuneracionesEmpleado(empleadoId),
          getHaberesEmpleado(empleadoId),
          getPagosEmpleados({ empleadoId }),
        ]);
        await loadExchangeRates();
        await loadUTM();
        const pend = getPendientesRemuneracion(empleado, {
          remuneraciones,
          haberes,
          pagos,
          uf: getUFValue,
          utm: getUTMValue,
          añoBase: new Date().getFullYear(),
          // Comisión: se REUSA el comisionGen ya calculado por el panel padre (FlujoCajaV2), sin
          // recalcular ni fetch nuevo. Solo aplica al comercial que matchee comisionGen.empleadoId.
          comisionGen,
        });
        if (activo) setPendientesRemuneracion(pend);
      } catch {
        if (activo) setPendientesRemuneracion([]);
      }
    })();
    return () => {
      activo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, pais, empleadoId, comisionGen]);

  // --- Modo "Pago a edificio": helpers de asignación multi-edificio + cuadre exacto ---
  const agregarEdificioAsignacion = () => {
    const id = Number(edificioSelectorId);
    if (!id) return;
    if (edificiosAsignaciones.some((a) => a.edificioId === id)) return; // ya agregado
    const ed = edificiosSaldo.find((e) => e.edificioId === id);
    if (!ed) return;
    // Primer edificio (reparto vacío): autocompletar con el monto total del movimiento → queda
    // cuadrado y el confirmar se habilita solo (caso 1 edificio, el más común). Si ya hay filas,
    // el nuevo entra en 0 y el usuario reparte a mano (NO redistribuimos los montos ya tipeados).
    const esPrimero = edificiosAsignaciones.length === 0;
    setEdificiosAsignaciones((prev) => [
      ...prev,
      {
        edificioId: id,
        edificioNombre: ed.edificioNombre,
        proveedorId: ed.proveedorId ?? null,
        monto: esPrimero ? Math.abs(monto) : "",
      },
    ]);
    setEdificioSelectorId("");
  };
  const actualizarEdificioAsignacion = (edificioId, valor) => {
    setEdificiosAsignaciones((prev) => prev.map((a) => (a.edificioId === edificioId ? { ...a, monto: valor } : a)));
  };
  const quitarEdificioAsignacion = (edificioId) => {
    setEdificiosAsignaciones((prev) => prev.filter((a) => a.edificioId !== edificioId));
  };

  // Cuadre EXACTO (tolerancia 0): Σ montos tipeados === abs(monto del movimiento). Moneda local,
  // mismo signo, sin conversión → no hay drift cambiario que tolerar.
  const totalEdificiosAsignado = edificiosAsignaciones.reduce((s, a) => s + Number(a.monto || 0), 0);
  const montoAbsMovimiento = Math.abs(monto);
  const restanteEdificios = montoAbsMovimiento - totalEdificiosAsignado;
  const edificiosCuadra = edificiosAsignaciones.length > 0 && restanteEdificios === 0;
  // Guard de validación del "Pago a edificio" (se usa en confirmarDisabled). Requiere solo:
  // al menos un edificio + reparto que cuadra exacto + todos los montos > 0. La factura es
  // adjunto opcional de soporte: NO es requisito y no bloquea el confirmar.
  const edificioConfirmarDisabled =
    edificiosAsignaciones.length === 0 || !edificiosCuadra || edificiosAsignaciones.some((a) => Number(a.monto) <= 0);

  // ── Modo "Costo recurrente": selección de ocurrencias + cuadre (mismo criterio que edificios) ──
  const toggleOcurrenciaRecurrente = (o) => {
    const key = keyOcurrencia(o);
    setRecSeleccion((prev) => {
      const next = new Map(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        // Primera seleccionada → prefill con el monto completo del banco; el resto en blanco.
        next.set(key, prev.size === 0 ? montoAbsMovimiento : "");
      }
      return next;
    });
  };
  const setMontoOcurrenciaRecurrente = (o, valor) => {
    const key = keyOcurrencia(o);
    setRecSeleccion((prev) => {
      const next = new Map(prev);
      if (next.has(key)) next.set(key, valor);
      return next;
    });
  };
  const totalRecAsignado = [...recSeleccion.values()].reduce((s, v) => s + Number(v || 0), 0);
  const restanteRec = montoAbsMovimiento - totalRecAsignado;
  const recCuadra = recSeleccion.size > 0 && restanteRec === 0;
  const recConfirmarDisabled =
    !recProveedorId ||
    !recCuentaER ||
    recSeleccion.size === 0 ||
    !recCuadra ||
    [...recSeleccion.values()].some((v) => Number(v) <= 0);

  const handleConfirmarRecurrente = async () => {
    if (!recProveedorId) return toast.error("Seleccioná un proveedor");
    if (!recCuentaER) return toast.error("Seleccioná la cuenta del Estado de Resultados");
    if (recSeleccion.size === 0) return toast.error("Seleccioná al menos una ocurrencia");
    if (!recCuadra) return toast.error("La suma asignada debe igualar el monto del movimiento");
    if ([...recSeleccion.values()].some((v) => Number(v) <= 0)) return toast.error("Cada monto debe ser mayor a 0");
    setLoading(true);
    try {
      const asignaciones = recOcurrencias
        .filter((o) => recSeleccion.has(keyOcurrencia(o)))
        .map((o) => ({
          costo_asociado_id: o.costo_asociado_id ?? null,
          concepto: o.concepto,
          periodo_año: o.periodo_año,
          periodo_mes: o.periodo_mes,
          monto: Number(recSeleccion.get(keyOcurrencia(o))),
        }));
      const res = await conciliarOcurrenciasRecurrentes({
        movimiento,
        proveedor: { id: Number(recProveedorId), nombre: recProveedorNombre },
        asignaciones,
        cuentaEr: recCuentaER,
        pais,
        userId: usuarioId || null,
      });
      if (res?.yaVinculado) {
        toast("Este movimiento ya estaba vinculado");
      } else {
        const n = asignaciones.length;
        toast.success(`${n} ocurrencia${n > 1 ? "s" : ""} recurrente${n > 1 ? "s" : ""} conciliada${n > 1 ? "s" : ""}`);
      }
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error("Error al conciliar: " + (err.message || ""));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmarEmpleado = async () => {
    if (!empleadoId) return toast.error("Seleccioná un empleado");
    if (componentes.length === 0) return toast.error("Agregá al menos un componente");
    if (!cuadra) {
      return toast.error(
        `La suma (${formatAmount(totalComponentes, moneda)}) debe igualar el monto del movimiento (${formatAmount(
          monto,
          moneda
        )})`
      );
    }
    for (const c of componentes) {
      if (!c.tipo) return toast.error("Faltó tipo en un componente");
      if (TIPOS_REQUIEREN_PERIODO.includes(c.tipo) && (!c.periodo_año || !c.periodo_mes)) {
        return toast.error(`Tipo "${c.tipo}" requiere período (año/mes)`);
      }
      if (!c.monto || Number(c.monto) <= 0) return toast.error("Monto debe ser > 0 en todos los componentes");
    }
    setLoading(true);
    try {
      // Candado (10.1, CUALQUIER empleado Chile): valida los componentes 'Sueldo' y 'Bono' contra el
      // líquido del período según el motor (`liquidoBonoSplit` → dos corridas de calcularLiquidacionChile,
      // patrón bonoNeto de FlujoCajaV2 — no se reimplementa la fórmula, 10.19/10.1). Motivo: un adelanto
      // parcial tageado 'sueldo' dispara el ancla de `saldoLiquidoPorPeriodo` (devengado=pagado→saldo 0) y
      // BORRA el sueldo proyectado del mes (bug Franco sept/oct). Capa de carga; NO toca el ancla/Fase B
      // (P3.17). Solo Chile (Perú usa modelo plano sin motor canónico).
      //   • 'Sueldo' válido SOLO contra su porción base: liquidoSinBono − adelantos − otrosSueldos
      //     (ESTRICTO, decisión Ignacio: un pago sueldo+bono junto se carga en DOS componentes, no deja
      //     el bono implícito en un solo 'Sueldo').
      //   • 'Bono' se valida SOLO si hay bono de VIGENCIA (bonoNeto>0): monto == bonoNeto − otrosBonos.
      //     Bonos discrecionales/one-off (empleado sin `bonos_fijo` → bonoNeto=0) NO se validan.
      // Solo los componentes TIPEADOS a mano pasan por el candado. Los que vinieron de tildar un
      // pendiente (`origen_pendiente`) ya son correctos por construcción (monto del motor) → se saltean,
      // aunque se haya ajustado su monto a mano por redondeo bancario (el cuadre exacto es el backstop).
      const necesitanCandado = componentes.filter(
        (c) => (c.tipo === "sueldo" || c.tipo === "bono") && !c.origen_pendiente
      );
      if (pais === "Chile" && necesitanCandado.length > 0) {
        const remuneraciones = await getRemuneracionesEmpleado(empleadoId);
        await loadExchangeRates();
        await loadUTM();
        const ctxPorPeriodo = new Map(); // "año-mes" -> { rem, split, adelantosDB, sueldosDB, bonosDB } | { rem:null }
        const contextoPeriodo = async (anio, mes) => {
          const key = `${anio}-${mes}`;
          if (ctxPorPeriodo.has(key)) return ctxPorPeriodo.get(key);
          const rem = remuneracionVigenteParaMes(remuneraciones, anio, mes);
          let ctx;
          if (!rem || Number(rem.sueldo_base) <= 0) {
            ctx = { rem: null }; // sin vigencia → no se puede validar → no bloquea
          } else {
            const ultimoDia = new Date(anio, mes, 0).toISOString().split("T")[0];
            const haberes = await getHaberesEmpleadoMes(empleadoId, anio, mes);
            const haberesMes = (haberes || []).map((h) => ({ monto: h.monto, imponible: h.imponible }));
            const split = liquidoBonoSplit(rem, getUFValue(ultimoDia), getUTMValue(ultimoDia), haberesMes);
            const pagosMes = await getPagosEmpleadoMes(empleadoId, anio, mes);
            const sumTipo = (t) =>
              (pagosMes || []).filter((p) => p.tipo === t).reduce((s, p) => s + Number(p.monto || 0), 0);
            ctx = {
              rem,
              split,
              adelantosDB: sumTipo("adelanto"),
              sueldosDB: sumTipo("sueldo"),
              bonosDB: sumTipo("bono"),
            };
          }
          ctxPorPeriodo.set(key, ctx);
          return ctx;
        };
        // Σ componentes del form de un tipo/período, EXCLUYENDO el componente actual (por referencia).
        const sumForm = (tipo, anio, mes, excepto) =>
          componentes
            .filter(
              (x) => x !== excepto && x.tipo === tipo && Number(x.periodo_año) === anio && Number(x.periodo_mes) === mes
            )
            .reduce((s, x) => s + Number(x.monto || 0), 0);
        for (const c of necesitanCandado) {
          const anio = Number(c.periodo_año);
          const mes = Number(c.periodo_mes);
          const ctx = await contextoPeriodo(anio, mes);
          if (!ctx.rem) continue;
          const { liquidoSinBono, bonoNeto } = ctx.split;
          const adelantos = ctx.adelantosDB + sumForm("adelanto", anio, mes, c);
          const monto = Number(c.monto);
          if (c.tipo === "sueldo") {
            // ESTRICTO (decisión Ignacio 2026-08-29): el 'Sueldo' valida SOLO contra la porción de sueldo
            // base (`liquidoSinBono` − adelantos − otros sueldos). Un pago que cubre sueldo+bono junto DEBE
            // cargarse en DOS componentes (Sueldo + Bono, con "+ Componente"), no dejar el bono implícito.
            const otrosSueldos = ctx.sueldosDB + sumForm("sueldo", anio, mes, c);
            const esperado = Math.round(liquidoSinBono - adelantos - otrosSueldos);
            if (Math.abs(monto - esperado) > TOLERANCIA_SALDO_LIQUIDO) {
              setLoading(false);
              const hint =
                bonoNeto > 0
                  ? "Si el pago cubre sueldo + bono, cargá el Bono en un componente aparte (+ Componente). "
                  : "";
              return toast.error(
                `El Sueldo de ${MESES[mes - 1]} ${anio} debería ser ${formatAmount(esperado, moneda)}. ${hint}` +
                  `Si es un pago parcial, cargá ${formatAmount(monto, moneda)} como Adelanto.`,
                { duration: 9000 }
              );
            }
          } else if (c.tipo === "bono") {
            if (bonoNeto <= TOLERANCIA_SALDO_LIQUIDO) continue; // sin bono de vigencia → discrecional → no se valida
            const otrosBonos = ctx.bonosDB + sumForm("bono", anio, mes, c);
            const espBono = Math.round(bonoNeto - otrosBonos);
            if (Math.abs(monto - espBono) > TOLERANCIA_SALDO_LIQUIDO) {
              setLoading(false);
              return toast.error(
                `El Bono de vigencia de ${MESES[mes - 1]} ${anio} debería ser ${formatAmount(espBono, moneda)}. ` +
                  "Si estás pagando un bono variable/one-off distinto, revisá el monto o el tipo.",
                { duration: 9000 }
              );
            }
          }
        }
      }
      const rows = componentes.map((c) => ({
        empleado_id: empleadoId,
        tipo: c.tipo,
        periodo_año: c.periodo_año ? Number(c.periodo_año) : null,
        periodo_mes: c.periodo_mes ? Number(c.periodo_mes) : null,
        monto: Number(c.monto),
        moneda,
        fecha_pago: movimiento.fecha,
        movimiento_bancario_id: movimiento.id,
        notas: c.notas?.trim() ? c.notas.trim() : null,
        created_by: usuarioId || null,
      }));
      const pagosInsertados = await crearPagosEmpleado(rows);
      toast.success("Pago a empleado registrado");
      // Empleado: el badge E depende de pagosEmpleadosRegistrados (state separado en
      // el padre). Pasamos las filas insertadas para que el padre las inyecte a ese
      // state (update optimista) en lugar de recargar todo.
      onSuccess?.({ id: movimiento.id, _empleado: true, _pagosEmpleado: pagosInsertados });
      onClose();
    } catch (err) {
      toast.error("Error al registrar pago: " + (err.message || ""));
    } finally {
      setLoading(false);
    }
  };

  // Transporte: sub-modo simple (mismo patrón que Caja Chica — sin desglose, sin campos). El
  // movimiento COMPLETO es una asignación de movilización. Persiste UNA fila en pagos_empleados con
  // tipo='transporte' y SIN período (no está en TIPOS_REQUIEREN_PERIODO: no compone líquido ni netea
  // un mes). No toca movimientos_bancarios (igual que handleConfirmarEmpleado); el badge E y la cuenta
  // corriente lo derivan de la fila.
  const handleConfirmarTransporte = async () => {
    if (!empleadoId) return toast.error("Seleccioná un empleado");
    const emp = empleados.find((e) => String(e.id) === String(empleadoId));
    if (!emp) return toast.error("Empleado no encontrado");
    setLoading(true);
    try {
      const rows = [
        {
          empleado_id: empleadoId,
          tipo: "transporte",
          periodo_año: null,
          periodo_mes: null,
          monto: Number(movimiento.monto),
          moneda,
          fecha_pago: movimiento.fecha,
          movimiento_bancario_id: movimiento.id,
          notas: null,
          created_by: usuarioId || null,
        },
      ];
      const pagosInsertados = await crearPagosEmpleado(rows);
      toast.success(`Transporte registrado para ${emp.nombre}`);
      onSuccess?.({ id: movimiento.id, _empleado: true, _pagosEmpleado: pagosInsertados });
      onClose();
    } catch (err) {
      toast.error("Error al registrar transporte: " + (err.message || ""));
    } finally {
      setLoading(false);
    }
  };

  // Adelanto: sub-modo simple (mismo patrón que Caja Chica/Transporte — sin desglose, sin input de
  // monto). El movimiento COMPLETO es un adelanto a cuenta del líquido del período elegido. Persiste
  // UNA fila en pagos_empleados con tipo='adelanto' y CON período (adelanto ∈ TIPOS_REQUIEREN_PERIODO;
  // el CHECK periodo_segun_tipo exige periodo_año+periodo_mes). A diferencia de Caja Chica, NO escribe
  // en movimientos_caja: reduce el saldo de sueldo del mes vía la cuenta corriente (saldoLiquidoPorPeriodo).
  // No toca movimientos_bancarios (igual que handleConfirmarEmpleado); el badge E y el saldo lo derivan.
  const handleConfirmarAdelanto = async () => {
    if (!empleadoId) return toast.error("Seleccioná un empleado");
    if (!adelantoMes || !adelantoAño) return toast.error("Elegí el mes y año que se imputa el adelanto");
    const emp = empleados.find((e) => String(e.id) === String(empleadoId));
    if (!emp) return toast.error("Empleado no encontrado");
    setLoading(true);
    try {
      const rows = [
        {
          empleado_id: empleadoId,
          tipo: "adelanto",
          periodo_año: Number(adelantoAño),
          periodo_mes: Number(adelantoMes),
          monto: Number(movimiento.monto),
          moneda,
          fecha_pago: movimiento.fecha,
          movimiento_bancario_id: movimiento.id,
          notas: null,
          created_by: usuarioId || null,
        },
      ];
      const pagosInsertados = await crearPagosEmpleado(rows);
      toast.success(`Adelanto registrado para ${emp.nombre}`);
      onSuccess?.({ id: movimiento.id, _empleado: true, _pagosEmpleado: pagosInsertados });
      onClose();
    } catch (err) {
      toast.error("Error al registrar adelanto: " + (err.message || ""));
    } finally {
      setLoading(false);
    }
  };

  // Caja chica: categoriza un egreso bancario como entrega de fondos a un empleado.
  // Escribe en movimientos_caja (categoria_pago='caja_chica', entidad_tipo='empleado') y
  // confirma el bancario — NO usa pagos_empleados. entidad_id es INTEGER y referencia
  // usuarios.id, así que necesitamos el usuario_id del empleado (no su id UUID). Si el
  // empleado no tiene usuario vinculado, abortamos con mensaje claro (sin esto el insert
  // tiraría "invalid input syntax for type integer").
  const handleConfirmarCajaChica = async () => {
    if (!empleadoId) return toast.error("Seleccioná un empleado");
    const emp = empleados.find((e) => String(e.id) === String(empleadoId));
    if (!emp) return toast.error("Empleado no encontrado");
    if (!emp.usuario_id) {
      return toast.error(`${emp.nombre} no tiene usuario vinculado — no se puede registrar caja chica`);
    }
    const usuarioIdEmp = emp.usuario_id;

    setLoading(true);
    try {
      const movCaja = {
        tipo: "pago",
        fecha: movimiento.fecha,
        monto: Number(movimiento.monto),
        moneda: movimiento.moneda || moneda,
        estado: "confirmado",
        cuenta_banco: movimiento.cuenta_bancaria_id,
        entidad_tipo: "empleado",
        entidad_id: usuarioIdEmp,
        entidad_nombre: `${emp.nombre} ${emp.apellido}`.trim(),
        categoria_pago: "caja_chica",
        descripcion: movimiento.descripcion_raw || "Caja chica",
        pais,
        registrado_por: usuarioId,
        movimiento_bancario_id: movimiento.id,
        origen: "clay",
      };

      const { data: cajaData, error: cajaError } = await client
        .from("movimientos_caja")
        .upsert(movCaja, { onConflict: "movimiento_bancario_id" })
        .select()
        .single();
      if (cajaError) throw cajaError;

      await client
        .from("movimientos_bancarios")
        .update({
          estado: "procesado",
          movimiento_caja_id: cajaData.id,
          procesado_por: usuarioId,
          procesado_at: new Date().toISOString(),
        })
        .eq("id", movimiento.id);

      toast.success(`Vinculado a caja chica de ${emp.nombre}`);
      onSuccess?.({
        id: movimiento.id,
        movimiento_caja_id: cajaData.id,
        movimiento_caja: cajaData,
        estado: "procesado",
      });
      onClose();
    } catch (err) {
      toast.error("Error: " + (err.message || ""));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmarSinFactura = async () => {
    // Proveedor es OPCIONAL en este flujo: un gasto operativo chico (café, taxi, propina) no siempre
    // tiene un proveedor formal dado de alta. Si no se elige, la fila queda sin entidad (entidad_tipo
    // 'otro', entidad_id null) — mismo patrón que costo_financiero. No amortiza deuda de ningún proveedor.
    // P3 (red de seguridad): un proveedor que emite factura no puede cerrarse como gasto sin factura,
    // aunque el usuario llegue al confirm por un camino que esquive el gate del tab. Por id (10.7).
    // Con sfProveedorId null, Number(null)=0 → find no matchea → check no-op (seguro).
    if (proveedores.find((p) => p.id === Number(sfProveedorId))?.es_extranjero) {
      return toast.error(`${sfProveedorNombre || "Este proveedor"} emite factura — cargala en "Factura externa"`);
    }
    if (!sfConcepto.trim()) return toast.error("Ingresá el concepto del gasto");
    if (!sfCuentaER) return toast.error("Seleccioná la cuenta del Estado de Resultados");
    setLoading(true);
    try {
      // Idempotencia por movimiento_bancario_id
      const { data: existente, error: errSelect } = await client
        .from("movimientos_caja")
        .select("id")
        .eq("movimiento_bancario_id", movimiento.id)
        .maybeSingle();
      if (errSelect) throw errSelect;

      let movCajaId = existente?.id || null;
      if (!existente) {
        const movCaja = await crearMovimiento({
          pais,
          tipo: "pago",
          fecha: movimiento.fecha,
          monto,
          monto_neto: monto,
          monto_impuesto: 0,
          moneda,
          estado: "confirmado",
          descripcion: sfConcepto.trim(),
          entidad_tipo: sfProveedorId ? "proveedor" : "otro",
          entidad_id: sfProveedorId || null,
          entidad_nombre: sfProveedorId ? sfProveedorNombre : null,
          categoria_pago: "gasto_sin_factura",
          cuenta_er: sfCuentaER,
          origen: "clay",
          movimiento_bancario_id: movimiento.id,
          registrado_por: usuarioId || null,
        });
        if (!movCaja?.id) throw new Error("No se pudo crear el movimiento en caja");
        movCajaId = movCaja.id;
      }

      await confirmarMovimiento(movimiento.id, {
        movimiento_caja_id: movCajaId,
        procesado_por: usuarioId || null,
      });

      toast.success("Gasto sin factura registrado");
      onSuccess?.({
        id: movimiento.id,
        estado: "procesado",
        movimiento_caja_id: movCajaId,
        movimiento_caja: { categoria_pago: "gasto_sin_factura" },
      });
      onClose();
    } catch (err) {
      toast.error("Error al registrar: " + (err.message || ""));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmarACuenta = async () => {
    if (!acProveedorId) return toast.error("Seleccioná un proveedor");
    const categoriaResuelta = acBucket === "factura" ? "pago_factura" : "pago_credito";
    setLoading(true);
    try {
      const { data: existente, error: errSelect } = await client
        .from("movimientos_caja")
        .select("id")
        .eq("movimiento_bancario_id", movimiento.id)
        .maybeSingle();
      if (errSelect) throw errSelect;

      let movCajaId = existente?.id || null;
      if (!existente) {
        const movCaja = await crearMovimiento({
          pais,
          tipo: "pago",
          fecha: movimiento.fecha,
          monto,
          monto_neto: monto,
          monto_impuesto: 0,
          moneda,
          estado: "confirmado",
          descripcion: acNota.trim() || movimiento.descripcion_raw || movimiento.descripcion || "",
          entidad_tipo: "proveedor",
          entidad_id: acProveedorId,
          entidad_nombre: acProveedorNombre,
          categoria_pago: categoriaResuelta,
          cuenta_er: null,
          origen: "clay",
          movimiento_bancario_id: movimiento.id,
          registrado_por: usuarioId || null,
        });
        if (!movCaja?.id) throw new Error("No se pudo crear el movimiento en caja");
        movCajaId = movCaja.id;
      }

      await confirmarMovimiento(movimiento.id, {
        movimiento_caja_id: movCajaId,
        procesado_por: usuarioId || null,
      });

      toast.success("Pago a cuenta registrado");
      onSuccess?.({
        id: movimiento.id,
        estado: "procesado",
        movimiento_caja_id: movCajaId,
        movimiento_caja: { categoria_pago: categoriaResuelta },
        // Patch scoped: el pago a cuenta entra al ledger FIFO del proveedor → recalcular solo ese ledger.
        _scopedProveedorId: acProveedorId,
      });
      onClose();
    } catch (err) {
      toast.error("Error al registrar: " + (err.message || ""));
    } finally {
      setLoading(false);
    }
  };

  // ── Forzado por faltante (10.108 invertida): auditoría + rollback ──────────────────────────
  // Inserta la fila de vinculaciones_forzadas ANTES de escribir MBF/caja (orden seguro: si esto
  // falla, se aborta y las facturas quedan ABIERTAS — el lado seguro). Idempotente: reemplaza
  // cualquier fila previa del mismo movimiento (la tabla no tiene UNIQUE → delete + insert).
  // Devuelve el id para el rollback manual. NO toca ledgerProveedor (el cierre al 100% sale solo
  // de las MBF sobre-asignadas, Pasada 2, exactamente como la propina). Ver 10.108 / 10.160.
  const insertarAuditoriaForzado = async (facturasForzadas) => {
    await client.from("vinculaciones_forzadas").delete().eq("movimiento_bancario_id", movimiento.id);
    const { data, error } = await client
      .from("vinculaciones_forzadas")
      .insert({
        movimiento_bancario_id: movimiento.id,
        usuario_id: usuarioId,
        pais,
        faltante: montoFaltante,
        facturas: facturasForzadas,
        estado: "pendiente",
        motivo: null,
        nc_factura_id: null,
      })
      .select("id")
      .single();
    if (error) throw error;
    return data?.id ?? null;
  };
  // Rollback best-effort de la fila de auditoría si MBF/caja fallan DESPUÉS (no dejar la marca sin
  // vinculación). Se traga su propio error para no enmascarar el error real que disparó el catch.
  const rollbackAuditoriaForzado = async (auditId) => {
    if (!auditId) return;
    try {
      await client.from("vinculaciones_forzadas").delete().eq("id", auditId);
    } catch {
      /* best-effort: si el rollback falla, la fila queda pendiente sin MBF (caso raro) */
    }
  };

  // Modo múltiple: 1 mov bancario vincula a N facturas SII (mismo proveedor/cliente).
  // Las FKs directas en movimientos_bancarios.factura_sii_*_id quedan en NULL; las N
  // vinculaciones se registran en la tabla movimiento_bancario_facturas. Se crea 1 sola
  // fila en movimientos_caja con el monto del banco y monto_neto/monto_impuesto sumados.
  const handleConfirmarMultiple = async () => {
    // Blindaje del forzado: si el toggle quedó en ON pero el gate NO habilita → cortar. Nunca
    // confiar solo en el estado del toggle (el gate puede haber dejado de aplicar). Ver 10.108.
    if (esForzado && !puedeForzar) return toast.error("No estás autorizado a forzar esta vinculación.");
    // La propina (excedente sin DTE, 10.79) es un sobrepago legítimo: la suma de facturas es MENOR al
    // débito por la propina. Con propina marcada + excedente positivo se permite (no exige cuadre exacto).
    const excedentePropinaMultiple = esPropina && diferenciaMontos > 0.5;
    // Forzado por faltante (10.108 invertida): la Σ de facturas es MAYOR al débito por el faltante.
    // Con el gate autorizado se permite forzar (no exige cuadre exacto): las MBF sobre-asignadas
    // cierran las facturas al 100% y la caja guarda el débito real.
    if (
      !sumaCuadraExacto &&
      !reparteParcialValido &&
      !esCobroExportacion &&
      !excedentePropinaMultiple &&
      !(esForzado && puedeForzar)
    ) {
      return toast.error(
        "La suma debe coincidir con el monto del movimiento, o repartilo parcial (cada factura ≤ su saldo, Σ ≤ el monto)."
      );
    }
    // Guardrail blando propina (MISMO umbral/criterio que el single, 10.108): si la propina supera el 13%
    // de Σ facturas, pedir confirmación (NO bloquea). propinaPorcentaje/montoPropina ya se miden contra
    // totalFacturasSeleccionadas. No aplica a cobros (esPropina es false ahí).
    if (esPropina && propinaPorcentaje > 0.13) {
      const ok = window.confirm(
        `La propina (${formatAmount(montoPropina, moneda)}) supera el 13% de las facturas (${formatAmount(
          totalFacturasSeleccionadas,
          moneda
        )}) — ¿facturas correctas?\n\n¿Confirmar de todos modos?`
      );
      if (!ok) return;
    }
    const entidadCampo = esCobro ? "cliente_id" : "proveedor_id";
    const entidadId = facturasSeleccionadas[0][entidadCampo];
    const entidadNombre = esCobro
      ? facturasSeleccionadas[0].razon_social_receptor
      : facturasSeleccionadas[0].razon_social_emisor;
    setLoading(true);
    let forzadoAuditId = null;
    try {
      // 0. Forzado por faltante: registrar la auditoría ANTES de MBF/caja. Si falla, throw → no se
      //    escribe nada (facturas abiertas, lado seguro). Idempotente por movimiento.
      if (esForzado && puedeForzar) {
        forzadoAuditId = await insertarAuditoriaForzado(
          facturasSeleccionadas.map((f) => ({
            factura_tipo: esCobro ? "sii_emitida" : "sii_recibida",
            factura_id: f.id,
            monto_total: Number(f.monto_total || 0),
          }))
        );
      }

      // 1. UPDATE movimientos_bancarios (B3 Fase B: no se tocan las FK directas — el vínculo vive en
      //    el puente MBF, insertado en el paso 2).
      const updateMov = {
        estado: "procesado",
        procesado_por: usuarioId || null,
        procesado_at: new Date().toISOString(),
        match_entidad_tipo: null,
        match_entidad_id: null,
        match_entidad_nombre: null,
        match_factura_tipo: null,
        match_factura_id: null,
        match_confianza: null,
        match_razon: null,
      };
      await client.from("movimientos_bancarios").update(updateMov).eq("id", movimiento.id);

      // 2. INSERT N filas en movimiento_bancario_facturas
      const filasMBF = facturasSeleccionadas.map((f) => ({
        movimiento_bancario_id: movimiento.id,
        factura_tipo: esCobro ? "sii_emitida" : "sii_recibida",
        factura_id: f.id,
        // Pieza B: monto vinculado = ASIGNADO (parcial editable), no el total. Sin editar = monto_total
        // (multi previo). El ledger acumula Σ mbf.monto y deja la factura parcial visible con su saldo
        // restante (getFacturasYaVinculadas la marca "vinculada" solo si Σ ≥ total). Regla 10.66/10.67.
        monto: Number(f.montoAsignado != null ? f.montoAsignado : f.monto_total || 0),
      }));
      const { error: errMBF } = await client.from("movimiento_bancario_facturas").insert(filasMBF);
      if (errMBF) throw errMBF;

      // 3. UPDATE cada factura SII: vinculada + plazopago/facturafechacobro
      for (const f of facturasSeleccionadas) {
        const tabla = esCobro ? "facturas_sii_emitidas" : "facturas_sii_recibidas";
        const update = {
          estado_vinculacion: "vinculada",
          vinculado_por: usuarioId || null,
          vinculado_at: new Date().toISOString(),
        };
        if (esCobro) update.facturafechacobro = movimiento.fecha;
        else update.plazopago = movimiento.fecha;
        const { error: errF } = await client.from(tabla).update(update).eq("id", f.id);
        if (errF) throw errF;

        // Replicar a tabla 1:N facturas_sii_vinculaciones (solo emitidas con venta legacy ya populada).
        // Sin esto el modal de DTE las muestra como pendientes. L4: por RPC (índices parciales, 10.202) —
        // cartelería/hardware por linea_id, pauta/contratos/BTL por venta. `facturafechacobro` ya lo setea
        // el update de arriba (la RPC SII no lo toca).
        if (esCobro && f.venta_tipo && f.venta_ref_id != null) {
          const esCart = f.venta_tipo === "negocios_carteleria" || f.venta_tipo === "negocios_carteleria_hw";
          if (esCart) {
            const lineaId = await getLineaIdPorPosicion(f.venta_ref_id, f.venta_tipo, f.venta_ref_index);
            if (lineaId == null) {
              throw new Error(
                `No se pudo resolver la línea de facturación del negocio ${f.venta_ref_id} (folio ${f.folio}). Reintentá; si persiste, avisá.`
              );
            }
            const { error: rpcErr } = await client.rpc("vincular_sii_linea", {
              p_factura_id: f.id,
              p_linea_id: lineaId,
              p_monto_aplicado: null, // fsv_a_fill lo llena con el neto (paridad; explícito en F4/L6)
              p_usuario_id: usuarioId || null,
              p_cliente_id: null,
            });
            if (rpcErr) throw rpcErr;
          } else {
            const { error: rpcErr } = await client.rpc("vincular_sii_venta", {
              p_factura_id: f.id,
              p_venta_tipo: f.venta_tipo,
              p_venta_ref_id: f.venta_ref_id,
              p_venta_ref_index: f.venta_ref_index ?? null,
              p_usuario_id: usuarioId || null,
              p_cliente_id: null,
            });
            if (rpcErr) throw rpcErr;
          }
        }
      }

      // 4. INSERT movimientos_caja (idempotente por movimiento_bancario_id)
      const { data: existente, error: errSelect } = await client
        .from("movimientos_caja")
        .select("id")
        .eq("movimiento_bancario_id", movimiento.id)
        .maybeSingle();
      if (errSelect) throw errSelect;

      let movCajaIdMultiple = existente?.id || null;
      if (!existente) {
        const totalNeto = facturasSeleccionadas.reduce((s, f) => s + Number(f.monto_neto || 0), 0);
        const totalIva = facturasSeleccionadas.reduce((s, f) => s + Number(f.monto_iva || 0), 0);
        const folios = facturasSeleccionadas.map((f) => f.folio).join(", ");
        // Breadcrumb de propina: la caja guarda el débito completo; el excedente sobre Σ facturas es la
        // propina sin DTE (los MBF guardan cada factura por su monto_total, así el FIFO no sobre-acredita).
        const sufijoPropina = esPropina ? ` · incl. propina ${formatAmount(montoPropina, moneda)}` : "";
        // Breadcrumb de forzado: la caja guarda el débito real; el faltante sobre Σ facturas quedó
        // registrado en vinculaciones_forzadas (las MBF sobre-asignadas cierran las facturas al 100%).
        const sufijoForzado =
          esForzado && puedeForzar ? ` · forzado, faltante ${formatAmount(montoFaltante, moneda)}` : "";
        const movCaja = await crearMovimiento({
          pais,
          tipo: esCobro ? "cobro" : "pago",
          fecha: movimiento.fecha,
          monto, // del movimiento bancario
          monto_neto: totalNeto,
          monto_impuesto: totalIva,
          moneda: movimiento.moneda || (pais === "Chile" ? "CLP" : "PEN"),
          estado: "confirmado",
          descripcion: `${entidadNombre} | Folios ${folios}${sufijoPropina}${sufijoForzado}`,
          entidad_tipo: esCobro ? "cliente" : "proveedor",
          entidad_id: entidadId,
          entidad_nombre: entidadNombre,
          categoria_pago: esCobro ? "cobro_factura" : "pago_proveedor",
          origen: "clay",
          movimiento_bancario_id: movimiento.id,
          registrado_por: usuarioId || null,
        });
        if (movCaja?.id) {
          await client.from("movimientos_bancarios").update({ movimiento_caja_id: movCaja.id }).eq("id", movimiento.id);
          movCajaIdMultiple = movCaja.id;
        }
      } else if (!esCobro) {
        // Formalización de un pago a cuenta preexistente contra facturas SII recibidas (múltiple): la
        // fila de caja ya existía (bucket 'pago_factura'/'pago_credito' → badge A). Reclasificar a
        // 'pago_proveedor' para que el badge pase de A a C. Solo recibidas (!esCobro); NO toca el
        // motor de imputación del ledger (los MBF ya saldan las facturas).
        // Ver docs/modulos/flujo-caja-v2.md (§4.2) / estado-cuenta.md (§4.9.2).
        await client.from("movimientos_caja").update({ categoria_pago: "pago_proveedor" }).eq("id", existente.id);
      }

      // 5. Cobro export (modelo 7.18): registrar el giro internacional (transferencias_internacionales)
      //    y derivar los costos de la capa programática (costos_intermediacion), prorrateados por
      //    factura. Idempotente por movimiento_bancario_id. monto_real poblado (es el real del SWIFT);
      //    la capa agencia_local NO se escribe acá (es fase 6). Si la transferencia falla, no se
      //    escriben costos (throw → lo captura el catch).
      if (esCobroExportacion) {
        const { data: giroExistente } = await client
          .from("transferencias_internacionales")
          .select("id")
          .eq("movimiento_bancario_id", movimiento.id)
          .maybeSingle();
        if (!giroExistente) {
          // Resolver entidades por lookup (NO hardcodear ids: pueden cambiar). Place en
          // intermediarios; Citibank y Santander en proveedores (Santander por RUT 97036000-K
          // para no confundir con "Santander - Comisiones" id 57). Si falta alguna → id null +
          // nombre (no rompe el confirm) + warning.
          const [placeRes, citiRes, santaRes] = await Promise.all([
            client.from("intermediarios").select("id").ilike("nombre", "%place exchange%").limit(1).maybeSingle(),
            client.from("proveedores").select("id").ilike("nombre", "%citi%").limit(1).maybeSingle(),
            client.from("proveedores").select("id").eq("rut_ruc", "97036000-K").limit(1).maybeSingle(),
          ]);
          const placeId = placeRes.data?.id ?? null;
          const citibankId = citiRes.data?.id ?? null;
          const santanderId = santaRes.data?.id ?? null;
          const faltantes = [];
          if (placeId == null) faltantes.push("Place Exchange (intermediarios)");
          if (citibankId == null) faltantes.push("Citibank (proveedores)");
          if (santanderId == null) faltantes.push("Santander 97036000-K (proveedores)");
          if (faltantes.length > 0) {
            // eslint-disable-next-line no-console
            console.warn(
              `[costos_intermediacion] entidad no resuelta, se escribe entidad_id=null: ${faltantes.join(", ")}`
            );
          }

          const referenciaGiro = (movimiento.descripcion_raw || "").match(/Rec Or de Pago\s+(\d+)/)?.[1] || null;
          const { data: giroRow, error: errGiro } = await client
            .from("transferencias_internacionales")
            .insert({
              referencia: referenciaGiro,
              movimiento_bancario_id: movimiento.id,
              ordenante_nombre: giroOrdenante.trim() || null,
              ordenante_banco_bic: giroOrdenanteBic.trim() || null,
              monto_instruido: giroInstdAmtNum,
              comision_banco_internacional: giroCitibankNum,
              // Recibido por el banco local: el dato independiente del aviso si se cargó; si no,
              // el despeje instruido − Citibank (modo parcial).
              monto_recibido_banco_local: giroRecibidoValido
                ? giroSantanderRecibidoNum
                : giroInstdAmtNum - giroCitibankNum,
              moneda: "USD",
              fecha_valor: giroFechaValor || null,
              uetr: giroUetr.trim() || null,
              fuente: "manual",
              pais,
              registrado_por: usuarioId || null,
            })
            .select("id")
            .single();
          if (errGiro) throw errGiro;
          const giroId = giroRow.id;

          // Prorrateo por factura (peso = monto_usd / facturado). El residuo de redondeo va a la
          // última factura para que Σ partes == total del giro exacto.
          const prorratear = (total) => {
            const partes = facturasSeleccionadas.map(
              (f) => Math.round(total * (Number(f.monto_usd || 0) / giroFacturado) * 100) / 100
            );
            const sumaParcial = partes.reduce((s, p) => s + p, 0);
            const lastIdx = partes.length - 1;
            if (lastIdx >= 0) {
              partes[lastIdx] = Math.round((partes[lastIdx] + (total - sumaParcial)) * 100) / 100;
            }
            return partes;
          };
          const partesPlace = prorratear(giroComisionPlace);
          const partesCitibank = prorratear(giroCitibankNum);
          const partesSantander = prorratear(giroComisionSantander);
          const periodoDevengo = `${movimiento.fecha.substring(0, 7)}-01`;

          const filasCostos = [];
          facturasSeleccionadas.forEach((f, i) => {
            const comun = {
              venta_tipo: f.venta_tipo,
              venta_ref_id: f.venta_ref_id,
              base_calculo: Number(f.monto_usd || 0),
              monto_teorico: null, // real directo del SWIFT, no devengo teórico
              moneda: "USD",
              fuente: "manual",
              giro_id: giroId,
              periodo_devengo: periodoDevengo,
              pais,
              registrado_por: usuarioId || null,
              reduce_base_comision: true,
            };
            filasCostos.push(
              {
                ...comun,
                tipo_costo: "intermediario_programatico",
                entidad_tipo: "intermediario",
                entidad_id: placeId, // Place Exchange Inc (lookup intermediarios)
                entidad_nombre: "Place Exchange Inc",
                monto_real: partesPlace[i],
              },
              {
                ...comun,
                tipo_costo: "banco_internacional",
                entidad_tipo: "proveedor",
                entidad_id: citibankId, // Citibank (lookup proveedores)
                entidad_nombre: "Citibank",
                monto_real: partesCitibank[i],
              },
              {
                ...comun,
                tipo_costo: "banco_local",
                entidad_tipo: "proveedor",
                entidad_id: santanderId, // Santander 97036000-K (lookup proveedores)
                entidad_nombre: "Santander",
                monto_real: partesSantander[i],
              }
            );
          });
          const { error: errCostos } = await client.from("costos_intermediacion").insert(filasCostos);
          if (errCostos) throw errCostos;
        }
      }

      toast.success(`${facturasSeleccionadas.length} facturas vinculadas`);
      onSuccess?.({
        id: movimiento.id,
        estado: "procesado",
        movimiento_caja_id: movCajaIdMultiple,
        movimiento_caja: { categoria_pago: esCobro ? "cobro_factura" : "pago_proveedor" },
      });
      onClose();
    } catch (err) {
      // Si MBF/caja fallaron con la auditoría ya escrita → rollback (no dejar marca sin vinculación).
      await rollbackAuditoriaForzado(forzadoAuditId);
      toast.error("Error al vincular: " + (err.message || ""));
    } finally {
      setLoading(false);
    }
  };

  // Modo "Factura externa → Vincular existente": vincula 1..N facturas externas pendientes del mismo
  // proveedor a este movimiento bancario. Fase 1.2a: TODA la escritura pasa por la RPC transaccional
  // `vincular_pago_bancario` (migración 20260920_rpc_pago_externas.sql) vía `ejecutarVinculacionExterna`
  // — MBF + facturas_externas + movimientos_bancarios (match_*/estado) + movimientos_caja +
  // aplicaciones_saldo, todo o nada (10.177). Ya NO escribe tablas directo.
  const handleConfirmarVincularExternas = async () => {
    if (externasSeleccionadas.length === 0) return toast.error("Seleccioná al menos una factura");
    // Guard: no se vincula una factura con saldo PARCIAL. El confirm usa monto_total (marca la factura
    // 100% pagada + MBF por el total + cuadre contra el total) → vincular una parcial haría doble
    // conteo del pago a cuenta ya amortizado por FIFO. El residuo es diferencia de cambio, no deuda:
    // se sincera desde Estado de Cuenta (ajuste futuro), no por banco.
    const parcial = externasSeleccionadas.find((f) => f._parcial);
    if (parcial) {
      return toast.error(
        `No se puede vincular "${parcial.numero_documento || parcial.concepto || `Doc ${parcial.id}`}" con saldo parcial (${formatAmount(
          parcial.saldoDisplay,
          parcial.moneda || moneda
        )}) — el residuo es diferencia de cambio, no deuda por pagar. Se sincera desde Estado de Cuenta.`
      );
    }
    if (!extCuadra) {
      return toast.error(
        "La suma de las facturas debe coincidir con el monto del movimiento (tolerancia 1%, 5% con conversión de moneda)"
      );
    }
    setLoading(true);
    try {
      const { movCajaId, provId, provNombre } = await ejecutarVinculacionExterna({
        movimiento,
        externasSeleccionadas,
        pais,
        usuarioId,
      });
      if (!movCajaId) throw new Error("No se pudo crear el movimiento en caja");

      toast.success(
        externasSeleccionadas.length === 1 ? "Factura vinculada" : `${externasSeleccionadas.length} facturas vinculadas`
      );

      onSuccess?.({
        id: movimiento.id,
        estado: "procesado",
        movimiento_caja_id: movCajaId,
        movimiento_caja: { categoria_pago: "pago_proveedor" },
        match_factura_tipo: "factura_externa",
        match_entidad_nombre: provNombre,
        factura_externa: externasSeleccionadas.map((f) => ({ numero_documento: f.numero_documento || null })),
        // Update optimista del Set de dedup en FlujoCajaV2: hace desaparecer las facturas
        // recién vinculadas del panel "Por pagar" sin esperar un reload completo.
        _externasVinculadas: externasSeleccionadas.map((f) => f.id),
        // Patch scoped del ledger del proveedor (las EXT vinculadas salen del map por saldada). Todas
        // las externasSeleccionadas son del mismo proveedor (validado en UI, L1722).
        _scopedProveedorId: provId,
      });
      onClose();
    } catch (err) {
      toast.error("Error al vincular: " + (err.message || ""));
    } finally {
      setLoading(false);
    }
  };

  // Modo "Pago institucional": INSERT en movimientos_caja sin factura asociada.
  // Sin INSERT en movimiento_bancario_facturas (no hay factura). cuenta_er solo para banco.
  const handleConfirmarInstitucional = async () => {
    const inst = INSTITUCIONES.find((i) => i.value === piInstitucion);
    if (!inst) return toast.error("Seleccioná una institución");
    if (inst.requierePeriodo && (!piPeriodoMes || !piPeriodoAño)) {
      return toast.error(`${inst.label} requiere período (mes y año)`);
    }
    setLoading(true);
    try {
      // Idempotencia
      const { data: existente, error: errSelect } = await client
        .from("movimientos_caja")
        .select("id")
        .eq("movimiento_bancario_id", movimiento.id)
        .maybeSingle();
      if (errSelect) throw errSelect;

      let movCajaId = existente?.id || null;
      if (!existente) {
        const periodoLabel =
          inst.requierePeriodo && piPeriodoMes && piPeriodoAño ? ` — ${MESES[piPeriodoMes - 1]} ${piPeriodoAño}` : "";
        const descripcion = `${inst.label}${periodoLabel}${piNotas.trim() ? ` (${piNotas.trim()})` : ""}`;
        const movCaja = await crearMovimiento({
          pais,
          tipo: "pago",
          fecha: movimiento.fecha,
          monto,
          monto_neto: monto,
          monto_impuesto: 0,
          moneda,
          estado: "confirmado",
          descripcion,
          entidad_tipo: "otro",
          entidad_id: null,
          entidad_nombre: inst.label,
          categoria_pago: inst.categoria_pago,
          cuenta_er: inst.cuenta_er,
          origen: "clay",
          movimiento_bancario_id: movimiento.id,
          registrado_por: usuarioId || null,
        });
        if (!movCaja?.id) throw new Error("No se pudo crear el movimiento en caja");
        movCajaId = movCaja.id;
      }

      await confirmarMovimiento(movimiento.id, {
        movimiento_caja_id: movCajaId,
        procesado_por: usuarioId || null,
      });

      toast.success(`Pago a ${inst.label} registrado`);
      onSuccess?.({
        id: movimiento.id,
        estado: "procesado",
        movimiento_caja_id: movCajaId,
        movimiento_caja: { categoria_pago: inst.categoria_pago },
      });
      onClose();
    } catch (err) {
      toast.error("Error al registrar: " + (err.message || ""));
    } finally {
      setLoading(false);
    }
  };

  // Modo "Pago a edificio" (Fase 2). Molde: handleConfirmarInstitucional. Diferencias:
  //  - Multi-edificio: una fila en movimientos_caja por edificio del reparto (Fase 1, ya con
  //    cuadre exacto validado), TODAS colgando del mismo movimiento_bancario_id. La FK escalar
  //    movimientos_bancarios.movimiento_caja_id apunta a la primera fila (representativa), igual
  //    criterio que el modo múltiple de proveedor.
  //  - categoria_pago: "comision_edificio" EXACTO en ambos modos (si no, getEdificiosSaldoPendiente
  //    no resta el pago del saldo del edificio).
  //  - Modo "Contra factura": además vincula la factura SII recibida del emisor por el mismo
  //    mecanismo que proveedor single (FK factura_sii_recibida_id + factura 'vinculada' +
  //    puente movimiento_bancario_facturas). Modo "A cuenta": sin factura.
  // Fase 3: el pago a edificio es SIEMPRE a cuenta (amortiza el saldo del edificio). La factura
  // SII es un ADJUNTO OPCIONAL de soporte: si se eligió una, se cuelga del pago por el puente
  // movimiento_bancario_facturas, sin marcarla 'vinculada' ni setear el FK escalar del bancario
  // (no es una vinculación factura↔proveedor formal, solo soporte documental del pago a cuenta).
  const handleConfirmarEdificio = async () => {
    if (edificiosAsignaciones.length === 0) return toast.error("Agregá al menos un edificio");
    if (!edificiosCuadra) return toast.error("La suma asignada debe igualar el monto del movimiento");
    if (edificiosAsignaciones.some((a) => Number(a.monto) <= 0)) return toast.error("Cada monto debe ser mayor a 0");
    setLoading(true);
    try {
      // Idempotencia por movimiento_bancario_id. OJO: crea N filas en movimientos_caja,
      // así que NO usamos .maybeSingle() (rompería con ≥2 filas) — chequeamos existencia con limit(1).
      const { data: existentes, error: errSelect } = await client
        .from("movimientos_caja")
        .select("id")
        .eq("movimiento_bancario_id", movimiento.id)
        .limit(1);
      if (errSelect) throw errSelect;

      let primeraCajaId = existentes?.[0]?.id || null;
      if (!existentes?.length) {
        const idsCreadas = [];
        for (const a of edificiosAsignaciones) {
          const montoFila = Number(a.monto);
          const movCaja = await crearMovimiento({
            pais,
            tipo: "pago",
            fecha: movimiento.fecha,
            monto: montoFila,
            monto_neto: montoFila,
            monto_impuesto: 0,
            moneda,
            estado: "confirmado",
            descripcion: `Comisión ${a.edificioNombre}`,
            entidad_tipo: "edificio",
            entidad_id: Number(a.edificioId),
            entidad_nombre: a.edificioNombre,
            categoria_pago: "comision_edificio",
            cuenta_er: null,
            origen: "clay",
            movimiento_bancario_id: movimiento.id,
            registrado_por: usuarioId || null,
          });
          if (!movCaja?.id) throw new Error("No se pudo crear el movimiento en caja");
          idsCreadas.push(movCaja.id);
        }
        primeraCajaId = idsCreadas[0];
        await client
          .from("movimientos_bancarios")
          .update({ movimiento_caja_id: primeraCajaId })
          .eq("id", movimiento.id);
      }

      // Adjunto opcional: si se eligió una factura, colgarla por el puente MBF como soporte
      // (idempotente). No marca la factura 'vinculada' ni setea factura_sii_recibida_id.
      if (facturaSeleccionada) {
        const { data: mbfExistente } = await client
          .from("movimiento_bancario_facturas")
          .select("id")
          .eq("movimiento_bancario_id", movimiento.id)
          .eq("factura_tipo", "sii_recibida")
          .eq("factura_id", facturaSeleccionada.id)
          .maybeSingle();
        if (!mbfExistente) {
          await client.from("movimiento_bancario_facturas").insert({
            movimiento_bancario_id: movimiento.id,
            factura_tipo: "sii_recibida",
            factura_id: facturaSeleccionada.id,
            monto: Math.abs(Number(movimiento.monto || 0)),
          });
        }
      }

      await confirmarMovimiento(movimiento.id, {
        movimiento_caja_id: primeraCajaId,
        procesado_por: usuarioId || null,
      });

      const nEd = edificiosAsignaciones.length;
      toast.success(`Pago a ${nEd} edificio${nEd > 1 ? "s" : ""} registrado`);

      // El pago amortiza el saldo del edificio (crédito categoria_pago='comision_edificio').
      // No hay señal de factura que dispare el recálculo selectivo en el padre, así que
      // onSuccess() sin payload cae en el branch !id de handleVincularSuccess → cargarDatos()
      // completo, que recomputa edificiosSaldo. (No tocamos FlujoCajaV2.)
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error("Error al registrar: " + (err.message || ""));
    } finally {
      setLoading(false);
    }
  };

  // Path "Compra de activo" (D.2, PASO 2). Fase 3B = CIERRE real, todo manual. Reusa el mecanismo de
  // "Pago a proveedor" para cerrar el DTE (que salga de "por pagar") PERO capitalizando en vez de
  // gastar: movimientos_caja categoria_pago='compra_activo' + activo_id, cuenta_er=null. Y en vez de
  // crear un activo nuevo, RECONCILIA el existente ('ejecutado_sin_factura') → 'activo' con
  // fecha_compra = fecha del DTE (arranca la depreciación; getDepreciacionMensual exige estado='activo'
  // + fecha_compra). NO toca "Pago a proveedor" (solo reusa su lógica).
  const handleConfirmarActivo = async () => {
    const activo = activosCapex.find((a) => a.id === activoCapexSel);
    const factura = facturas.find((f) => f.id === facturaActivoSel);
    if (!activo || !factura) {
      return toast.error("Seleccioná el activo y la factura para vincular");
    }
    setLoading(true);
    try {
      // (a) UPDATE movimientos_bancarios: procesado + limpiar match_*. B3 Fase B: la vinculación a la
      //     factura recibida vive en el puente MBF (insertado en (d)), no en la FK directa.
      await client
        .from("movimientos_bancarios")
        .update({
          estado: "procesado",
          procesado_por: usuarioId || null,
          procesado_at: new Date().toISOString(),
          match_entidad_tipo: null,
          match_entidad_id: null,
          match_entidad_nombre: null,
          match_factura_tipo: null,
          match_factura_id: null,
          match_confianza: null,
          match_razon: null,
        })
        .eq("id", movimiento.id);

      // (b) UPDATE facturas_sii_recibidas: fecha de pago + marcarla vinculada.
      await client.from("facturas_sii_recibidas").update({ plazopago: movimiento.fecha }).eq("id", factura.id);
      await client
        .from("facturas_sii_recibidas")
        .update({
          estado_vinculacion: "vinculada",
          vinculado_por: usuarioId || null,
          vinculado_at: new Date().toISOString(),
        })
        .eq("id", factura.id);

      // (c) INSERT movimientos_caja (idempotente por movimiento_bancario_id): CAPITALIZABLE →
      //     categoria_pago='compra_activo', activo_id, cuenta_er=null (no es gasto del ER), entidad=proveedor.
      const { data: existente, error: errSelect } = await client
        .from("movimientos_caja")
        .select("id")
        .eq("movimiento_bancario_id", movimiento.id)
        .maybeSingle();
      if (errSelect) throw errSelect;

      let movCajaIdSingle = existente?.id || null;
      if (!existente) {
        const movCaja = await crearMovimiento({
          pais,
          tipo: "pago",
          fecha: movimiento.fecha,
          monto: Math.abs(Number(movimiento.monto || 0)),
          monto_neto: factura.monto_neto,
          monto_impuesto: factura.monto_iva,
          moneda: movimiento.moneda || (pais === "Chile" ? "CLP" : "PEN"),
          estado: "confirmado",
          descripcion: `Compra activo: ${activo.tipo_activo} | ${activo.edificioNombre} · Folio ${factura.folio}`,
          entidad_tipo: "proveedor",
          entidad_id: factura.proveedor_id || null,
          entidad_nombre: factura.razon_social_emisor,
          categoria_pago: "compra_activo",
          cuenta_er: null,
          activo_id: activo.id,
          origen: movimiento.origen || "clay",
          movimiento_bancario_id: movimiento.id,
          registrado_por: usuarioId || null,
        });
        if (movCaja?.id) {
          await client.from("movimientos_bancarios").update({ movimiento_caja_id: movCaja.id }).eq("id", movimiento.id);
          movCajaIdSingle = movCaja.id;
        }
      } else {
        // Asiento preexistente (pago a cuenta ya registrado): reclasificar a compra_activo + pegar activo_id.
        await client
          .from("movimientos_caja")
          .update({ categoria_pago: "compra_activo", activo_id: activo.id })
          .eq("id", existente.id);
      }

      // (d) INSERT movimiento_bancario_facturas (idempotente): monto = monto_total de la factura →
      //     Σ mbf.monto ≥ monto_total → getFacturasYaVinculadas la saca de "por pagar".
      const { data: mbfExistente } = await client
        .from("movimiento_bancario_facturas")
        .select("id")
        .eq("movimiento_bancario_id", movimiento.id)
        .eq("factura_tipo", "sii_recibida")
        .eq("factura_id", factura.id)
        .maybeSingle();
      if (!mbfExistente) {
        await client.from("movimiento_bancario_facturas").insert({
          movimiento_bancario_id: movimiento.id,
          factura_tipo: "sii_recibida",
          factura_id: factura.id,
          monto: Number(factura.monto_total || 0),
        });
      }

      // (e) Reconciliar el activo EXISTENTE (NO crear otro): arranca la depreciación con la fecha del DTE.
      await actualizarActivo(activo.id, {
        estado: "activo",
        fecha_compra: factura.fecha_emision?.substring(0, 10) || movimiento.fecha,
        factura_sii_recibida_id: factura.id,
      });

      toast.success("Activo vinculado a la factura · depreciación iniciada");
      onSuccess?.({
        id: movimiento.id,
        estado: "procesado",
        movimiento_caja_id: movCajaIdSingle,
        movimiento_caja: { categoria_pago: "compra_activo" },
        factura_sii_recibida_id: factura.id,
        factura_recibida: {
          folio: factura.folio,
          razon_social_emisor: factura.razon_social_emisor,
        },
      });
      onClose();
    } catch (err) {
      toast.error("Error al vincular activo: " + (err.message || ""));
    } finally {
      setLoading(false);
    }
  };

  // Saldar N proyecciones CapEx caja-pura (cuotas de activo + componentes de lote, MEZCLADAS) contra UN
  // movimiento bancario. Un pago al proveedor suele ser un adelanto que cubre varios conceptos → se
  // "hermana" la proyección con la caja. Caja CONSOLIDADA: UNA sola fila movimientos_caja que refleja el
  // banco (respeta la UNIQUE viva sobre movimiento_bancario_id — no la dropeamos). El "quién pagó qué"
  // vive en el movimiento_bancario_id de cada cuota/componente (N-tolerante). IVA opcional: los ítems son
  // netos y el banco bruto; el IVA se grossea automático por ítem desde exento_iva. NO toca activos, lotes, unidades ni
  // depreciación (eso va por la factura / ejecución del lote, aparte). NO MBF, NO FIFO. Idempotente por
  // movimiento_bancario_id. Ver ARQUITECTURA 4.2.nn / 4.2.oo / 10.100 / 10.160.
  const handleConfirmarCapexMultiple = async () => {
    const items = capexItemsSel;
    if (items.length === 0) return toast.error("Seleccioná al menos una cuota o componente");
    setLoading(true);
    try {
      // 1. Marcar cada target pagado + SEMBRAR su evento en el subledger pagos_capex, ATÓMICO (Fase 1,
      //    RPC saldar_capex_targets). El flip/materialización y el evento van en UNA transacción: si el
      //    evento falla, revierte el flip (10.177/10.239) — y como la caja se escribe DESPUÉS, tampoco
      //    queda caja. Todo-o-nada aún: el abono cubre el objetivo COMPLETO → monto_origen == monto_target
      //    == objetivo (moneda del target); monto_local (moneda país) se congela por el TC del pago, solo
      //    para totales multimoneda (10.185). El pago parcial es Fase 3.
      //    · cuota / componente: UPDATE a 'pagada'/'pagado' (target existente).
      //    · activo planificado-sin-plan: MATERIALIZA una cuota única ya 'pagada' (numero_cuota=1, centinela).
      //    · FOB del lote: MATERIALIZA una fila lote_costos tipo='fob' ya 'pagada' (centinela).
      //    La lectura de Futuros/picker sigue por el flag `estado` (el cutover al ledger es Fase 2), así
      //    que esto es INVISIBLE. Idempotente por el UNIQUE (mov, target_tipo, target_id) dentro de la RPC.
      await loadExchangeRates();
      const monedaLocalPais = pais === "Perú" ? "PEN" : "CLP";
      const tcDeMoneda = (mon, fch) => {
        const m = (mon || "").toUpperCase();
        if (m === monedaLocalPais) return 1;
        if (pais === "Chile" && m.includes("UF")) return getUFValue(fch);
        if (m.includes("USD") || m.includes("US$")) return getDolarValue(fch, pais);
        return 1;
      };
      const fechaPago = movimiento.fecha;
      const rpcItems = items.map((it) => {
        // Fase 3b-i: se aplica el monto ASIGNADO (parcial), no el objetivo. `objetivo` (completo) va aparte
        // para que la RPC pueda nacer la fila materializada (activo/fob) — 3a lo usa en el find-or-create.
        const aplicado = Number(it.montoAsignado ?? it.montoNeto ?? 0);
        return {
          tipo: it.tipo,
          id: it.id, // cuota/comp: id del target; activo/fob: activo_id/lote_id a materializar.
          monto_origen: aplicado,
          moneda: it.moneda,
          monto_target: aplicado, // moneda del target; el saldo se recomputa en la RPC (Σ monto_target).
          monto_local: convertirAMonedaLocal(aplicado, it.moneda, pais, fechaPago),
          tc: tcDeMoneda(it.moneda, fechaPago),
          objetivo: Number(it.objetivoNeto ?? it.montoNeto ?? 0), // completo, para materializar activo/fob (3a)
          fecha: fechaPago,
          fecha_planificada: it.fecha_planificada || it.fecha || null,
        };
      });
      // Camino único, atómico: la RPC siembra el evento en pagos_capex + flip del target en UNA
      // transacción (Fase 2b commit 1). El backend ya está vivo y verificado en prod, así que el
      // fallback legacy (flip sin evento) se eliminó — cualquier error propaga (10.177), sin fallback.
      await saldarCapexTargets({ movimientoBancarioId: movimiento.id, pais, items: rpcItems, usuarioId });

      // 2. UPDATE banco: procesado + limpiar match_*. Sin factura (son pagos de proyección, no DTE).
      await client
        .from("movimientos_bancarios")
        .update({
          estado: "procesado",
          procesado_por: usuarioId || null,
          procesado_at: new Date().toISOString(),
          match_entidad_tipo: null,
          match_entidad_id: null,
          match_entidad_nombre: null,
          match_factura_tipo: null,
          match_factura_id: null,
          match_confianza: null,
          match_razon: null,
        })
        .eq("id", movimiento.id);

      // Atribución al proveedor (2026-08-22): el CapEx conoce su proveedor sin ambigüedad
      // (activos.proveedor_id). Si TODOS los ítems resuelven al MISMO proveedor, la caja nace
      // atribuida a él como `pago_a_cuenta` → aparece en su Estado de Cuenta como "Pagos a cuenta"
      // (bucket crédito). Es FIFO-SEGURO: un `pago_a_cuenta` está estructuralmente excluido del pase
      // FIFO del ledger (ledgerProveedor.js Pasada 3, `bucket !== "credito"`), así que NUNCA amortiza
      // una factura por error — verificado con datos reales (X Capital prov 38: pago > deuda total no
      // toca ninguna factura). El vínculo al CapEx sigue viviendo en `plan_pagos_activos.movimiento_bancario_id`.
      // Fallback defensivo (10.177): si algún ítem no tiene proveedor (lote importado) o se mezclan
      // proveedores distintos, se mantiene el comportamiento previo (`otro` + `compra_activo`) para no
      // mis-atribuir en silencio.
      const provIdsItems = [...new Set(items.map((it) => it.proveedorId).filter((id) => id != null))];
      const provUnico = provIdsItems.length === 1 ? provIdsItems[0] : null;
      const atribuido = provUnico != null;
      const provNombreUnico = atribuido
        ? (items.find((it) => it.proveedorId === provUnico)?.proveedorNombre ?? null)
        : null;
      const categoriaCaja = atribuido ? "pago_a_cuenta" : "compra_activo";

      // 3. UNA fila movimientos_caja consolidada (idempotente por movimiento_bancario_id). monto = débito
      //    real (bruto); monto_neto = Σ ítems (neto, convertidos a moneda local); monto_impuesto = Σneto×tasa
      //    si el pago lleva IVA/IGV, 0 si no. Atribuida al proveedor (pago_a_cuenta) cuando es único; si no,
      //    `otro`/`compra_activo`. `activo_id=null` y `cuenta_er=null` en ambos casos (el ER va por la factura,
      //    10.2; el FIFO no la toca: MBF ausente + pago_a_cuenta fuera del FIFO). La moneda de la caja es la
      //    del banco (local en la práctica CLP/PEN); el desglose neto/impuesto queda en esa moneda local.
      const { data: existente, error: errSelect } = await client
        .from("movimientos_caja")
        .select("id")
        .eq("movimiento_bancario_id", movimiento.id)
        .maybeSingle();
      if (errSelect) throw errSelect;

      let movCajaId = existente?.id || null;
      if (!existente) {
        // IVA automático por ítem (bruto − neto), desde exento_iva. Sin toggle manual (CapEx-IVA F2).
        const impuesto = Math.round(capexImpuestoLocal);
        const nCuotas = items.filter((i) => i.tipo === "cuota").length;
        const nComps = items.filter((i) => i.tipo === "comp").length;
        const nActivos = items.filter((i) => i.tipo === "activo").length;
        const nFob = items.filter((i) => i.tipo === "fob").length;
        const partes = [];
        if (nCuotas) partes.push(`${nCuotas} cuota${nCuotas > 1 ? "s" : ""}`);
        if (nComps) partes.push(`${nComps} componente${nComps > 1 ? "s" : ""}`);
        if (nActivos) partes.push(`${nActivos} activo${nActivos > 1 ? "s" : ""}`);
        if (nFob) partes.push(`${nFob} FOB`);
        const movCaja = await crearMovimiento({
          pais,
          tipo: "pago",
          fecha: movimiento.fecha,
          monto: Math.abs(Number(movimiento.monto || 0)),
          monto_neto: Math.round(capexSumaNetoLocal),
          monto_impuesto: impuesto,
          moneda: movimiento.moneda || (pais === "Chile" ? "CLP" : "PEN"),
          estado: "confirmado",
          descripcion: `Compra activo (${partes.join(" + ")})`,
          entidad_tipo: atribuido ? "proveedor" : "otro",
          entidad_id: atribuido ? provUnico : null,
          entidad_nombre: atribuido ? provNombreUnico : null,
          categoria_pago: categoriaCaja,
          cuenta_er: null,
          activo_id: null,
          origen: movimiento.origen || "clay",
          movimiento_bancario_id: movimiento.id,
          registrado_por: usuarioId || null,
        });
        if (movCaja?.id) {
          await client.from("movimientos_bancarios").update({ movimiento_caja_id: movCaja.id }).eq("id", movimiento.id);
          movCajaId = movCaja.id;
        }
      } else {
        await client
          .from("movimientos_caja")
          .update(
            atribuido
              ? {
                  categoria_pago: "pago_a_cuenta",
                  entidad_tipo: "proveedor",
                  entidad_id: provUnico,
                  entidad_nombre: provNombreUnico,
                  activo_id: null,
                }
              : { categoria_pago: "compra_activo", activo_id: null }
          )
          .eq("id", existente.id);
      }

      toast.success(
        `${items.length} ítem${items.length > 1 ? "s" : ""} saldado${items.length > 1 ? "s" : ""} · la proyección se actualizó`
      );
      onSuccess?.({
        id: movimiento.id,
        estado: "procesado",
        movimiento_caja_id: movCajaId,
        movimiento_caja: { categoria_pago: categoriaCaja },
      });
      onRefreshFuturos?.();
      onClose();
    } catch (err) {
      toast.error("Error al saldar las proyecciones: " + (err.message || ""));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmarCobroACuenta = async () => {
    if (!acCobroProveedorId) return toast.error("Seleccioná un proveedor");
    const categoriaResuelta = acCobroBucket === "factura" ? "cobro_factura" : "cobro_credito";
    setLoading(true);
    try {
      const { data: existente, error: errSelect } = await client
        .from("movimientos_caja")
        .select("id")
        .eq("movimiento_bancario_id", movimiento.id)
        .maybeSingle();
      if (errSelect) throw errSelect;

      let movCajaId = existente?.id || null;
      if (!existente) {
        const movCaja = await crearMovimiento({
          pais,
          tipo: "cobro",
          fecha: movimiento.fecha,
          monto,
          monto_neto: monto,
          monto_impuesto: 0,
          moneda,
          estado: "confirmado",
          descripcion: acCobroNota.trim() || movimiento.descripcion_raw || movimiento.descripcion || "",
          entidad_tipo: "proveedor",
          entidad_id: acCobroProveedorId,
          entidad_nombre: acCobroProveedorNombre,
          categoria_pago: categoriaResuelta,
          cuenta_er: null,
          origen: "clay",
          movimiento_bancario_id: movimiento.id,
          registrado_por: usuarioId || null,
        });
        if (!movCaja?.id) throw new Error("No se pudo crear el movimiento en caja");
        movCajaId = movCaja.id;
      }

      await confirmarMovimiento(movimiento.id, {
        movimiento_caja_id: movCajaId,
        procesado_por: usuarioId || null,
      });

      toast.success("Cobro a cuenta registrado");
      onSuccess?.({
        id: movimiento.id,
        estado: "procesado",
        movimiento_caja_id: movCajaId,
        movimiento_caja: { categoria_pago: categoriaResuelta },
        // Patch scoped: el cobro a cuenta entra al ledger del proveedor (débito) → recalcular ese ledger.
        _scopedProveedorId: acCobroProveedorId,
      });
      onClose();
    } catch (err) {
      toast.error("Error al registrar: " + (err.message || ""));
    } finally {
      setLoading(false);
    }
  };

  // Desvincula una fila de movimiento_bancario_facturas. Si era la última, limpia FK directa
  // en movimientos_bancarios, borra el movimiento_caja asociado y devuelve el banco a
  // Fase 4.1 (solo lectura): al apretar "Regularizar con NC", cargar las NC candidatas del proveedor
  // del forzado. Como la NC no referencia folio (confirmado), el matching es manual → se filtran por
  // proveedor_id + tipo_dte=61 + estado_vinculacion='pendiente' y en el render se resaltan las de
  // monto ≈ faltante. NO escribe nada (la reducción de MBF + marca 'resuelto' es F4.2).
  const handleAbrirPickerNC = async () => {
    setNcSeleccionada(null);
    if (!forzadoPendiente?.proveedorId) {
      setNcCandidatas([]);
      setMostrarPickerNC(true);
      return;
    }
    setCargandoNC(true);
    try {
      const { data } = await client
        .from("facturas_sii_recibidas")
        .select("id, folio, fecha_emision, monto_total, razon_social_emisor")
        .eq("pais", pais)
        .eq("tipo_dte", 61)
        .eq("proveedor_id", forzadoPendiente.proveedorId)
        .eq("estado_vinculacion", "pendiente")
        .order("fecha_emision", { ascending: false });
      setNcCandidatas(data || []);
    } catch {
      setNcCandidatas([]);
    } finally {
      setCargandoNC(false);
      setMostrarPickerNC(true);
    }
  };

  // Fase 4.2 — Regularizar el forzado con la NC real (Opción A): reducir el MBF sobre-asignado a
  // Σ=|débito| y dejar que la NC cubra el remanente vía FIFO. NO toca ledgerProveedor (10.1/10.42):
  // el cierre ya sale de las MBF; acá solo se sustituye el ajuste interno por el documento real.
  const handleRegularizarConNC = async () => {
    // Blindaje (criterio Fase 3): nunca confiar solo en el estado de la UI.
    if (!puedeRegularizarForzado || !ncSeleccionada) {
      return toast.error("No autorizado o sin NC seleccionada para regularizar.");
    }
    const faltante = forzadoPendiente.faltante;
    // Requisito de monto (F4.2): la NC debe igualar el faltante dentro de la tolerancia de redondeo.
    // Diferencias mayores → bloquear (el caso NC ≠ faltante lo maneja F4.4).
    if (Math.abs(Number(ncSeleccionada.monto_total || 0) - faltante) > TOLERANCIA_REDONDEO_LOCAL) {
      return toast.error(
        `La NC (${formatAmount(ncSeleccionada.monto_total, moneda)}) no coincide con el faltante ` +
          `(${formatAmount(faltante, moneda)}) dentro de la tolerancia de redondeo.`
      );
    }
    // Factura de MAYOR monto de la selección (sii_recibida): sobre ella se reduce el MBF por el
    // faltante. Guarda: su monto_total debe cubrir el faltante (no dejar el MBF negativo).
    const recibidas = (forzadoPendiente.facturas || []).filter((f) => f.factura_tipo === "sii_recibida");
    const facturaMayor = recibidas.reduce(
      (max, f) => (Number(f.monto_total || 0) > Number(max?.monto_total || 0) ? f : max),
      null
    );
    if (!facturaMayor) return toast.error("El forzado no tiene facturas recibidas para regularizar.");
    const nuevoMbf = Number(facturaMayor.monto_total || 0) - faltante;
    if (nuevoMbf < 0) {
      return toast.error("El faltante supera el monto de la factura mayor — no se puede regularizar automáticamente.");
    }

    setLoading(true);
    try {
      // ── Orden seguro (criterio Fase 3): un fallo a mitad NO deja un estado peor que el original, e
      //    idempotente. Sin transacción multi-tabla (9.23), así que orden cuidadoso + throw temprano.
      // 1) Reducir el MBF con un SET ABSOLUTO (monto = monto_total − faltante), NO una resta relativa
      //    → correr dos veces deja el MISMO valor (idempotente). Es el paso contable clave: elimina el
      //    doble conteo (MBF sobre-asignado + NC real) dejando Σ MBF = |débito|; la NC cubre el
      //    remanente vía FIFO. Si falla acá, nada cambió (estado original).
      const { error: errMbf } = await client
        .from("movimiento_bancario_facturas")
        .update({ monto: nuevoMbf })
        .eq("movimiento_bancario_id", movimiento.id)
        .eq("factura_tipo", "sii_recibida")
        .eq("factura_id", facturaMayor.factura_id);
      if (errMbf) throw errMbf;

      // 2) Marcar el forzado resuelto + linkear la NC. Si falla DESPUÉS de (1), el ledger ya quedó
      //    correcto (MBF reducido + NC) y el ⚠ sigue visible (aún 'pendiente') → re-ejecutar es
      //    seguro porque (1) es idempotente.
      const { error: errFz } = await client
        .from("vinculaciones_forzadas")
        .update({ estado: "resuelto", nc_factura_id: ncSeleccionada.id })
        .eq("id", forzadoPendiente.id);
      if (errFz) throw errFz;

      // 3) Marcar la NC como vinculada (cosmético, ÚLTIMO — no afecta el ledger). Si falla, el forzado
      //    ya está resuelto y el ledger correcto; solo queda la NC en 'pendiente' (impacto menor).
      await client
        .from("facturas_sii_recibidas")
        .update({
          estado_vinculacion: "vinculada",
          vinculado_por: usuarioId || null,
          vinculado_at: new Date().toISOString(),
        })
        .eq("id", ncSeleccionada.id);

      toast.success("Forzado regularizado con la NC");
      // Refresco histórico + futuros: el ⚠ se va (forzado 'resuelto', renderMarcaForzado chequea
      // 'pendiente') y "Por pagar" refleja el nuevo reparto (MBF reducido + NC vía FIFO).
      onSuccess?.({ id: movimiento.id, _forzadoResuelto: true });
      onClose();
    } catch (err) {
      toast.error("Error al regularizar: " + (err.message || ""));
    } finally {
      setLoading(false);
    }
  };

  // Fase 4.3 — Deshacer la regularización (espejo inverso de handleRegularizarConNC, regla 10.121):
  // deja todo como ANTES de regularizar (forzado 'pendiente' de nuevo, MBF sobre-asignados, NC libre).
  // Para cuando el gerente eligió la NC equivocada. NO toca ledgerProveedor.
  const handleDeshacerRegularizacion = async () => {
    // Blindaje (criterio Fase 3): nunca confiar solo en el estado de la UI.
    if (!puedeDeshacerRegularizacion) {
      return toast.error("No autorizado o el forzado no está regularizado.");
    }
    const ncId = forzadoPendiente.ncFacturaId;
    setLoading(true);
    try {
      // ── Orden seguro (espejo de F4.2): un fallo a mitad NO deja un estado peor que el original, e
      //    idempotente. Sin transacción multi-tabla (9.23).
      // 1) Restaurar cada MBF a su monto_total (Σ MBF = Σ facturas = el estado sobre-asignado del
      //    forzado). SET ABSOLUTO por factura → idempotente. Si falla, nada peor (aún 'resuelto').
      for (const f of (forzadoPendiente.facturas || []).filter((x) => x.factura_tipo === "sii_recibida")) {
        const { error } = await client
          .from("movimiento_bancario_facturas")
          .update({ monto: Number(f.monto_total || 0) })
          .eq("movimiento_bancario_id", movimiento.id)
          .eq("factura_tipo", "sii_recibida")
          .eq("factura_id", f.factura_id);
        if (error) throw error;
      }

      // 2) Forzado vuelve a 'pendiente' + nc_factura_id=null. Si falla después de (1), el ledger volvió
      //    al estado sobre-asignado (como el forzado original) y el ⚠ aún no reaparece (sigue
      //    'resuelto') → re-ejecutar es seguro porque (1) es idempotente.
      const { error: errFz } = await client
        .from("vinculaciones_forzadas")
        .update({ estado: "pendiente", nc_factura_id: null })
        .eq("id", forzadoPendiente.id);
      if (errFz) throw errFz;

      // 3) Liberar la NC (cosmético, ÚLTIMO): vuelve a 'pendiente', limpiando vinculado_por/at. No
      //    afecta el ledger. Si falla, el forzado ya está pendiente; solo queda la NC 'vinculada'.
      if (ncId != null) {
        await client
          .from("facturas_sii_recibidas")
          .update({ estado_vinculacion: "pendiente", vinculado_por: null, vinculado_at: null })
          .eq("id", ncId);
      }

      toast.success("Regularización deshecha — el forzado vuelve a pendiente");
      // Refresco histórico + futuros: el ⚠ REAPARECE (forzado 'pendiente') y "Por pagar" refleja el
      // estado sobre-asignado de nuevo (mismo flag que regularizar; el handler recarga ambos).
      onSuccess?.({ id: movimiento.id, _forzadoResuelto: true });
      onClose();
    } catch (err) {
      toast.error("Error al deshacer: " + (err.message || ""));
    } finally {
      setLoading(false);
    }
  };

  // 'pendiente' — mismo patrón de unwind que handleDesvincularFactoring.
  // Desvincula el PAGO COMPLETO (todas las facturas del movimiento de una vez), nunca de a una
  // factura: desvincular una sola de un pago que agrupa varias (bundle) dejaría las otras huérfanas.
  const handleDesvincularPagoCompleto = async () => {
    // Guard CapEx (Fase 2.5): si este movimiento es parte de una asociación CapEx ACTIVA, el unwind de
    // acá es CIEGO a ella — detecta el compra_activo por movimientos_caja.activo_id (que en la asociación
    // consolidada es NULL, 10.66) y haría un unwind PARCIAL: borra MBF/caja pero deja los activos y la
    // fila de auditoría colgados (el estado híbrido ya visto). Se BLOQUEA y se redirige al "Desasociar"
    // del panel CapEx, que revierte todo atómicamente. Check autoritativo por la tabla de auditoría,
    // comparando por ID (10.7); respaldo por activos.factura_sii_recibida_id si la audit se desincronizara.
    // NUNCA silencioso (10.177): siempre avisa con toast antes de cortar.
    let asocCapex = null;
    try {
      const { data } = await client
        .from("asociaciones_capex_factura")
        .select("factura_sii_recibida_id")
        .eq("estado", "activa")
        .contains("bancario_ids", [movimiento.id])
        .limit(1);
      asocCapex = (data && data[0]) || null;
    } catch {
      asocCapex = null;
    }
    // Respaldo (defensa en profundidad): las cuotas pagadas de este movimiento apuntan a un activo que ya
    // quedó ligado a una factura → es un CapEx asociado aunque la audit no lo refleje.
    if (!asocCapex) {
      try {
        const { data: cuotas } = await client
          .from("plan_pagos_activos")
          .select("activo_id")
          .eq("movimiento_bancario_id", movimiento.id)
          .eq("estado", "pagada");
        const activoIds = [...new Set((cuotas || []).map((c) => c.activo_id))];
        if (activoIds.length > 0) {
          const { data: ligados } = await client
            .from("activos")
            .select("factura_sii_recibida_id")
            .in("id", activoIds)
            .not("factura_sii_recibida_id", "is", null)
            .limit(1);
          if (ligados && ligados[0]) asocCapex = { factura_sii_recibida_id: ligados[0].factura_sii_recibida_id };
        }
      } catch {
        // best-effort; el check primario es la audit.
      }
    }
    if (asocCapex) {
      toast.error(
        `Este pago es parte de una asociación CapEx (factura ${asocCapex.factura_sii_recibida_id}). ` +
          "Para deshacerlo, usá 'Desasociar' en el panel 'Asociar pagos de CapEx' de esa factura."
      );
      return;
    }

    // Detección por DATO (no por el flag de modo del modal, que puede no estar seteado al reabrir):
    // si el movimiento tiene un giro en transferencias_internacionales, es un cobro de exportación.
    // El giro es ATÓMICO: desvincular CUALQUIER factura deshace el giro COMPLETO (todas las facturas
    // + costos_intermediacion + la transferencia + caja). No se puede sostener un giro con prorrateo
    // de costos sobre un subconjunto cambiante de facturas. Ver ARQUITECTURA 7.18.
    let girosExport = [];
    try {
      const { data } = await client
        .from("transferencias_internacionales")
        .select("id")
        .eq("movimiento_bancario_id", movimiento.id);
      girosExport = data || [];
    } catch {
      girosExport = [];
    }
    const esGiroExport = girosExport.length > 0;

    const mensajeConfirm = esGiroExport
      ? `Este cobro es un giro de exportación que cubre ${vinculacionesSII.length} factura(s). ` +
        "Desvincular deshará el GIRO COMPLETO (todas las facturas, sus costos y la transferencia) y el " +
        "movimiento volverá a estar disponible para revincular. ¿Continuar?"
      : "¿Desvincular el pago completo? El movimiento volverá a pendiente y se liberarán TODAS las facturas vinculadas.";
    if (!window.confirm(mensajeConfirm)) return;

    setLoading(true);
    try {
      // ── Rama EXPORTACIÓN: unwind atómico del giro (hijos→padres). Sin transacción multi-tabla
      // disponible (ver 9.23), así que orden cuidadoso y throw ante el primer error para no dejar
      // un estado intermedio silencioso. Resultado: MBF, caja, costos_intermediacion y
      // transferencias_internacionales quedan SIN filas para este movimiento (foja cero).
      if (esGiroExport) {
        const giroIds = girosExport.map((g) => g.id);

        // 1. costos_intermediacion (hijos del giro)
        const { error: errCostos } = await client.from("costos_intermediacion").delete().in("giro_id", giroIds);
        if (errCostos) throw errCostos;

        // 2. transferencias_internacionales (el giro)
        const { error: errGiro } = await client
          .from("transferencias_internacionales")
          .delete()
          .eq("movimiento_bancario_id", movimiento.id);
        if (errGiro) throw errGiro;

        // 3. TODAS las MBF del movimiento (el giro cubre todas, no solo la factura desvinculada)
        const { error: errMbf } = await client
          .from("movimiento_bancario_facturas")
          .delete()
          .eq("movimiento_bancario_id", movimiento.id);
        if (errMbf) throw errMbf;

        // 4. Caja del movimiento + 5. movimiento → pendiente
        const { data: movActual } = await client
          .from("movimientos_bancarios")
          .select("movimiento_caja_id")
          .eq("id", movimiento.id)
          .single();
        await client.from("movimientos_bancarios").update(RESET_BANCARIO).eq("id", movimiento.id);
        // Cuotas de plan de pagos/cobros atadas al movimiento → 'pendiente' (reverso simétrico).
        await revertirCuotasFacturaPorMovimiento({ movimientoBancarioId: movimiento.id });
        if (movActual?.movimiento_caja_id) {
          await client.from("movimientos_caja").delete().eq("id", movActual.movimiento_caja_id);
        }
        // Defensa: borrar cualquier caja colgada del movimiento aunque movimiento_caja_id no
        // estuviera seteado (el confirm multi crea la caja con movimiento_bancario_id).
        await client.from("movimientos_caja").delete().eq("movimiento_bancario_id", movimiento.id);

        // Cierre de la asimetría con `aplicaciones_saldo` (igual que la rama no-export): borrar los
        // eventos de este origen y limpiar facturafechacobro de las emitidas del giro. Idempotente.
        const emitidasIdsGiro = vinculacionesSII
          .filter((v) => v.factura_tipo === "sii_emitida")
          .map((v) => v.factura_id);
        const { ok: okSaldoGiro, error: errSaldoGiro } = await escribirEventosSaldo(
          client,
          { origen_tipo: "movimiento_bancario", origen_id: movimiento.id },
          []
        );
        if (!okSaldoGiro) {
          // eslint-disable-next-line no-console
          console.error("[desvincular giro · aplicaciones_saldo] no se pudieron borrar los eventos:", errSaldoGiro);
        }
        if (emitidasIdsGiro.length > 0) {
          const { error: errEmiGiro } = await client
            .from("facturas_sii_emitidas")
            .update({ facturafechacobro: null })
            .in("id", emitidasIdsGiro);
          if (errEmiGiro) throw errEmiGiro;
        }

        toast.success("Giro de exportación deshecho");
        onSuccess?.({
          id: movimiento.id,
          estado: "pendiente",
          movimiento_caja_id: null,
          procesado_por: null,
          procesado_at: null,
          movimiento_caja: null,
          factura_emitida: null,
          factura_recibida: null,
          factura_externa: [],
          facturas_vinculadas: [],
          match_factura_tipo: null,
          match_factura_id: null,
          match_entidad_tipo: null,
          match_entidad_id: null,
          match_entidad_nombre: null,
          _desvinculado: true,
        });
        onClose();
        return;
      }

      // ── Fase 1.1c/1.2a/1.2b: SII PURO, EXTERNAS/BOLETAS PURO o cualquier MEZCLA entre sii_recibida/
      //    factura_externa/boleta_honorario (pago) → RPC transaccional (unwind atómico + limpieza de
      //    libro/facturafechacobro/estado_vinculacion/facturas_externas/vinculaciones_forzadas, todo o
      //    nada — 10.177). Boletas NO tienen tabla de documento que limpiar (sin estado_vinculacion).
      //    compra_activo o mezcla con OTRO tipo (p.ej. sii_emitida) → camino viejo intacto (abajo). Los
      //    guards CapEx/giro de arriba ya corrieron; la RPC los repite por dentro (redundante, OK).
      const tiposMBF = new Set(vinculacionesSII.map((v) => v.factura_tipo));
      const TIPOS_PAGO_RPC = new Set(["sii_recibida", "factura_externa", "boleta_honorario"]);
      const esSoloEmitidas = tiposMBF.size === 1 && tiposMBF.has("sii_emitida");
      const esPagoElegibleRpc = tiposMBF.size > 0 && [...tiposMBF].every((t) => TIPOS_PAGO_RPC.has(t));
      let esCompraActivoSII = false;
      if (esPagoElegibleRpc) {
        const { data: ca } = await client
          .from("movimientos_caja")
          .select("activo_id")
          .eq("movimiento_bancario_id", movimiento.id)
          .not("activo_id", "is", null)
          .maybeSingle();
        esCompraActivoSII = !!ca?.activo_id;
      }
      if (esSoloEmitidas || (esPagoElegibleRpc && !esCompraActivoSII)) {
        const rpcName = esSoloEmitidas ? "desvincular_cobro_bancario" : "desvincular_pago_bancario";
        const { error: errRpc } = await client.rpc(rpcName, {
          p_movimiento_id: movimiento.id,
          p_usuario_id: usuarioId || null,
        });
        if (errRpc) throw errRpc; // 10.177: la RPC es atómica; si lanza, no se tocó nada.
        toast.success("Pago desvinculado");
        onSuccess?.({
          id: movimiento.id,
          estado: "pendiente",
          movimiento_caja_id: null,
          procesado_por: null,
          procesado_at: null,
          movimiento_caja: null,
          factura_emitida: null,
          factura_recibida: null,
          factura_externa: [],
          facturas_vinculadas: [],
          match_factura_tipo: null,
          match_factura_id: null,
          match_entidad_tipo: null,
          match_entidad_id: null,
          match_entidad_nombre: null,
          _desvinculado: true,
        });
        onClose();
        return;
      }

      // ── Rama NO exportación (SII normal / externas): unwind del PAGO COMPLETO ──
      // Se borran TODAS las filas MBF del movimiento de una vez, NUNCA una sola: desvincular de a
      // una rompería un pago que agrupa varias facturas (bundle) → dejaría las otras huérfanas
      // (pagadas apuntando a un banco ya reseteado / caja borrada). Siempre: reset banco + borra
      // caja + libera todas las externas del movimiento.
      const externasDesvinculadasFilas = vinculacionesSII.filter((v) => v.factura_tipo === "factura_externa");
      const idsExternasDesvinculadas = externasDesvinculadasFilas.map((v) => v.factura_id);
      // Montos por factura: para inyectarlos al saldosPorFacturaMap optimista de FlujoCajaV2 (sino
      // el filtro #4 del useMemo descarta la factura aunque salga del Set yaVinculadasExternas).
      const externasDesvinculadasMontos = externasDesvinculadasFilas.map((v) => ({
        id: v.factura_id,
        monto: v.monto,
      }));

      const { data: movActual } = await client
        .from("movimientos_bancarios")
        .select("movimiento_caja_id")
        .eq("id", movimiento.id)
        .single();

      // Compra de activo (D.2 / Fase 3C): detectar ANTES de borrar la caja si este cierre era un
      // compra_activo (asiento de caja con activo_id). Si lo es, además del unwind del pago hay que
      // revertir el activo a 'ejecutado_sin_factura' y devolver el/los DTE a 'pendiente' (abajo).
      // NO aplica a pagos normales (activo_id null) → su comportamiento queda intacto.
      const { data: cajaActivo } = await client
        .from("movimientos_caja")
        .select("activo_id")
        .eq("movimiento_bancario_id", movimiento.id)
        .not("activo_id", "is", null)
        .maybeSingle();
      const recibidasIdsMBF = vinculacionesSII
        .filter((v) => v.factura_tipo === "sii_recibida")
        .map((v) => v.factura_id);
      // Emitidas cobradas por este movimiento (para limpiar su facturafechacobro stale al desvincular).
      const emitidasIdsMBF = vinculacionesSII.filter((v) => v.factura_tipo === "sii_emitida").map((v) => v.factura_id);

      // 1. Borrar TODAS las filas MBF del movimiento.
      const { error: errDel } = await client
        .from("movimiento_bancario_facturas")
        .delete()
        .eq("movimiento_bancario_id", movimiento.id);
      if (errDel) throw errDel;

      // 2. Resetear el banco a pendiente (FKs + match_* limpios; sin esto el badge X re-aparece al
      // recargar porque renderBadgeMovimiento prende por match_factura_tipo). RESET_BANCARIO incluye
      // plan_pago_cuota_id / plan_cobro_cuota_id (lado movimiento del vínculo de cuota).
      await client.from("movimientos_bancarios").update(RESET_BANCARIO).eq("id", movimiento.id);

      // 2b. Revertir cuotas de plan de pagos/cobros atadas a este movimiento (lado cuota del vínculo).
      await revertirCuotasFacturaPorMovimiento({ movimientoBancarioId: movimiento.id });

      // 3. Borrar la caja del movimiento.
      if (movActual?.movimiento_caja_id) {
        await client.from("movimientos_caja").delete().eq("id", movActual.movimiento_caja_id);
      }

      // 4. Liberar TODAS las facturas externas del movimiento (reaparecen en "Por pagar").
      if (idsExternasDesvinculadas.length > 0) {
        await client
          .from("facturas_externas")
          .update({ movimiento_bancario_id: null, estado: "pendiente" })
          .in("id", idsExternasDesvinculadas);
      }

      // 4-bis. Cerrar la asimetría con el libro `aplicaciones_saldo` (fuente única de "cobrada"/"pagada"
      //   vía resolverSaldo, 10.190). El vincular dual-escribe eventos via='banco' por este movimiento
      //   (cobros emitidas + pagos recibidas + externas + boletas), pero el desvincular NO los borraba →
      //   quedaban ZOMBIES: resolverSaldo seguía viendo la factura saldada y no reaparecía en Futuros
      //   (caso folio 570 / evento 860). Se borran TODOS los eventos de este origen con el MISMO primitivo
      //   que usa el anular de factoring (escribirEventosSaldo con []). Best-effort/no silencioso (10.177):
      //   el unwind principal ya está commiteado; si falla, se loguea visible (no se traga).
      const { ok: okSaldo, error: errSaldo } = await escribirEventosSaldo(
        client,
        { origen_tipo: "movimiento_bancario", origen_id: movimiento.id },
        []
      );
      if (!okSaldo) {
        // eslint-disable-next-line no-console
        console.error("[desvincular · aplicaciones_saldo] no se pudieron borrar los eventos del movimiento:", errSaldo);
      }

      // 4-ter. Limpiar el rastro stale en las emitidas cobradas por este movimiento: facturafechacobro
      //   vuelve a null (el vincular lo setea a la fecha del mov). estado_vinculacion NO se toca: refiere
      //   al vínculo con el CONCEPTO de venta (negocios_carteleria/sales), no al cobro — una emitida
      //   vinculada a su concepto y sin cobro es 'vinculada' (p.ej. folio 554) y es como entra a "Por
      //   cobrar"; cambiarlo la sacaría del panel.
      if (emitidasIdsMBF.length > 0) {
        const { error: errEmi } = await client
          .from("facturas_sii_emitidas")
          .update({ facturafechacobro: null })
          .in("id", emitidasIdsMBF);
        if (errEmi) throw errEmi;
      }

      // 5. Compra de activo (Fase 3C): reverso específico. El activo vuelve a 'ejecutado_sin_factura'
      //    (deja de depreciar; fecha_compra→null; se despega el DTE) y el/los DTE recibidos vuelven a
      //    'pendiente' (reaparecen en "Por pagar"), limpiando plazopago/vinculado_*. Solo compra_activo.
      if (cajaActivo?.activo_id) {
        await actualizarActivo(cajaActivo.activo_id, {
          estado: "ejecutado_sin_factura",
          fecha_compra: null,
          factura_sii_recibida_id: null,
        });
        if (recibidasIdsMBF.length > 0) {
          await client
            .from("facturas_sii_recibidas")
            .update({
              estado_vinculacion: "pendiente",
              plazopago: null,
              vinculado_por: null,
              vinculado_at: null,
            })
            .in("id", recibidasIdsMBF);
        }
      }

      // Forzado por faltante (10.108 invertida): si este movimiento tenía una vinculación forzosa,
      // deshacerla por completo → borrar su fila de auditoría DESPUÉS del unwind estándar (MBF/caja/
      // banco ya reseteados arriba). Con el refresco de forzadosMap (onSuccess _desvinculado) el ⚠
      // desaparece. Si no había forzado, borra 0 filas (no-op).
      await client.from("vinculaciones_forzadas").delete().eq("movimiento_bancario_id", movimiento.id);

      toast.success(
        cajaActivo?.activo_id ? "Vinculación revertida · activo vuelve a pendiente de factura" : "Pago desvinculado"
      );
      onSuccess?.({
        id: movimiento.id,
        estado: "pendiente",
        movimiento_caja_id: null,
        procesado_por: null,
        procesado_at: null,
        movimiento_caja: null,
        factura_emitida: null,
        factura_recibida: null,
        factura_externa: [],
        facturas_vinculadas: [],
        match_factura_tipo: null,
        match_factura_id: null,
        match_entidad_tipo: null,
        match_entidad_id: null,
        match_entidad_nombre: null,
        _externasDesvinculadas: idsExternasDesvinculadas,
        _externasDesvinculadasMontos: externasDesvinculadasMontos,
        _desvinculado: true,
      });
      // Compra de activo: el DTE vuelve a "Por pagar" y el activo cambió de estado → refrescar futuros
      // (el patch in-memory de onSuccess no recarga la proyección por sí solo).
      if (cajaActivo?.activo_id) onRefreshFuturos?.();
      onClose();
    } catch (err) {
      toast.error("Error al desvincular: " + (err.message || ""));
    } finally {
      setLoading(false);
    }
  };

  // Desvincular vinculación que vive solo en movimientos_caja (badges G/A/P/D):
  // gasto sin factura, pago/cobro a cuenta, pago institucional, cambio de moneda.
  // No hay MBF ni FK directa a facturas externas/SII — solo caja + estado del banco.
  const handleDesvincularCaja = async () => {
    if (!window.confirm("¿Deshacer esta vinculación?")) return;
    setLoading(true);
    try {
      // CapEx caja-pura MULTI-target: revertir TODAS las cuotas de activo y componentes de lote del
      // movimiento + MARCAR reversado_at sus eventos en el subledger pagos_capex, ATÓMICO (Fase 2a, RPC
      // reversar_capex_targets — 10.177/10.239). Sintéticos (notas=centinela) → DELETE; reales →
      // 'pendiente' + limpia fecha_pago_real/movimiento_bancario_id → reaparecen en la proyección de
      // CapEx. La reversa del evento es INVISIBLE (nadie lee el ledger aún, Fase 2b); solo lo mantiene
      // consistente para el cutover. No toca activos/lotes/unidades/depreciación (fue caja pura). El
      // resto del unwind (caja, banco, cuotas crédito/factura) queda en JS abajo, mismo patrón que el
      // settlement.
      // Camino único, atómico: la RPC marca reversado_at los eventos + revierte los targets en UNA
      // transacción (Fase 2b commit 1). El backend ya está vivo y verificado en prod → se eliminó el
      // fallback legacy (revertía sin tocar el ledger); cualquier error propaga (10.177), sin fallback.
      const capexRev = await reversarCapexTargets({ movimientoBancarioId: movimiento.id, usuarioId });
      const huboCapexPuro = (capexRev?.targets_tocados || 0) > 0;

      // Ejecución ENTERA de activo caja-only (D.2, Fase 3C): si NO hubo cuotas ni componentes, esto fue la
      // ejecución de un activo entero cerrada caja-only (sin MBF) → revertir el activo a
      // 'ejecutado_sin_factura' (fecha_compra→null, deja de depreciar). Ruta defensiva; el cierre 3B (con
      // MBF) se revierte por handleDesvincularPagoCompleto. No toca ER (regla 10.2). Solo cuando no hubo
      // caja pura (si la hubo, el activo/lote no se tocó y no hay nada que revertir acá).
      if (!huboCapexPuro) {
        const { data: cajasActivo } = await client
          .from("movimientos_caja")
          .select("activo_id")
          .eq("movimiento_bancario_id", movimiento.id)
          .not("activo_id", "is", null);
        for (const c of cajasActivo || []) {
          // eslint-disable-next-line no-await-in-loop
          await actualizarActivo(c.activo_id, {
            estado: "ejecutado_sin_factura",
            fecha_compra: null,
            factura_sii_recibida_id: null,
          });
        }
      }

      // RESET_BANCARIO centraliza el shape (10.1): además de caja/match_*, deja en null las FKs de
      // factura y de cuota (plan_pago_cuota_id / plan_cobro_cuota_id). En un pago caja-pura ya eran
      // null (no-op); si este path corriera sobre un pago de cuota SII, el reset queda igual de completo.
      await client.from("movimientos_bancarios").update(RESET_BANCARIO).eq("id", movimiento.id);

      // Borrar la caja consolidada del movimiento POR movimiento_bancario_id (molde giro-exportación). La
      // UNIQUE viva garantiza ≤1 fila, así que borra exactamente la caja del movimiento sin depender de que
      // movimiento_caja_id estuviera seteado. Cubre single y multi (una sola caja consolidada en ambos).
      await client.from("movimientos_caja").delete().eq("movimiento_bancario_id", movimiento.id);

      // Si el movimiento estaba vinculado a una cuota de crédito (path "Préstamo"), revertirla a
      // pendiente para que reaparezca en el panel futuros y en el modal. El filtro por
      // movimiento_bancario_id solo matchea cuotas de crédito → inofensivo para los demás pagos.
      const { data: cuotasRevertidas } = await client
        .from("creditos_cuotas")
        .update({ estado: "pendiente", movimiento_bancario_id: null })
        .eq("movimiento_bancario_id", movimiento.id)
        .select("id");

      // Cuotas de plan de pagos/cobros de factura SII atadas a este movimiento → 'pendiente'
      // (reverso simétrico centralizado; huérfano histórico que ningún path deshacía).
      const { pagos: cuotasFacturaRev, cobros: cuotasCobroRev } = await revertirCuotasFacturaPorMovimiento({
        movimientoBancarioId: movimiento.id,
      });

      toast.success("Vinculación deshecha");
      onSuccess?.({
        id: movimiento.id,
        estado: "pendiente",
        movimiento_caja_id: null,
        movimiento_caja: null,
        match_factura_tipo: null,
        match_factura_id: null,
        match_entidad_tipo: null,
        match_entidad_id: null,
        match_entidad_nombre: null,
        factura_emitida: null,
        factura_recibida: null,
        factura_externa: [],
        facturas_vinculadas: [],
        procesado_por: null,
        procesado_at: null,
        // Fase 2b: limpiar el detalle del badge "A" (CapEx a cuenta) de ESTE movimiento sin recargar
        // el histórico (patch puntual, 10.234). No-op si el mov no era CapEx. Mata el bug de "badge no
        // se limpia sin hard refresh".
        _capexDesvinculado: true,
      });
      // Cuota (crédito o activo), componente de lote, o cuota de factura SII revertida → recargar
      // futuros para que reaparezca en "Por pagar"/"Por cobrar".
      if (cuotasRevertidas?.length > 0 || huboCapexPuro || cuotasFacturaRev > 0 || cuotasCobroRev > 0)
        onRefreshFuturos?.();
      onClose();
    } catch (err) {
      toast.error("Error al desvincular: " + (err.message || ""));
    } finally {
      setLoading(false);
    }
  };

  // Desvincular pago a empleado: borra TODAS las filas de pagos_empleados de este
  // movimiento (son componentes del mismo pago). No hay movimientos_caja que limpiar
  // ni estado del banco que tocar — el badge E del padre depende solo de pagos_empleados.
  const handleDesvincularEmpleado = async () => {
    if (!window.confirm("¿Desvincular este pago a empleado del movimiento?")) return;
    setLoading(true);
    try {
      await eliminarPagosPorMovimiento(movimiento.id);
      toast.success("Pago a empleado desvinculado");
      onSuccess?.({
        id: movimiento.id,
        _empleadoDesvinculado: true,
        _movimientoBancarioId: movimiento.id,
      });
      onClose();
    } catch (err) {
      toast.error("Error al desvincular: " + (err.message || ""));
    } finally {
      setLoading(false);
    }
  };

  const handleDesvincularFactoring = async () => {
    if (!yaVinculadoFactoring) return;
    if (!window.confirm("¿Desvincular este movimiento del factoring?")) return;
    setLoading(true);
    try {
      const esExcedente = factoringTipoVinc === "excedente";
      const campo = esExcedente ? "movimiento_bancario_remanente_id" : "movimiento_bancario_adelanto_id";
      await actualizarOperacionFactoring(yaVinculadoFactoring.id, { [campo]: null });

      // Fase 2: reversa completa de la caja del remanente creada al vincular. El giro no
      // genera caja, así que solo aplica al excedente.
      if (esExcedente) {
        const { error: errCaja } = await client
          .from("movimientos_caja")
          .delete()
          .eq("movimiento_bancario_id", movimiento.id)
          .eq("categoria_pago", "cobro_factura");
        if (errCaja) throw errCaja;
      }

      await client
        .from("movimientos_bancarios")
        .update({ estado: "pendiente", movimiento_caja_id: null, procesado_por: null, procesado_at: null })
        .eq("id", movimiento.id);
      toast.success("Movimiento desvinculado del factoring");
      onSuccess?.({
        id: movimiento.id,
        estado: "pendiente",
        procesado_por: null,
        procesado_at: null,
        _desvinculaFactoring: true,
        // Patch scoped inverso (Fase 2): nulear el FK de la op → su entry giro/excedente reaparece.
        _factoringOpId: yaVinculadoFactoring.id,
        _factoringTipoVinc: factoringTipoVinc,
      });
      onClose();
    } catch (err) {
      toast.error("Error al desvincular: " + (err.message || ""));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmarFactoring = async () => {
    if (factoringOpIds.length === 0) return;
    setLoading(true);
    try {
      const esExcedente = factoringTipoVinc === "excedente";
      const campo = esExcedente ? "movimiento_bancario_remanente_id" : "movimiento_bancario_adelanto_id";
      await Promise.all(factoringOpIds.map((id) => actualizarOperacionFactoring(id, { [campo]: movimiento.id })));

      // Fase 2: al vincular el depósito del REMANENTE, su movimiento de caja nace acá con
      // respaldo bancario desde el arranque (cumple regla 10.29 sin deuda; la liquidación
      // ya no crea caja). Solo el caso 1 op ↔ 1 movimiento (el dominante). El multi-op
      // (1 mov → N ops) queda pendiente de decisión de reparto — hoy no existe en datos
      // reales. El giro (anticipo) no genera caja.
      let movCajaId = null;
      if (esExcedente && factoringOpIds.length === 1) {
        const opExc = operacionesFactoring.find((op) => op.id === factoringOpIds[0]);
        // Idempotencia por movimiento_bancario_id (mismo patrón que gasto sin factura).
        const { data: existente, error: errSelect } = await client
          .from("movimientos_caja")
          .select("id")
          .eq("movimiento_bancario_id", movimiento.id)
          .maybeSingle();
        if (errSelect) throw errSelect;
        if (existente) {
          movCajaId = existente.id;
        } else {
          const movCaja = await crearMovimiento({
            pais,
            tipo: "cobro",
            fecha: movimiento.fecha,
            monto: Number(movimiento.monto),
            moneda: movimiento.moneda || moneda,
            estado: "confirmado",
            descripcion: `Remanente factoring — Folio ${opExc?.factura?.folio || opExc?.factura_sii_emitida_id || ""}`,
            entidad_tipo: "cliente",
            entidad_id: opExc?.factura?.cliente_id || null,
            categoria_pago: "cobro_factura",
            origen: "manual",
            movimiento_bancario_id: movimiento.id,
            registrado_por: usuarioId || null,
          });
          if (!movCaja?.id) throw new Error("No se pudo crear el movimiento en caja del remanente");
          movCajaId = movCaja.id;
        }
      }

      const confirmArgs = { procesado_por: usuarioId };
      if (movCajaId) confirmArgs.movimiento_caja_id = movCajaId;
      await confirmarMovimiento(movimiento.id, confirmArgs);
      toast.success(
        factoringTipoVinc === "giro"
          ? factoringOpIds.length > 1
            ? `${factoringOpIds.length} giros vinculados`
            : "Giro de factoring vinculado"
          : factoringOpIds.length > 1
            ? `${factoringOpIds.length} excedentes vinculados`
            : "Excedente de factoring vinculado"
      );
      // Op completa (con embed proveedor/factura/giro_a_recibir/monto_remanente) para que
      // mapaFactoring del padre arme el tooltip del badge F. Si hay N ops, pasamos la primera —
      // el caso multi-op (mismo mov compartido por varias ops) se refresca al próximo cargarDatos.
      const opSeleccionada = operacionesFactoring.find((op) => op.id === factoringOpIds[0]);
      const tipoVincLabel = factoringTipoVinc === "giro" ? "Giro" : "Excedente";
      onSuccess?.({
        id: movimiento.id,
        estado: "procesado",
        movimiento_caja_id: movCajaId,
        procesado_por: usuarioId,
        procesado_at: new Date().toISOString(),
        _factoringOp: opSeleccionada,
        _factoringTipo: tipoVincLabel,
        // Patch scoped del panel FUTUROS (Fase 2): setear el FK del movimiento en TODAS las ops
        // vinculadas → sus entries giro/excedente desaparecen sin recargar. tipoVinc en minúscula
        // ('giro'|'excedente') para elegir el campo (adelanto/remanente).
        _factoringOpIds: factoringOpIds,
        _factoringTipoVinc: factoringTipoVinc,
      });
      onClose();
    } catch (err) {
      toast.error("Error al vincular: " + (err.message || ""));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmarPrestamo = async () => {
    if (!creditoPrestamo || !cuotaPrestamo) return toast.error("Seleccioná un crédito y una cuota");
    setLoading(true);
    try {
      const { cajaId } = await vincularMovimientoACuota({
        movimiento,
        credito: creditoPrestamo,
        cuota: cuotaPrestamo,
        pais,
        userId: usuarioId,
      });
      toast.success(`Cuota ${cuotaPrestamo.numero_cuota} vinculada — ${creditoPrestamo.nombre}`);
      // onSuccess actualiza el movimiento histórico (badge P). La cuota proyectada en el panel
      // futuros se alimenta de getCuotasPendientesProyeccion → hay que recargar futuros para que
      // la cuota recién pagada desaparezca del tab "Créditos"/"Todo".
      onSuccess?.({
        id: movimiento.id,
        estado: "procesado",
        movimiento_caja_id: cajaId,
        movimiento_caja: { categoria_pago: "pago_banco" },
      });
      onRefreshFuturos?.();
      onClose();
    } catch (err) {
      toast.error("Error al vincular: " + (err.message || ""));
    } finally {
      setLoading(false);
    }
  };

  // F2b — Vincular un pago DIRECTO a una boleta de honorarios (espejo de handleConfirmarMultiple para
  // un solo documento). Inserta MBF factura_tipo='boleta_honorario' + caja pago_proveedor. La boleta
  // NO tiene columna estado_vinculacion: su estado de pago se deriva del ledger/MBF (Compras,
  // EstadoCuenta, "Por pagar"). El anti-doble-conteo lo garantiza el ledger (el pago con MBF entra a
  // pagosConsumidosPorMBF → excluido del FIFO; la boleta sale de la cola). Reversible por
  // handleDesvincularPagoCompleto (el DELETE de MBF por movimiento la deja impaga de nuevo).
  // Vincular un pago a 1..N boletas de honorarios del MISMO proveedor (espejo de handleConfirmarMultiple).
  // Escribe N filas en movimiento_bancario_facturas (una por boleta, factura_tipo='boleta_honorario',
  // monto = su montoAsignado) + UNA sola fila en movimientos_caja por el débito consolidado. Las boletas
  // NO tienen columna estado_vinculacion: su estado de pago se deriva del ledger/MBF. El anti-doble-conteo
  // lo garantiza el ledger (el pago con MBF entra a pagosConsumidosPorMBF → excluido del FIFO).
  const handleConfirmarHonorario = async () => {
    if (boletasSeleccionadas.length === 0) return toast.error("Seleccioná al menos una boleta");
    // Gate de cuadre (mismo motor que el SII, cuadreBoletas): Σ montoAsignado ≈ |débito|. Defensa en
    // profundidad — el botón ya está deshabilitado si no cuadra, pero nunca confiar solo en el botón.
    if (!cuadreBoletas.cuadra) {
      const verbo = cuadreBoletas.sinAsignar > 0 ? "falta asignar" : "sobra";
      return toast.error(
        `No cuadra: ${verbo} ${formatAmount(Math.abs(cuadreBoletas.sinAsignar), cuadreBoletas.moneda)} ` +
          `(asignado ${formatAmount(cuadreBoletas.asignado, cuadreBoletas.moneda)} de ` +
          `${formatAmount(cuadreBoletas.montoMovCmp, cuadreBoletas.moneda)}).`
      );
    }
    // Proveedor consolidado: todas las boletas comparten proveedor_id (gate de selección). El único caso
    // de boleta huérfana (_sinProveedor) es de UNA sola boleta (el gate impide agruparlas), y se resuelve
    // por el banner inline (entidadFallbackId). SIN proveedor resuelto NO se vincula: el asiento de caja
    // quedaría con entidad_id null y el pago fuera del ledger por-proveedor (10.177).
    const primera = boletasSeleccionadas[0];
    const hayHuerfana = boletasSeleccionadas.some((b) => b._sinProveedor);
    let proveedorIdResuelto = primera.proveedor_id;
    if (hayHuerfana) {
      if (!entidadFallbackId) {
        return toast.error("Esta boleta no tiene proveedor asociado. Asigná uno antes de vincular.");
      }
      proveedorIdResuelto = entidadFallbackId;
    }
    setLoading(true);
    try {
      // 0. Backfill de proveedor: si la boleta era huérfana (caso single), ANTES de escribir ningún
      //    vínculo se persiste proveedor_id en TODAS las boletas del mismo RUT (normalizado, 10.7) y país
      //    que sigan en proveedor_id IS NULL — nunca se pisa una que ya tenga otro proveedor (doble guard
      //    `.is`). Si este paso falla, se corta acá y NO se vincula (lado seguro, 10.177). Dato de
      //    entidad PERMANENTE por diseño: no se revierte al desvincular, queda fuera de la RPC.
      let boletasAsociadas = 0;
      let provNotif = null;
      if (hayHuerfana) {
        const rutObjetivo = normalizarRut(primera.emisor_rut);
        const { data: huerfanas, error: errHuerfanas } = await client
          .from("boletas_honorarios")
          .select("id, emisor_rut")
          .eq("pais", pais)
          .is("proveedor_id", null);
        if (errHuerfanas) throw new Error("No se pudieron leer las boletas sin proveedor: " + errHuerfanas.message);
        const idsMismoRut = (huerfanas || [])
          .filter((h) => normalizarRut(h.emisor_rut) === rutObjetivo)
          .map((h) => h.id);
        if (!idsMismoRut.includes(primera.id)) idsMismoRut.push(primera.id);
        const { data: actualizadas, error: errUpd } = await client
          .from("boletas_honorarios")
          .update({ proveedor_id: proveedorIdResuelto })
          .in("id", idsMismoRut)
          .is("proveedor_id", null)
          .select("id");
        if (errUpd) throw new Error("No se pudo asociar el proveedor a la(s) boleta(s): " + errUpd.message);
        boletasAsociadas = actualizadas?.length || 0;
        // Estado local: las asociadas dejan de ser huérfanas (para las otras del mismo RUT visibles).
        const setIds = new Set((actualizadas || []).map((r) => r.id));
        if (setIds.size > 0) {
          setBoletasHonorarios((prev) =>
            prev.map((b) => (setIds.has(b.id) ? { ...b, proveedor_id: proveedorIdResuelto, _sinProveedor: false } : b))
          );
        }
        // Datos para la notificación (nombre/RUT del proveedor resuelto + verbo creado/asignado).
        const prov = entidadFallbackOptions.find((o) => o.id === proveedorIdResuelto);
        provNotif = {
          nombre: prov?.nombre || primera.emisor_nombre,
          rut: prov?.rut_ruc || primera.emisor_rut,
          verbo: entidadFallbackCreada ? "creado" : "asignado",
        };
      }

      // Fase 1.2b: MBF + cuotas + caja + libro por la RPC transaccional `vincular_pago_bancario` —
      // todo o nada (10.177). Ya no escribe tablas directo.
      const { movCajaId } = await ejecutarVinculacionBoletas({
        movimiento,
        boletasSeleccionadas,
        proveedorId: proveedorIdResuelto || null,
        proveedorNombre: primera.emisor_nombre,
        pais,
        usuarioId,
      });
      if (!movCajaId) throw new Error("No se pudo crear el movimiento en caja");

      if (provNotif) {
        const plural = boletasAsociadas === 1 ? "boleta asociada" : "boletas asociadas";
        toast.success(
          `Proveedor ${provNotif.nombre} ${provNotif.verbo} (RUT ${provNotif.rut}) — ${boletasAsociadas} ${plural}`
        );
      } else {
        toast.success(
          boletasSeleccionadas.length === 1
            ? "Boleta de honorarios vinculada al pago"
            : `${boletasSeleccionadas.length} boletas de honorarios vinculadas al pago`
        );
      }
      onSuccess?.({
        id: movimiento.id,
        estado: "procesado",
        movimiento_caja_id: movCajaId,
        movimiento_caja: { categoria_pago: "pago_proveedor" },
      });
      onRefreshFuturos?.();
      onClose();
    } catch (err) {
      toast.error("Error al vincular la(s) boleta(s): " + (err.message || ""));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmar = async () => {
    // Blindaje del forzado (cubre también el dispatch a handleConfirmarMultiple): si el toggle quedó
    // en ON pero el gate NO habilita → cortar. Nunca confiar solo en el estado del toggle. Ver 10.108.
    if (esForzado && !puedeForzar) return toast.error("No estás autorizado a forzar esta vinculación.");
    if (esFactoring) return handleConfirmarFactoring();
    if (esCajaChica) return handleConfirmarCajaChica();
    if (esTransporte) return handleConfirmarTransporte();
    if (esAdelanto) return handleConfirmarAdelanto();
    if (esEmpleado) return handleConfirmarEmpleado();
    if (esPagoExterna) {
      return modoExterno === "existente" ? handleConfirmarVincularExternas() : facturaExt.handleSubmit();
    }
    if (esPagoSinFactura) return handleConfirmarSinFactura();
    if (esPagoACuenta) return handleConfirmarACuenta();
    if (esPagoRecurrente) return handleConfirmarRecurrente();
    if (esCobroACuenta) return handleConfirmarCobroACuenta();
    if (esPagoInstitucional) return handleConfirmarInstitucional();
    if (esPrestamo) return handleConfirmarPrestamo();
    // Intercepta ANTES del path de facturaSeleccionada: en modo "Contra factura" hay factura
    // elegida, pero la consume handleConfirmarEdificio (entidad edificio), no el path proveedor.
    if (esPagoEdificio) return handleConfirmarEdificio();
    if (esPagoActivo) {
      // Selección caja-pura MULTI-target (cuotas de activo + componentes de lote, mezcladas) → caja
      // consolidada. Tiene prioridad: la UI limpia el activo entero al tildar ítems y viceversa.
      if (capexSel.length > 0) return handleConfirmarCapexMultiple();
      // Sin selección caja-pura → ejecución ENTERA de un activo contra su DTE (single, sin cambios).
      return handleConfirmarActivo();
    }
    // F2b: boleta(s) de honorarios seleccionada(s) (excluyente con facturas) → su propio path.
    if (!esCobro && boletasSeleccionadas.length > 0) return handleConfirmarHonorario();
    // Cobro de exportación (DTE 110): mantiene su wizard y escritura propia (giro + costos_intermediacion).
    if (esCobroExportacion) return handleConfirmarMultiple();
    // Flujo SII unificado (pago/cobro, 1..N facturas, con o sin cuotas) → executor único (Fase 1).
    return handleConfirmarSII();
  };

  // Flujo unificado "Pago/Cobro SII": vincula el movimiento a 1..N facturas del mismo proveedor/cliente,
  // cada una saldada por sus CUOTAS tildadas (monto derivado) o por un MONTO manual (sin plan). Enruta
  // por ejecutarVinculacionFacturasCuotas (Fase 1) — una sola escritura para single y múltiple (10.1),
  // con el fix del puente (MBF = monto aplicado). El cuadre total (cuadreSII) gobierna el Confirmar; el
  // exceso sin DTE (propina/OUR) es sobrepago legítimo; el faltante/descuadre requiere gerente + forzar.
  const handleConfirmarSII = async () => {
    if (facturasSeleccionadas.length === 0) return toast.error("Seleccioná al menos una factura");
    // Entidad para el asiento de caja en pagos: la de la 1ª factura o la resuelta a mano (single).
    const entidadPago = esCobro ? null : facturasSeleccionadas[0].proveedor_id || entidadFallbackId;
    if (!esCobro && !entidadPago) {
      return toast.error("La factura no tiene proveedor asociado. Seleccioná uno en el listado.");
    }
    // Blindaje del forzado: el toggle solo vale con gate activo (gerente + descuadre tras un intento).
    if (esForzado && !puedeForzar) return toast.error("No estás autorizado a forzar esta vinculación.");

    // Gate de cuadre: Sin asignar debe ser cero (dentro de tolerancia). Excepciones:
    //   · exceso sin DTE (propina/OUR): el débito supera Σ facturas legítimamente (single sin plan).
    //   · forzado de gerente: se permite el descuadre (faltante/sobrante) explícitamente.
    // El "forzar" recién aparece ACÁ (intentoForzarConfirm), no mientras se arma el reparto (10.177:
    // la diferencia se muestra en el toast y en el pie).
    if (!cuadreSII.cuadra && !excesoLegitimoSII && !(esForzado && esGerente)) {
      if (esGerente) setIntentoForzarConfirm(true);
      const verbo = cuadreSII.sinAsignar > 0 ? "falta asignar" : "sobra";
      return toast.error(
        `No cuadra: ${verbo} ${formatAmount(Math.abs(cuadreSII.sinAsignar), cuadreSII.moneda)} ` +
          `(asignado ${formatAmount(cuadreSII.asignado, cuadreSII.moneda)} de ` +
          `${formatAmount(cuadreSII.montoMovCmp, cuadreSII.moneda)}).` +
          (esGerente ? " Ajustá el reparto o forzá abajo." : "")
      );
    }

    // Guardrail blando de cobertura (solo factura SIN plan, single): si el pago cubre menos del 50%
    // de la factura, pedir confirmación (no bloquea — frena el click accidental). Con plan el reparto
    // es por cuotas completas y no aplica; el forzado ya confirmó el cierre aparte.
    if (facturasSeleccionadas.length === 1 && !hayCuotasSel && !excesoSinDte && !(esForzado && esGerente)) {
      const f0 = facturasSeleccionadas[0];
      const total = Number(f0.monto_total || 0);
      const aplicado = montoAplicadoFactura(f0);
      const monedaF0 = esCobro ? f0.moneda || moneda : pais === "Perú" ? "PEN" : "CLP";
      if (total > 0 && aplicado / total < 0.5) {
        const pct = Math.round((aplicado / total) * 100);
        const ok = window.confirm(
          `Este pago (${formatAmount(aplicado, monedaF0)}) cubre solo el ${pct}% de la factura ` +
            `(${formatAmount(total, monedaF0)}).\n\n¿Confirmar como pago parcial?`
        );
        if (!ok) return;
      }
    }

    // Guardrails blandos de exceso sin DTE (propina/OUR): piden confirmación, no bloquean.
    if (esPropina && propinaPorcentaje > 0.13) {
      const ok = window.confirm(
        `La propina (${formatAmount(montoPropina, moneda)}) supera el 13% de las facturas (${formatAmount(
          totalFacturasSeleccionadas,
          moneda
        )}) — ¿facturas correctas?\n\n¿Confirmar de todos modos?`
      );
      if (!ok) return;
    }
    if (esComisionTransferenciaInternacional && (warnOURExcesivo || warnFechaDesalineada)) {
      const partes = [];
      if (warnOURExcesivo)
        partes.push(`Los Gastos OUR representan el ${Math.round(ourPorcentaje * 100)}% del débito, parece excesivo.`);
      if (warnFechaDesalineada)
        partes.push(
          `La fecha de la factura (${facturaSeleccionada?.fecha_emision}) difiere del débito (${movimiento.fecha}) por más de 1 día.`
        );
      const ok = window.confirm(`${partes.join("\n\n")}\n\n¿Confirmar de todos modos?`);
      if (!ok) return;
    }

    // Faltante forzado (Σ facturas > débito, SIN cuotas): se cierran las facturas al 100% y el faltante
    // queda registrado en vinculaciones_forzadas. Con cuotas, el forzado solo salda lo tildado (sin audit).
    const esFaltanteForzado = esForzado && esGerente && cuadreSII.sinAsignar < -0.5 && !hayCuotasSel;

    setLoading(true);
    let forzadoAuditId = null;
    try {
      // Persistir la entidad elegida a mano (single) para próximas consultas.
      if (entidadFallbackId && facturasSeleccionadas.length === 1) {
        const tabla = esCobro ? "facturas_sii_emitidas" : "facturas_sii_recibidas";
        const campo = esCobro ? "cliente_id" : "proveedor_id";
        const { error } = await client
          .from(tabla)
          .update({ [campo]: entidadFallbackId })
          .eq("id", facturaSeleccionada.id);
        if (error) throw error;
      }

      // Auditoría de forzado por faltante ANTES de escribir (lado seguro: si falla, nada se escribe).
      let facturasPayload = facturasSeleccionadas;
      if (esFaltanteForzado) {
        forzadoAuditId = await insertarAuditoriaForzado(
          facturasSeleccionadas.map((f) => ({
            factura_tipo: esCobro ? "sii_emitida" : "sii_recibida",
            factura_id: f.id,
            monto_total: Number(f.monto_total || 0),
          }))
        );
        // Sobre-asignar a monto_total para cerrar cada factura al 100% (el débito real va a la caja).
        facturasPayload = facturasSeleccionadas.map((f) => ({ ...f, montoAsignado: Number(f.monto_total || 0) }));
      }

      // Breadcrumb de la glosa de caja (propina / forzado).
      const descripcionExtra = esPropina
        ? ` · incl. propina ${formatAmount(montoPropina, moneda)}`
        : esFaltanteForzado
          ? ` · forzado, faltante ${formatAmount(montoFaltante, moneda)}`
          : "";

      const { movCajaId, fkEscalar } = await ejecutarVinculacionFacturasCuotas({
        movimiento,
        facturasSeleccionadas: facturasPayload,
        esCobro,
        pais,
        usuarioId,
        entidadId: esCobro ? null : entidadPago,
        descripcionExtra,
      });

      const f0 = facturasSeleccionadas[0];
      toast.success(
        facturasSeleccionadas.length > 1
          ? `${facturasSeleccionadas.length} facturas vinculadas`
          : "Movimiento vinculado correctamente"
      );
      // Marcadores para el patch scoped del panel FUTUROS (evita el reload de ~30 queries). Con cuotas
      // tildadas se cae a recarga completa (las cuotas 'pagado'/'cobrado' salen del set de pendientes y
      // ese estado no lo parchea el padre). En pago, si alguna factura no tenía proveedor_id (se asignó a
      // mano en la vinculación) también se cae a recarga: proveedoresPendientes del padre sigue con
      // proveedor_id null y su fila no leería el ledger. montoAplicado = MBF escrito por factura.
      const tieneCuotasTildadas = facturasSeleccionadas.some(
        (f) => Array.isArray(f.cuotasSeleccionadas) && f.cuotasSeleccionadas.length > 0
      );
      const provScoped = !esCobro && facturasSeleccionadas.every((f) => f.proveedor_id) ? entidadPago : null;
      let emitidasScoped = null;
      if (esCobro) {
        emitidasScoped = facturasSeleccionadas.map((f) => ({
          id: f.id,
          monto_total: Number(f.monto_total || 0),
          montoAplicado: montoAplicadoFactura(f),
        }));
      }
      onSuccess?.({
        id: movimiento.id,
        estado: "procesado",
        movimiento_caja_id: movCajaId,
        movimiento_caja: { categoria_pago: esCobro ? "cobro_factura" : "pago_proveedor" },
        _scopedProveedorId: provScoped,
        _scopedEmitidas: emitidasScoped,
        _scopedCuotas: tieneCuotasTildadas,
        // Embed rico para el badge del histórico (solo single → fkEscalar).
        ...(fkEscalar
          ? esCobro
            ? { factura_emitida: { folio: f0.folio, razon_social_receptor: f0.razon_social_receptor } }
            : { factura_recibida: { folio: f0.folio, razon_social_emisor: f0.razon_social_emisor } }
          : {}),
      });
      onClose();
    } catch (err) {
      // Si el executor falló con la auditoría ya escrita → rollback (no dejar marca sin vinculación).
      await rollbackAuditoriaForzado(forzadoAuditId);
      toast.error("Error al vincular: " + (err.message || ""));
    } finally {
      setLoading(false);
    }
  };

  const facturasFiltradas = facturas.filter((f) => {
    if (esCobroExportacion && f.tipo_dte !== 110) return false;
    if (esCobro && modoCobro === "sii" && f.tipo_dte === 110) return false;
    // NCs recibidas (tipo_dte=61) reducen deuda, no son facturas pagables → fuera del dropdown de pago.
    if (!esCobro && esNotaCredito(f)) return false;
    // "Mostrar DTE AA" apagado (default): ocultar DTEs de años anteriores al actual. Sin fecha_emision
    // → se conserva. Con el checkbox en true no hay corte por año.
    if (!mostrarDteAnioAnterior && f.fecha_emision && parseInt(f.fecha_emision.substring(0, 4), 10) < añoActualPI)
      return false;
    if (!busqueda) return true;
    const texto = busqueda.trim().toLowerCase();
    const nombre = esCobro
      ? (f.razon_social_receptor || "").toLowerCase()
      : (f.razon_social_emisor || "").toLowerCase();
    if (nombre.includes(texto)) return true;
    if (String(f.folio).includes(texto)) return true;
    // Monto: comparar dígitos del query (descartando . , espacios) contra el monto sin separadores.
    // Para exportación (DTE 110) el monto visible es monto_usd, así que se matchea contra ambos para
    // que la factura se encuentre escriba el usuario el valor USD o el CLP fiscal.
    const queryDigitos = texto.replace(/\D/g, "");
    if (queryDigitos && String(f.monto_total).includes(queryDigitos)) return true;
    if (queryDigitos && f.monto_usd != null && String(f.monto_usd).includes(queryDigitos)) return true;
    return false;
  });

  // Boletas de honorarios pendientes (F2a), filtradas por el MISMO buscador (nombre/folio/monto) y el
  // mismo corte "Mostrar DTE AA" que las facturas. Solo se renderizan en "Pago a proveedor" (!esCobro).
  const boletasHonorariosFiltradas = boletasHonorarios.filter((b) => {
    if (!mostrarDteAnioAnterior && b.fecha_emision && parseInt(b.fecha_emision.substring(0, 4), 10) < añoActualPI)
      return false;
    if (!busqueda) return true;
    const texto = busqueda.trim().toLowerCase();
    if ((b.emisor_nombre || "").toLowerCase().includes(texto)) return true;
    if (String(b.folio).includes(texto)) return true;
    const queryDigitos = texto.replace(/\D/g, "");
    if (queryDigitos && String(b.monto_bruto).includes(queryDigitos)) return true;
    return false;
  });

  // Selección de boletas (toggle append/remove, espeja handleSeleccionarFactura). Click en una tildada
  // la quita; click en otra la agrega. EXCLUYENTE con las facturas: al tildar una boleta se limpian las
  // facturas (y viceversa en handleSeleccionarFactura). Gate de misma entidad por `proveedor_id` (ID,
  // 10.7): todas las boletas del mismo proveedor. Una boleta huérfana (_sinProveedor) se selecciona
  // SOLA — hay que resolver el proveedor inline antes de agrupar (espeja el gate de facturas SII).
  const handleSeleccionarBoleta = async (b) => {
    const yaEsta = boletasSeleccionadas.some((x) => x.id === b.id);
    setIntentoForzarConfirm(false);
    setEsForzado(false);

    // QUITAR
    if (yaEsta) {
      setBoletasSeleccionadas(boletasSeleccionadas.filter((x) => x.id !== b.id));
      return;
    }

    // AGREGAR — gate de misma entidad (por proveedor_id) y de proveedor resuelto.
    if (boletasSeleccionadas.length > 0) {
      if (!b.proveedor_id) {
        return toast.error(
          "Esta boleta no tiene proveedor asociado. Seleccionala sola primero para asignarle proveedor."
        );
      }
      const provEsperado = boletasSeleccionadas[0].proveedor_id;
      if (provEsperado && b.proveedor_id !== provEsperado) {
        return toast.error("Todas las boletas deben ser del mismo proveedor.");
      }
    }

    // B2 — cargar el plan de cuotas DISPONIBLES de la boleta, igual que el path SII (handleSeleccionarFactura):
    // las boletas cuelgan de `documento_id` (documentos_fiscales, tipo='boleta_honorario'), NO de
    // factura_sii_recibida_id (NULL) → se resuelve el id surrogate y se carga por getPlanPagosPorDocumento.
    // La disponibilidad y el saldo por cuota se DERIVAN del libro `aplicaciones_saldo` (Σ eventos por
    // cuota_id, misma clave que escribe B1), no del flag `estado` escalar. Boleta sin plan → planPend=[]
    // → cae al input de monto libre (comportamiento previo). No-silencioso si falla (10.177): planPend=[].
    let planPend = [];
    try {
      const docId = await resolverDocumentoFiscalId("boleta_honorario", b.id);
      if (docId != null) {
        const plan = await getPlanPagosPorDocumento(docId);
        const { data: eventosLibro } = await client
          .from("aplicaciones_saldo")
          .select("cuota_id, monto_documento")
          .eq("documento_tipo", "boleta_honorario")
          .eq("documento_id", b.id);
        planPend = cuotasDisponiblesDesdeLibro(plan, eventosLibro || []);
      }
    } catch {
      planPend = [];
    }

    // Con plan → el monto se deriva de las cuotas tildadas (sin montoAsignado, como el SII). Sin plan →
    // montoAsignado pre-cargado = min(saldo pendiente, remanente del débito) para el input editable (10.66/10.67).
    const saldoBoleta = Number(b._saldoPendiente != null ? b._saldoPendiente : b.monto_bruto || 0);
    const yaAsignado = boletasSeleccionadas.reduce((s, x) => s + Number(x.montoAsignado || 0), 0);
    const remanenteMov = Math.abs(monto) - yaAsignado;
    const montoAsignado = planPend.length > 0 ? undefined : Math.max(0, Math.min(saldoBoleta, remanenteMov));

    // Excluyente con facturas SII.
    setFacturasSeleccionadas([]);
    setFacturaSeleccionada(null);
    setPlanPorFactura({});
    // Al abrir una selección nueva desde cero, limpiar la resolución inline de proveedor previa (no
    // arrastrar el proveedor elegido/creado para otra boleta). Solo al pasar de 0 a 1.
    if (boletasSeleccionadas.length === 0) {
      setEntidadFallbackId(null);
      setEntidadFallbackBusqueda("");
      setEntidadFallbackCreada(false);
      setCreandoProvSII(false);
      setProvSIINombre("");
      setProvSIIRut("");
    }
    setPlanPorBoleta((prev) => ({ ...prev, [b.id]: planPend }));
    setBoletasSeleccionadas([
      ...boletasSeleccionadas,
      { ...b, cuotasSeleccionadas: [], ...(montoAsignado != null ? { montoAsignado } : {}) },
    ]);
  };

  // Tildar/destildar una cuota de una boleta con plan (espeja handleToggleCuota del path SII). El monto
  // aplicado a esa boleta se deriva de lo tildado (no editable). Cambiar el reparto oculta "forzar".
  const handleToggleCuotaBoleta = (boletaId, cuota) => {
    setIntentoForzarConfirm(false);
    setEsForzado(false);
    setBoletasSeleccionadas((prev) =>
      prev.map((b) => {
        if (b.id !== boletaId) return b;
        const sel = b.cuotasSeleccionadas || [];
        const yaEsta = sel.some((c) => c.id === cuota.id);
        return { ...b, cuotasSeleccionadas: yaEsta ? sel.filter((c) => c.id !== cuota.id) : [...sel, cuota] };
      })
    );
  };

  // Editar el monto aplicado de una boleta de honorarios (espeja handleMontoAsignado). Acota a (0, saldo].
  const handleMontoAsignadoBoleta = (boletaId, valor) => {
    setIntentoForzarConfirm(false);
    setEsForzado(false);
    setBoletasSeleccionadas((prev) =>
      prev.map((b) => {
        if (b.id !== boletaId) return b;
        const saldo = Number(b._saldoPendiente != null ? b._saldoPendiente : b.monto_bruto || 0);
        const limpio = Number(String(valor).replace(/[^\d.-]/g, ""));
        const val = isNaN(limpio) ? 0 : Math.max(0, Math.min(saldo, limpio));
        return { ...b, montoAsignado: val };
      })
    );
  };

  // Path activo (PASO 2): monto de referencia del activo elegido (costo × cantidad) para el HINT visual
  // en la lista de facturas — resalta en verde las que coinciden en monto. Solo pista: NO auto-selecciona
  // ni filtra; la vinculación se confirma a mano. null si no hay activo elegido.
  const activoSelObj = activosCapex.find((a) => a.id === activoCapexSel);
  const montoActivoRef = activoSelObj
    ? Number(activoSelObj.costo_unitario || 0) * Number(activoSelObj.cantidad || 1)
    : null;
  // Cuotas pendientes del activo elegido: si tiene → path pago de CUOTA (no ejecución entera).
  const cuotasDelActivo = cuotasActivoPorId[activoCapexSel] || [];
  const activoTienePlan = cuotasDelActivo.length > 0;
  // ── Guard de monto CapEx (Fase 3b-i): el cómputo vive más abajo (guardParcialCapex, con la lista
  // enriquecida por montoAsignado). Acá solo las constantes compartidas. El 5% pasó a aplicar SOLO al
  // pago que CIERRA un ítem cross-currency; el parcial puro no. Ver docs/modulos/flujo-caja-v2.md.
  const TOLERANCIA_GUARD_MONTO = 0.05;
  const monedaLocalGuard = pais === "Perú" ? "PEN" : "CLP";
  const esMonedaNoLocalGuard = (m) => (m || "").toUpperCase() !== monedaLocalGuard;

  // ── Selección caja-pura MULTI-target del path CapEx: cuotas de activo + componentes de lote, mezcladas ──
  // Lista unificada: una entrada por cuota pendiente (de cualquier activo con plan) + una por componente de
  // lote pendiente. Clave compuesta `cuota:{id}` / `comp:{id}`. montoNeto en la moneda del target; el guard
  // convierte a local. El "quién pagó qué" al confirmar vive en el movimiento_bancario_id de cada target.
  const capexNombrePorActivo = Object.fromEntries(activosCapex.map((a) => [a.id, a.edificioNombre]));
  const capexItems = [
    ...Object.values(cuotasActivoPorId)
      .flat()
      .map((c) => {
        // Concepto igual que la proyección CapEx (mapCuotaActivo): edificio | TIPO_ACTIVO | proveedor.
        // El tipo_activo distingue filas del mismo edificio (TOTEM vs COSTO_INSTALACION).
        const concepto = [
          capexNombrePorActivo[c.activo_id] || `Activo #${c.activo_id}`,
          c.activo?.tipo_activo?.toUpperCase(),
          c.activo?.proveedor?.nombre || null,
        ]
          .filter(Boolean)
          .join(" | ");
        // objetivoNeto = objetivo COMPLETO (para materializar/recomputar); montoNeto = saldo pendiente
        // que ya trae el loader (2b, _saldoRestante) → es lo que se muestra y lo aplicable por defecto (F3).
        const objetivoNeto = Number(c.monto_cuota || 0);
        const montoNeto = c._saldoRestante != null ? Number(c._saldoRestante) : objetivoNeto;
        const moneda = c.activo?.moneda || monedaLocalDePais(pais);
        return {
          key: `cuota:${c.id}`,
          tipo: "cuota",
          id: c.id,
          // Proveedor del activo padre → atribución de la caja al proveedor (pago a cuenta, no huérfano).
          proveedorId: c.activo?.proveedor_id ?? null,
          proveedorNombre: c.activo?.proveedor?.nombre ?? null,
          objetivoNeto,
          montoNeto,
          _saldoPendiente: montoNeto,
          // Bruto = neto + IVA si el activo PADRE es afecto y en moneda local (helper único, CapEx-IVA F2).
          montoBruto: brutoCapex(montoNeto, { exento_iva: c.activo?.exento_iva, moneda, pais }),
          moneda,
          // Fecha del documento para el TC de conversión a local (10.185): vencimiento de la cuota.
          fecha: c.fecha_vencimiento,
          label: `${concepto} · Cuota ${c.numero_cuota}/${c.total_cuotas} · vence ${c.fecha_vencimiento}`,
        };
      }),
    ...componentesLotePendientes.map((c) => {
      const objetivoNeto = Number(c.monto || 0);
      const montoNeto = c._saldoRestante != null ? Number(c._saldoRestante) : objetivoNeto;
      return {
        key: `comp:${c.id}`,
        tipo: "comp",
        id: c.id,
        proveedorId: c.lote?.proveedor?.id ?? c.lote?.proveedor_id ?? null,
        proveedorNombre: c.lote?.proveedor?.nombre ?? null,
        objetivoNeto,
        montoNeto,
        _saldoPendiente: montoNeto,
        // Componentes de lote (importados): NO se grossean — sin IVA local, igual que en Futuros.
        montoBruto: montoNeto,
        moneda: c.moneda,
        // Fecha del documento para el TC de conversión a local (10.185): fecha de pago del componente.
        fecha: c.fecha_pago,
        label: `${{ transporte: "Transporte", aduana: "Aduana", otro: "Otro" }[c.tipo] || c.tipo} · ${c.lote?.descripcion || `lote #${c.lote_id}`}${c.lote?.proveedor?.nombre ? ` — ${c.lote.proveedor.nombre}` : ""} · vence ${c.fecha_pago}`,
      };
    }),
    // Activos planificado SIN plan de cuotas: la línea única de CapEx que hoy Futuros muestra. Se ofrecen
    // saldables acá, en bruto (helper único brutoCapex, heredando exento_iva/moneda del activo). El neto
    // (costo_unitario) es el capitalizable y NO se toca. CapEx-IVA Fase 3.
    ...activosSinPlan.map((a) => {
      // Sin plan: aún no materializado → sin _saldoRestante (saldo = objetivo completo). Al primer parcial
      // la RPC materializa la cuota sintética y a partir de ahí fluye por getCuotasActivosFuturas (2b).
      const objetivoNeto = Number(a.costo_unitario || 0);
      const montoNeto = a._saldoRestante != null ? Number(a._saldoRestante) : objetivoNeto;
      const moneda = a.moneda || monedaLocalDePais(pais);
      const concepto = [a.edificioNombre, a.tipo_activo?.toUpperCase(), a.proveedor?.nombre || null]
        .filter(Boolean)
        .join(" | ");
      return {
        key: `activo:${a.id}`,
        tipo: "activo",
        id: a.id,
        proveedorId: a.proveedor?.id ?? a.proveedor_id ?? null,
        proveedorNombre: a.proveedor?.nombre ?? null,
        objetivoNeto,
        montoNeto,
        _saldoPendiente: montoNeto,
        montoBruto: brutoCapex(montoNeto, { exento_iva: a.exento_iva, moneda, pais }),
        moneda,
        fecha_planificada: a.fecha_planificada,
        // Fecha del documento para el TC de conversión a local (10.185): fecha planificada de pago.
        fecha: a.fecha_planificada,
        label: `${concepto}${a.fecha_planificada ? ` · estimado ${a.fecha_planificada}` : ""}`,
      };
    }),
    // FOB del lote (bloque Stock): la compra de los equipos, el CapEx más grande. La línea FOB que hoy
    // Futuros proyecta desde lotes_compra (costo_unitario × cantidad_total). Importado (USD): SIN IVA local
    // (bruto = neto); el guard de Fase 4 convierte por su fecha. El neto/moneda del lote no se toca. F5.
    ...lotesFobPendiente.map((l) => {
      // FOB (3b-ii): acepta parcial. objetivoNeto = completo (para materializar la fila fob en la RPC);
      // montoNeto = saldo restante del target fob si ya hay un parcial (Map), si no el completo.
      const objetivoNeto = Number(l.costo_unitario || 0) * Number(l.cantidad_total || 0);
      const montoNeto = l._saldoRestante != null ? Number(l._saldoRestante) : objetivoNeto;
      const concepto = [
        `STOCK ${l.cantidad_total}x`,
        l.tipo_activo?.toUpperCase(),
        l.proveedor_rel?.nombre || l.proveedor || null,
      ]
        .filter(Boolean)
        .join(" | ");
      return {
        key: `fob:${l.id}`,
        tipo: "fob",
        id: l.id,
        proveedorId: l.proveedor_rel?.id ?? l.proveedor_id ?? null,
        proveedorNombre: l.proveedor_rel?.nombre ?? null,
        objetivoNeto,
        montoNeto,
        _saldoPendiente: montoNeto,
        montoBruto: montoNeto, // importado: sin IVA local (mismo criterio que los componentes de lote)
        moneda: l.moneda,
        fecha: l.fecha_planificada,
        label: `FOB · ${concepto}${l.fecha_planificada ? ` · estimado ${l.fecha_planificada}` : ""}`,
      };
    }),
  ];
  // ── Fase 3b-i: selección con MONTO A APLICAR por ítem (pago parcial) ─────────────────────────────
  // Banco en local (por SU fecha, 10.185) y TC target→local por ítem (convertir 1 unidad).
  const bancoLocalGuard = convertirAMonedaLocal(Math.abs(monto), moneda, pais, movimiento?.fecha);
  const tcItemLocal = (it) => convertirAMonedaLocal(1, it.moneda, pais, it.fecha) || 1;
  // Modo B (campo editable por ítem) cuando hay más de un ítem tildado; single = modo A (automático).
  const capexModoReparto = capexSel.length > 1;
  // Enriquecer cada seleccionado con `montoAsignado` (neto, moneda del target) y `_brutoAplicadoLocal`.
  // Default (A): lo que el banco (remanente, local, bruto) puede cubrir del saldo, de-grosseado y
  // de-convertido a neto-target; capado al saldo pendiente (candado de sobrepago, 3a lo dejó acá). B:
  // override editable de capexMontos. FOB: todo-o-nada (montoAsignado = saldo). El sobrante del banco se
  // consume en orden entre los ítems (molde reparteParcialValido del path proveedor, 10.66/10.67).
  let _remanenteLocal = bancoLocalGuard;
  const capexItemsSel = capexItems
    .filter((it) => capexSel.includes(it.key))
    .map((it) => {
      const saldoNet = Number(it.montoNeto || 0);
      const grossFactor = saldoNet > 0 ? Number(it.montoBruto || 0) / saldoNet : 1;
      const tc = tcItemLocal(it);
      let asignadoNet;
      // Fase 3b-ii: FOB entra al mismo camino parcial que cuota/comp (ya no todo-o-nada).
      const override = capexMontos[it.key];
      if (override != null && override !== "") {
        const limpio = Number(String(override).replace(/[^\d.-]/g, "")) || 0;
        asignadoNet = Math.max(0, Math.min(saldoNet, limpio));
      } else {
        const bancoNetTarget = tc > 0 && grossFactor > 0 ? _remanenteLocal / grossFactor / tc : _remanenteLocal;
        asignadoNet = Math.max(0, Math.min(saldoNet, bancoNetTarget));
      }
      const netLocal = convertirAMonedaLocal(asignadoNet, it.moneda, pais, it.fecha);
      const brutoAplicadoLocal = netLocal * grossFactor;
      _remanenteLocal = Math.max(0, _remanenteLocal - brutoAplicadoLocal);
      return {
        ...it,
        montoAsignado: asignadoNet,
        _netAplicadoLocal: netLocal,
        _brutoAplicadoLocal: brutoAplicadoLocal,
      };
    });
  // Lookup para el input del reparto (modo B): monto asignado efectivo por key.
  const capexMontoAsignadoPorKey = Object.fromEntries(capexItemsSel.map((it) => [it.key, it.montoAsignado]));
  // Sums desde lo APLICADO (no el objetivo): alimentan la caja consolidada (monto_neto/impuesto).
  const capexSumaNetoLocal = capexItemsSel.reduce((s, it) => s + it._netAplicadoLocal, 0);
  const capexSumaBrutoLocal = capexItemsSel.reduce((s, it) => s + it._brutoAplicadoLocal, 0);
  const capexImpuestoLocal = capexSumaBrutoLocal - capexSumaNetoLocal;
  const capexTotalLocal = capexSumaBrutoLocal;
  // Guard parcial (reemplaza el 5%-sobre-Σ). Por ítem: 0 < asignado ≤ saldo (sobrepago). Σ bruto local
  // === banco (redondeo). El 5% solo cuando un ítem CIERRA cross-currency (drift de TC banco↔target).
  const guardMontoCapex = (() => {
    if (!(esPagoActivo && capexSel.length > 0)) return null;
    const cadaValido = capexItemsSel.every(
      (it) => it.montoAsignado > 0.005 && it.montoAsignado <= it._saldoPendiente + 0.5
    );
    const hayCierreCross = capexItemsSel.some(
      (it) => it._saldoPendiente - it.montoAsignado <= 1 && esMonedaNoLocalGuard(it.moneda)
    );
    const tolCuadre = hayCierreCross
      ? bancoLocalGuard * TOLERANCIA_GUARD_MONTO
      : Math.max(TOLERANCIA_REDONDEO_LOCAL, 1);
    const cuadra = Math.abs(capexSumaBrutoLocal - bancoLocalGuard) <= tolCuadre;
    const bloquea = !(cadaValido && cuadra);
    let mensaje = null;
    if (!cadaValido) mensaje = "Cada monto a aplicar debe ser mayor a 0 y no superar el saldo pendiente del ítem.";
    else if (!cuadra)
      mensaje = `La suma aplicada (${formatAmount(capexSumaBrutoLocal, monedaLocalGuard)}) debe coincidir con el banco (${formatAmount(bancoLocalGuard, monedaLocalGuard)}).`;
    return { bloquea, warn: bloquea, mensaje };
  })();
  const bloqueaMontoCapex = !!guardMontoCapex?.bloquea;

  // Presentación del monto de una factura SII en las listas de cobro. Para exportación (DTE 110) la
  // verdad económica es monto_usd (snapshot de la venta en dólares); monto_total es el CLP fiscal del
  // SII. Con monto_usd → US$; sin él → CLP honesto. Nunca monto_total con etiqueta US$.
  const formatMontoFacturaSII = (f, monedaFallback) => {
    if (esCobro && f.tipo_dte === 110) {
      return f.monto_usd != null ? formatAmount(f.monto_usd, "USD") : formatAmount(f.monto_total, "CLP");
    }
    return formatAmount(f.monto_total, monedaFallback);
  };

  // Modo "Pago a edificio" — adjunto opcional: la lista de facturas de soporte se acota a los
  // proveedores vinculados a los edificios elegidos (edificios.proveedor_id). Comparación por ID
  // (regla 8). Si ningún edificio elegido tiene proveedor vinculado, el Set queda vacío → no se
  // muestran facturas (no exponemos facturas de otros proveedores).
  const proveedorIdsEdificioSel = new Set(edificiosAsignaciones.map((a) => a.proveedorId).filter((id) => id != null));
  const facturasFiltradasEdificio = facturasFiltradas.filter(
    (f) => f.proveedor_id != null && proveedorIdsEdificioSel.has(Number(f.proveedor_id))
  );

  const empleadosFiltrados = empleados.filter((e) => {
    if (!empleadoBusqueda) return true;
    const texto = empleadoBusqueda.toLowerCase();
    const full = `${e.nombre || ""} ${e.apellido || ""}`.toLowerCase();
    return full.includes(texto);
  });

  // Datos derivados del panel "Vinculación actual" → Sección C (pago a empleado).
  const empVinc = pagosEmpleadoVinculados[0]?.empleado;
  const empleadoVinculadoNombre = empVinc ? `${empVinc.nombre || ""} ${empVinc.apellido || ""}`.trim() : "—";
  const totalPagosEmpleadoVinculados = pagosEmpleadoVinculados.reduce((s, p) => s + Number(p.monto || 0), 0);

  // Filtro de facturas externas pendientes para el modo "Vincular existente". Solo se muestran las
  // que tienen SALDO FIFO pendiente (presentes en saldosExtMap); las totalmente amortizadas por pagos
  // a cuenta quedan fuera. Cada fila lleva su `saldoDisplay` (saldo pendiente, NO monto_total) y el
  // flag `_parcial` (saldoDisplay < monto_total) que bloquea su vinculación en el confirm. Oldest-first.
  const extFiltradas = facturasExtPendientes
    .filter((f) => saldosExtMap.has(f.id))
    .map((f) => {
      const saldoDisplay = saldosExtMap.get(f.id);
      return { ...f, saldoDisplay, _parcial: saldoDisplay < Number(f.monto_total) - 0.01 };
    })
    // "Mostrar DTE AA" apagado (default): ocultar DTEs de años anteriores al actual (sin fecha → conservar).
    .filter(
      (f) => mostrarDteAnioAnterior || !f.fecha_emision || parseInt(f.fecha_emision.substring(0, 4), 10) >= añoActualPI
    )
    .filter((f) => {
      if (!busquedaExt) return true;
      const texto = busquedaExt.trim().toLowerCase();
      if ((f.proveedor_nombre || "").toLowerCase().includes(texto)) return true;
      if ((f.numero_documento || "").toLowerCase().includes(texto)) return true;
      if ((f.concepto || "").toLowerCase().includes(texto)) return true;
      const queryDigitos = texto.replace(/\D/g, "");
      if (queryDigitos && String(f.monto_total).includes(queryDigitos)) return true;
      return false;
    })
    .sort((a, b) => (a.fecha_emision || "").localeCompare(b.fecha_emision || ""));

  const monedaLocal = pais === "Perú" ? "PEN" : "CLP";
  const sumaExtSeleccionadas = externasSeleccionadas.reduce((s, f) => s + Number(f.monto_total || 0), 0);
  const monedaExtSeleccionada = externasSeleccionadas[0]?.moneda || null;
  // Cross-currency: el banco está en una moneda (ej: CLP) y las facturas en otra (ej: USD).
  const extCrossCurrency = !!monedaExtSeleccionada && monedaExtSeleccionada !== moneda;
  // Solo podemos convertir cuando el banco está en moneda LOCAL (CLP/PEN), porque
  // convertirAMonedaLocal convierte HACIA la moneda local usando el TC de fecha_emision.
  // Si el banco está en USD/UF (caso raro), no hay destino común → bypass manual.
  const puedeConvertirCrossCurrency = extCrossCurrency && moneda === monedaLocal;
  const extBypassManual = extCrossCurrency && !puedeConvertirCrossCurrency;
  // Suma comparable: cross-currency convertible → convertida a moneda del banco con TC
  // histórico de cada factura; same-currency → suma cruda.
  const convertirFacturaExt = (f) => convertirAMonedaLocal(Number(f.monto_total || 0), f.moneda, pais, f.fecha_emision);
  const sumaExtConvertida = puedeConvertirCrossCurrency
    ? externasSeleccionadas.reduce((s, f) => s + convertirFacturaExt(f), 0)
    : sumaExtSeleccionadas;
  // TC efectivo (local por unidad extranjera) — exacto en single, ponderado en múltiple.
  const tcEfectivoExt = sumaExtSeleccionadas > 0 ? sumaExtConvertida / sumaExtSeleccionadas : 0;
  const diferenciaExt = movimiento?.monto
    ? Math.abs(Number(movimiento.monto) - sumaExtConvertida) / Number(movimiento.monto)
    : 0;
  // Tolerancia sensible a la moneda (ver constantes arriba): 15% cross-currency convertible (spread
  // cambiario del banco), 1% same-currency. Cross-currency no convertible (banco USD/UF) → bypass
  // manual sin validación numérica.
  const extCuadra = extBypassManual
    ? true
    : puedeConvertirCrossCurrency
      ? diferenciaExt < TOLERANCIA_EXT_CROSS_CURRENCY
      : diferenciaExt < TOLERANCIA_EXT_SAME_CURRENCY;

  // Strings del aviso cross-currency precomputados (evita template literals multilínea en JSX).
  const tcLabelExt = tcEfectivoExt.toLocaleString("es-CL", { maximumFractionDigits: 2 });
  const difPctExt = (diferenciaExt * 100).toFixed(1);
  const sumaExtOrigFmt = formatAmount(sumaExtSeleccionadas, monedaExtSeleccionada || moneda);
  const sumaExtConvFmt = formatAmount(sumaExtConvertida, moneda);
  const avisoCrossCurrency = extBypassManual
    ? `Monedas distintas (${moneda} / ${monedaExtSeleccionada}) — cuadre manual`
    : `${sumaExtOrigFmt} → ${sumaExtConvFmt} (TC ${tcLabelExt}) — dif. ${difPctExt}%`;

  const tabs = [
    { key: "cobro", label: "Cobro de cliente" },
    { key: "pago", label: "Pago a proveedor" },
    ...(esEgreso && puedePagarEmpleado ? [{ key: "empleado", label: "Pago a empleado" }] : []),
    ...(esEgreso ? [{ key: "institucional", label: "Pago institucional" }] : []),
    ...(esEgreso ? [{ key: "edificio", label: "Pago a edificio" }] : []),
    ...(esEgreso ? [{ key: "activo", label: "CapEx" }] : []),
    ...(esEgreso && creditosDisponibles.length > 0 ? [{ key: "prestamo", label: "Créditos" }] : []),
    ...(!esEgreso ? [{ key: "factoring", label: "Factoring" }] : []),
  ];

  const mostrarListaFacturas =
    !esEmpleado &&
    !esPagoExterna &&
    !esPagoSinFactura &&
    !esPagoACuenta &&
    !esPagoRecurrente &&
    !esCobroACuenta &&
    !esPagoInstitucional &&
    // En modo edificio la lista compartida NO se muestra arriba: la factura es adjunto opcional
    // y vive en su propio acordeón (filtrado por proveedor del edificio) dentro del bloque reparto.
    !esPagoEdificio &&
    !esPagoActivo &&
    !esFactoring &&
    !esPrestamo &&
    !esCajaChica;

  const sumaSeleccionadas = operacionesFactoring
    .filter((op) => factoringOpIds.includes(op.id))
    .reduce((acc, op) => {
      const m = factoringTipoVinc === "giro" ? getGiroEfectivo(op) : Number(op.monto_remanente || 0);
      return acc + m;
    }, 0);

  const diffAbs = Math.abs(sumaSeleccionadas - monto);
  const diffPct = monto > 0 ? diffAbs / monto : 0;
  const matchExacto = diffAbs === 0;
  const matchOk = diffPct <= 0.01;
  const matchWarning = diffPct > 0.01 && diffPct <= 0.05;
  const matchError = diffPct > 0.05;

  const confirmarDisabled =
    // Boleta(s) seleccionada(s) (excluyente con facturas) → rutea a handleConfirmarHonorario. Se habilita
    // cuando el cuadre da (Σ montoAsignado ≈ |débito|), para 1 o N boletas por igual (mismo gate que el SII).
    !esCobro && boletasSeleccionadas.length > 0
      ? loading || !cuadreBoletas.cuadra
      : loading ||
        // Forzado (10.108 invertida): habilita SOLO si el gate autoriza (esForzado && puedeForzar).
        // Si el toggle quedó en ON pero el gate ya no habilita, sigue bloqueado (ver blindaje en
        // handleConfirmar*). Nunca confiar solo en el estado del toggle.
        (esForzado && !puedeForzar) ||
        (esPrestamo
          ? !cuotaPrestamo
          : esCajaChica || esTransporte
            ? !empleadoId
            : esAdelanto
              ? !empleadoId || !adelantoMes || !adelantoAño
              : esFactoring
                ? factoringOpIds.length === 0 || matchError
                : esEmpleado
                  ? !empleadoId || !cuadra || componentes.length === 0
                  : esPagoExterna
                    ? modoExterno === "existente"
                      ? externasSeleccionadas.length === 0 || !extCuadra
                      : !facturaExt.isValid
                    : esPagoSinFactura
                      ? !sfConcepto.trim() || !sfCuentaER || sfProveedorEsExtranjero
                      : esPagoACuenta
                        ? !acProveedorId || !acBucket
                        : esCobroACuenta
                          ? !acCobroProveedorId || !acCobroBucket
                          : esPagoRecurrente
                            ? recConfirmarDisabled
                            : esPagoEdificio
                              ? edificioConfirmarDisabled
                              : esPagoActivo
                                ? capexSel.length > 0
                                  ? bloqueaMontoCapex // caja-pura: monto a aplicar > saldo o Σ ≠ banco (3b-i)
                                  : // activo entero contra DTE
                                    !(activoCapexSel && !activoTienePlan && facturaActivoSel)
                                : esPagoInstitucional
                                  ? !piInstitucion ||
                                    (!!INSTITUCIONES.find((i) => i.value === piInstitucion)?.requierePeriodo &&
                                      (!piPeriodoMes || !piPeriodoAño))
                                  : esCobroExportacion
                                    ? facturasSeleccionadas.length === 0 || !costosGiroValidos
                                    : siiConfirmarDisabled);

  if (!movimiento) return null;

  return (
    <Modal show={show} onHide={onClose} size="lg" dialogClassName={esEgreso ? "vinc-mov-ancho" : undefined} scrollable>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "0.95rem" }}>Vincular movimiento bancario</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div
          style={{
            background: "#f8f9fa",
            borderRadius: 6,
            padding: "10px 14px",
            marginBottom: 16,
            fontSize: "0.85rem",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <div style={{ fontWeight: 600 }}>{movimiento.descripcion_raw}</div>
            <div style={{ color: "#888", fontSize: "0.78rem" }}>{movimiento.fecha}</div>
          </div>
          <div
            style={{
              fontWeight: 700,
              fontSize: "1rem",
              color: movimiento.tipo === "ingreso" ? "#27ae60" : "#c0392b",
            }}
          >
            {formatAmount(monto, moneda)}
          </div>
        </div>

        {(vinculacionesSII.length > 0 || tieneVinculacionCaja || pagosEmpleadoVinculados.length > 0) && (
          <div
            style={{
              padding: "12px 14px",
              background: "#fff3cd",
              border: "1px solid #f0d878",
              borderRadius: 6,
              marginBottom: 16,
            }}
          >
            <div style={{ fontWeight: 700, color: "#856404", marginBottom: 8, fontSize: "0.85rem" }}>
              Vinculación actual
            </div>

            {/* Sección A: filas de movimiento_bancario_facturas (V/C/X). Etiqueta humana
                según factura_tipo y, si el embed lo trae, folio/doc en vez del ID crudo. */}
            {vinculacionesSII.map((v) => {
              const tipoLabel =
                v.factura_tipo === "sii_emitida"
                  ? "Factura emitida"
                  : v.factura_tipo === "sii_recibida"
                    ? "Factura recibida"
                    : v.factura_tipo === "factura_externa"
                      ? "Factura externa"
                      : v.factura_tipo === "boleta_honorario"
                        ? "Boleta honorarios"
                        : v.factura_tipo;
              // El folio sale del puente: la fila enriquecida (v.factura) lo trae para SII, tanto
              // single como multi. B3 Fase B paso 2: ya no hay fallback a la FK directa
              // `movimiento.factura_emitida/recibida` (en retiro); la externa sí sigue en su embed.
              const identificador = v._boletaFolio
                ? `Boleta N°${v._boletaFolio}`
                : v.factura?.folio
                  ? `Folio ${v.factura.folio}`
                  : v.factura_tipo === "factura_externa"
                    ? movimiento?.factura_externa?.[0]?.numero_documento
                    : null;
              return (
                <div
                  key={v.id}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "6px 0",
                    borderTop: "1px solid #f0d878",
                    fontSize: "0.82rem",
                  }}
                >
                  <div>
                    <span style={{ fontWeight: 600 }}>{tipoLabel}</span>
                    <span style={{ margin: "0 8px", color: "#999" }}>|</span>
                    <span>{identificador || `ID ${v.factura_id}`}</span>
                    <span style={{ margin: "0 8px", color: "#999" }}>|</span>
                    <span style={{ fontWeight: 600 }}>
                      {/* Display en moneda real: DTE 110 export → US$ (monto_usd); resto → moneda del
                          movimiento. NO se toca mbf.monto (correcto para la cobertura). */}
                      {v.factura ? formatMontoFacturaSII(v.factura, moneda) : formatAmount(v.monto, moneda)}
                    </span>
                  </div>
                </div>
              );
            })}
            {/* Un ÚNICO botón de desvincular por PAGO (no por factura): libera todas las facturas del
                movimiento de golpe. Evita el camino peligroso de desvincular una sola de un bundle.
                A la izquierda, la nota de la NC usada cuando el forzado ya está regularizado (F4.3). */}
            {vinculacionesSII.length > 0 && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 8,
                  paddingTop: 8,
                  marginTop: 4,
                  borderTop: "1px solid #f0d878",
                }}
              >
                <div style={{ fontSize: "0.74rem", color: "#856404" }}>
                  {puedeDeshacerRegularizacion ? `Regularizado con NC #${forzadoPendiente.ncFacturaId}` : ""}
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  {/* Fase 4.2: solo si hay forzado pendiente + gerente. Abre el picker de NC candidatas. */}
                  {puedeRegularizarForzado && (
                    <button
                      type="button"
                      onClick={handleAbrirPickerNC}
                      disabled={loading || cargandoNC}
                      style={{
                        background: "none",
                        border: "1px solid #e67e22",
                        borderRadius: 4,
                        padding: "4px 12px",
                        fontSize: "0.78rem",
                        color: "#b35900",
                        cursor: loading || cargandoNC ? "not-allowed" : "pointer",
                      }}
                    >
                      {cargandoNC ? "..." : "Regularizar con NC"}
                    </button>
                  )}
                  {/* Fase 4.3: solo si el forzado ya está regularizado + gerente. Espejo inverso (10.121). */}
                  {puedeDeshacerRegularizacion && (
                    <button
                      type="button"
                      onClick={handleDeshacerRegularizacion}
                      disabled={loading}
                      title={`Deshacer la regularización (NC id ${forzadoPendiente.ncFacturaId})`}
                      style={{
                        background: "none",
                        border: "1px solid #e67e22",
                        borderRadius: 4,
                        padding: "4px 12px",
                        fontSize: "0.78rem",
                        color: "#b35900",
                        cursor: loading ? "not-allowed" : "pointer",
                      }}
                    >
                      {loading ? "..." : "Deshacer regularización"}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={handleDesvincularPagoCompleto}
                    disabled={loading}
                    style={{
                      background: "none",
                      border: "1px solid #c62828",
                      borderRadius: 4,
                      padding: "4px 12px",
                      fontSize: "0.78rem",
                      color: "#c62828",
                      cursor: loading ? "not-allowed" : "pointer",
                    }}
                  >
                    {loading ? "..." : "Desvincular pago completo"}
                  </button>
                </div>
              </div>
            )}

            {/* Fase 4.1 — Picker de NC candidatas (solo lectura). Como la NC no referencia folio, el
                match es manual: se listan las NC pendientes del proveedor y se resaltan (verde) las de
                monto ≈ faltante. La selección/escritura (reducir MBF + marcar 'resuelto') llega en F4.2. */}
            {puedeRegularizarForzado && mostrarPickerNC && (
              <div
                style={{
                  marginTop: 10,
                  padding: 10,
                  border: "1px solid #e67e22",
                  borderRadius: 4,
                  background: "#fdf3e7",
                  fontSize: "0.8rem",
                }}
              >
                <div style={{ fontWeight: 700, color: "#b35900", marginBottom: 6 }}>
                  Notas de crédito candidatas · faltante {formatAmount(forzadoPendiente.faltante, moneda)}
                </div>
                {ncCandidatas.length === 0 ? (
                  <div style={{ color: "#856404" }}>
                    No hay NC pendientes de este proveedor. Corré "Actualizar SII" cuando llegue la NC real.
                  </div>
                ) : (
                  ncCandidatas.map((nc) => {
                    const cerca =
                      Math.abs(Number(nc.monto_total || 0) - forzadoPendiente.faltante) <=
                      Math.max(forzadoPendiente.faltante * 0.05, 1);
                    const seleccionada = ncSeleccionada?.id === nc.id;
                    return (
                      <div
                        key={nc.id}
                        onClick={() => setNcSeleccionada(nc)}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "5px 8px",
                          marginTop: 4,
                          borderRadius: 4,
                          cursor: "pointer",
                          border: seleccionada
                            ? "2px solid #b35900"
                            : cerca
                              ? "1px solid #198754"
                              : "1px solid #f0d878",
                          background: seleccionada ? "#ffe9d1" : cerca ? "#f0f9f4" : "#fff",
                        }}
                      >
                        <span>
                          {seleccionada ? "● " : ""}NC Folio {nc.folio} · {nc.fecha_emision}
                        </span>
                        <span style={{ fontWeight: 600, color: cerca ? "#198754" : "#333" }}>
                          {formatAmount(nc.monto_total, moneda)}
                          {cerca ? " ✓" : ""}
                        </span>
                      </div>
                    );
                  })
                )}
                {ncCandidatas.length > 0 && (
                  <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
                    <button
                      type="button"
                      onClick={handleRegularizarConNC}
                      disabled={loading || !ncSeleccionada}
                      style={{
                        background: !ncSeleccionada ? "#e9c8a3" : "#e67e22",
                        border: "none",
                        borderRadius: 4,
                        padding: "5px 14px",
                        fontSize: "0.78rem",
                        fontWeight: 600,
                        color: "#fff",
                        cursor: loading || !ncSeleccionada ? "not-allowed" : "pointer",
                      }}
                    >
                      {loading ? "..." : "Confirmar regularización"}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Sección B: vinculación de solo-caja (G/A/P/D). Se suprime cuando ya hay
                MBF para evitar duplicar info — V/C/X exponen la caja vía sección A. */}
            {tieneVinculacionCaja && vinculacionesSII.length === 0 && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "6px 0",
                  borderTop: "1px solid #f0d878",
                  fontSize: "0.82rem",
                }}
              >
                <div>
                  <span style={{ fontWeight: 600 }}>
                    {categoriaCajaActual === "caja_chica"
                      ? "Caja chica"
                      : categoriaCajaActual === "gasto_sin_factura"
                        ? "Gasto sin factura"
                        : categoriaCajaActual === "pago_a_cuenta" ||
                            categoriaCajaActual === "pago_factura" ||
                            categoriaCajaActual === "pago_credito"
                          ? "Pago a cuenta"
                          : categoriaCajaActual === "cobro_a_cuenta" ||
                              categoriaCajaActual === "cobro_factura" ||
                              categoriaCajaActual === "cobro_credito"
                            ? "Cobro a cuenta"
                            : categoriaCajaActual === "costo_financiero"
                              ? "Cambio de moneda"
                              : categoriaCajaActual === "pago_banco"
                                ? "Pago banco"
                                : categoriaCajaActual === "pago_impuesto"
                                  ? "Pago impuesto"
                                  : categoriaCajaActual === "cotizaciones_previsionales"
                                    ? "Previsional"
                                    : categoriaCajaActual === "compra_activo"
                                      ? "CapEx"
                                      : categoriaCajaActual}
                  </span>
                  <span style={{ margin: "0 8px", color: "#999" }}>|</span>
                  <span>{entidadCajaActual || "—"}</span>
                  <span style={{ margin: "0 8px", color: "#999" }}>|</span>
                  <span style={{ fontWeight: 600 }}>{formatAmount(monto, moneda)}</span>
                </div>
                <button
                  type="button"
                  onClick={handleDesvincularCaja}
                  disabled={loading}
                  style={{
                    background: "none",
                    border: "1px solid #c62828",
                    borderRadius: 4,
                    padding: "4px 12px",
                    fontSize: "0.78rem",
                    color: "#c62828",
                    cursor: loading ? "not-allowed" : "pointer",
                  }}
                >
                  {loading ? "..." : "Desvincular"}
                </button>
              </div>
            )}

            {/* Sección C: pago a empleado (badge E). Vive solo en pagos_empleados — no hay
                MBF ni movimientos_caja. Se suprime si hay MBF (no debería coexistir). */}
            {pagosEmpleadoVinculados.length > 0 && vinculacionesSII.length === 0 && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  padding: "6px 0",
                  borderTop: "1px solid #f0d878",
                  fontSize: "0.82rem",
                }}
              >
                <div>
                  <div>
                    <span style={{ fontWeight: 600 }}>Pago a empleado</span>
                    <span style={{ margin: "0 8px", color: "#999" }}>|</span>
                    <span>{empleadoVinculadoNombre}</span>
                    <span style={{ margin: "0 8px", color: "#999" }}>|</span>
                    <span style={{ fontWeight: 600 }}>{formatAmount(totalPagosEmpleadoVinculados, moneda)}</span>
                  </div>
                  <div style={{ marginTop: 4, fontSize: "0.74rem", color: "#856404" }}>
                    {pagosEmpleadoVinculados.map((p) => {
                      const tipoCap = p.tipo ? p.tipo.charAt(0).toUpperCase() + p.tipo.slice(1) : "—";
                      const periodo =
                        p.periodo_mes && p.periodo_año ? ` ${MESES[p.periodo_mes - 1]} ${p.periodo_año}` : "";
                      return (
                        <div key={p.id}>
                          {tipoCap}
                          {periodo} · {formatAmount(p.monto, p.moneda || moneda)}
                        </div>
                      );
                    })}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleDesvincularEmpleado}
                  disabled={loading}
                  style={{
                    background: "none",
                    border: "1px solid #c62828",
                    borderRadius: 4,
                    padding: "4px 12px",
                    fontSize: "0.78rem",
                    color: "#c62828",
                    cursor: loading ? "not-allowed" : "pointer",
                  }}
                >
                  {loading ? "..." : "Desvincular"}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Transferencia internacional recibida YA vinculada: comisión de recepción neteada
            (Santander la descuenta de los USD entrantes). Sección dentro del flujo "ya vinculado". */}
        {esTransferenciaRecibida && yaEstaVinculado && (
          <div
            style={{
              padding: "12px 14px",
              background: "#fff3cd",
              border: "1px solid #f0d878",
              borderRadius: 6,
              marginBottom: 16,
            }}
          >
            <ComisionNeteadaSelector
              key={`ingreso-vinc-${movimiento.id}`}
              titulo="Comisión por recepción (neteada)"
              cargarVinculadas={() => getComisionesIngresoNeteadas(movimiento.id)}
              cargarDisponibles={() => getFacturasComisionIngresoDisponibles(pais)}
              vincular={(facturaId) => vincularComisionIngreso(movimiento.id, facturaId)}
              desvincular={(filaId) => desvincularComisionIngreso(filaId)}
              destacar={(f) =>
                !!f.fecha_emision &&
                !!movimiento?.fecha &&
                Math.abs(new Date(f.fecha_emision) - new Date(movimiento.fecha)) / 86400000 <= 3
              }
              formatAmount={formatAmount}
              formatFecha={(s) => (s ? `${s.substring(8, 10)}/${s.substring(5, 7)}` : "—")}
              moneda={pais === "Perú" ? "PEN" : "CLP"}
              onCambio={onRefreshFuturos}
              mostrarDteAnioAnterior={mostrarDteAnioAnterior}
              setMostrarDteAnioAnterior={setMostrarDteAnioAnterior}
            />
          </div>
        )}

        {!yaEstaVinculado && (
          <>
            <div style={{ display: "flex", gap: 8, marginBottom: 16, width: "fit-content" }}>
              {tabs.map((t) => {
                // Deshabilitar visualmente los tabs que no aplican al tipo de movimiento.
                // Egreso → no cobro ni factoring; ingreso → no pago/empleado/institucional/edificio.
                const tabDeshabilitado = esEgreso
                  ? t.key === "cobro" || t.key === "factoring"
                  : ["pago", "empleado", "institucional", "edificio", "activo"].includes(t.key);
                return (
                  <button
                    key={t.key}
                    type="button"
                    disabled={tabDeshabilitado}
                    onClick={() => {
                      if (tabDeshabilitado) return;
                      setTipoVinculacion(t.key);
                      setModoEmpleado("remuneracion");
                      resetBusquedas();
                    }}
                    style={{
                      width: 140,
                      padding: "6px 0",
                      fontSize: "0.82rem",
                      fontWeight: 600,
                      border: "1px solid #dee2e6",
                      borderRadius: 6,
                      cursor: tabDeshabilitado ? "not-allowed" : "pointer",
                      background: tipoVinculacion === t.key ? "#2c3e50" : "transparent",
                      color: tipoVinculacion === t.key ? "#fff" : "#666",
                      opacity: tabDeshabilitado ? 0.4 : 1,
                    }}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>

            {!esEmpleado && tipoVinculacion === "pago" && (
              <div style={{ display: "flex", gap: 8, marginBottom: 16, width: "fit-content" }}>
                {[
                  { key: "sii", label: `Factura ${enteFiscal}` },
                  { key: "externa", label: "Factura externa" },
                  { key: "sin_factura", label: "Sin factura" },
                  { key: "a_cuenta", label: "A cuenta" },
                  { key: "recurrente", label: "Costo recurrente" },
                ].map((m) => (
                  <button
                    key={m.key}
                    type="button"
                    onClick={() => {
                      setModoCompra(m.key);
                      resetBusquedas();
                    }}
                    style={{
                      width: 140,
                      padding: "6px 0",
                      fontSize: "0.82rem",
                      fontWeight: 600,
                      border: "1px solid #dee2e6",
                      borderRadius: 6,
                      cursor: "pointer",
                      background: modoCompra === m.key ? "#2c3e50" : "transparent",
                      color: modoCompra === m.key ? "#fff" : "#666",
                    }}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            )}

            {tipoVinculacion === "cobro" && (
              <div style={{ display: "flex", gap: 8, marginBottom: 16, width: "fit-content" }}>
                {[
                  { key: "sii", label: "SII" },
                  { key: "exportacion", label: "Exportación" },
                  { key: "a_cuenta", label: "A cuenta" },
                ].map((m) => (
                  <button
                    key={m.key}
                    type="button"
                    onClick={() => {
                      setModoCobro(m.key);
                      resetBusquedas();
                    }}
                    style={{
                      width: 140,
                      padding: "6px 0",
                      fontSize: "0.82rem",
                      fontWeight: 600,
                      border: "1px solid #dee2e6",
                      borderRadius: 6,
                      cursor: "pointer",
                      background: modoCobro === m.key ? "#2c3e50" : "transparent",
                      color: modoCobro === m.key ? "#fff" : "#666",
                    }}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            )}

            {esPagoEdificio && (
              <div
                style={{
                  fontSize: "0.75rem",
                  color: "#666",
                  background: "#f8f9fa",
                  borderRadius: 4,
                  padding: "8px 10px",
                  marginBottom: 12,
                }}
              >
                Pago <strong>a cuenta</strong> que amortiza el saldo de comisión del/los edificio(s). Adjuntar una
                factura SII de soporte es <strong>opcional</strong>: si no elegís ninguna, el pago se confirma igual.
              </div>
            )}

            {/* Wizard export: stepper de 3 pasos (solo en modo exportación). Clickeable para navegar
                libre; ✓ en los pasos válidos. El cobro normal no muestra stepper. */}
            {esCobroExportacion && (
              <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
                {[
                  { n: 1, label: "Facturas", ok: facturasSeleccionadas.length > 0 },
                  { n: 2, label: "Costos del giro", ok: costosGiroValidos },
                  { n: 3, label: "Comisión Santander", ok: false, opcional: true },
                ].map((s) => (
                  <button
                    key={s.n}
                    type="button"
                    onClick={() => setPasoExport(s.n)}
                    style={{
                      flex: 1,
                      padding: "6px 8px",
                      borderRadius: 6,
                      fontSize: "0.76rem",
                      fontWeight: 600,
                      border: `1px solid ${pasoExport === s.n ? "#2980b9" : "#dee2e6"}`,
                      background: pasoExport === s.n ? "#2980b9" : "transparent",
                      color: pasoExport === s.n ? "#fff" : "#666",
                      cursor: "pointer",
                    }}
                  >
                    {s.ok ? "✓ " : ""}
                    {s.n}. {s.label}
                    {s.opcional ? " (opc.)" : ""}
                  </button>
                ))}
              </div>
            )}

            {mostrarListaFacturas && (!esCobroExportacion || pasoExport === 1) && (
              <>
                <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10 }}>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Buscar por nombre, folio o monto..."
                    value={busqueda}
                    onChange={(e) => setBusqueda(e.target.value)}
                    style={{ flex: 3 }}
                  />
                  <label
                    style={{
                      flex: 1,
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                      fontSize: 13,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={mostrarDteAnioAnterior}
                      onChange={(e) => setMostrarDteAnioAnterior(e.target.checked)}
                    />
                    Mostrar DTE AA
                  </label>
                </div>

                <div style={{ maxHeight: 420, overflowY: "auto", border: "1px solid #dee2e6", borderRadius: 4 }}>
                  {loading ? (
                    <div style={{ padding: 20, textAlign: "center", color: "#999" }}>Cargando...</div>
                  ) : facturasFiltradas.length === 0 && (esCobro || boletasHonorariosFiltradas.length === 0) ? (
                    <div style={{ padding: 20, textAlign: "center", color: "#999" }}>Sin facturas</div>
                  ) : (
                    <>
                      {facturasFiltradas.map((f) => {
                        const nombre = esCobro ? f.razon_social_receptor : f.razon_social_emisor;
                        const seleccionada = facturasSeleccionadas.some((x) => x.id === f.id);
                        // SII recibidas no tiene columna `moneda` en DB → forzar moneda local del país.
                        // Para emitidas, `f.moneda` puede ser USD (DTE 110 exportación) y se respeta.
                        const monedaFactura = esCobro ? f.moneda || moneda : pais === "Perú" ? "PEN" : "CLP";
                        // Exportación: sin snapshot USD la factura no es cobrable en este flujo USD →
                        // se muestra deshabilitada y no clickeable, con hint para resolverla.
                        const exportSinUsd = esCobroExportacion && f.monto_usd == null;
                        return (
                          <div
                            key={f.id}
                            onClick={exportSinUsd ? undefined : () => handleSeleccionarFactura(f)}
                            style={{
                              padding: "8px 12px",
                              cursor: exportSinUsd ? "not-allowed" : "pointer",
                              opacity: exportSinUsd ? 0.5 : 1,
                              fontSize: "0.83rem",
                              borderBottom: "1px solid #f0f0f0",
                              background: seleccionada ? "#e8f4fd" : "transparent",
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <div>
                              <span>{`${f.fecha_emision.substring(8, 10)}/${f.fecha_emision.substring(5, 7)}`}</span>
                              <span style={{ margin: "0 10px" }}>|</span>
                              <span>{nombre}</span>
                              <span style={{ marginLeft: 8 }}>Folio {f.folio}</span>
                              {f._clienteNombre && (
                                <span style={{ marginLeft: 8, color: "#555" }}>· {f._clienteNombre}</span>
                              )}
                              {f._parcial && (
                                <span style={{ marginLeft: 8, color: "#e67e22", fontSize: "0.72rem", fontWeight: 600 }}>
                                  parcial
                                </span>
                              )}
                              {exportSinUsd && (
                                <span
                                  style={{ marginLeft: 8, color: "#999", fontSize: "0.72rem", fontStyle: "italic" }}
                                >
                                  Sin USD — vinculá la venta primero
                                </span>
                              )}
                            </div>
                            <div style={{ color: esCobro ? "#27ae60" : "#c0392b", fontWeight: 600 }}>
                              {f._parcial && (
                                <span
                                  style={{
                                    color: "#999",
                                    fontWeight: 400,
                                    textDecoration: "line-through",
                                    marginRight: 6,
                                  }}
                                >
                                  {formatAmount(f.monto_total_original, monedaFactura)}
                                </span>
                              )}
                              {formatMontoFacturaSII(f, monedaFactura)}
                            </div>
                          </div>
                        );
                      })}
                      {!esCobro &&
                        boletasHonorariosFiltradas.map((b) => {
                          const seleccionadaBoleta = boletasSeleccionadas.some((x) => x.id === b.id);
                          const monedaBoleta = pais === "Perú" ? "PEN" : "CLP";
                          const parcial = Number(b._saldoPendiente) < Number(b.monto_bruto) - 0.01;
                          return (
                            <div
                              key={`BHE-${b.id}`}
                              onClick={() => handleSeleccionarBoleta(b)}
                              style={{
                                padding: "8px 12px",
                                cursor: "pointer",
                                fontSize: "0.83rem",
                                borderBottom: "1px solid #f0f0f0",
                                background: seleccionadaBoleta ? "#e8f4fd" : "transparent",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <div>
                                <span>{`${b.fecha_emision.substring(8, 10)}/${b.fecha_emision.substring(5, 7)}`}</span>
                                <span style={{ margin: "0 10px" }}>|</span>
                                <span>{b.emisor_nombre}</span>
                                {/* Texto plano, mismo estilo/orden que "Folio 2112" de una factura. */}
                                <span style={{ marginLeft: 8 }}>Boleta N°{b.folio}</span>
                                {parcial && (
                                  <span
                                    style={{ marginLeft: 8, color: "#e67e22", fontSize: "0.72rem", fontWeight: 600 }}
                                  >
                                    parcial
                                  </span>
                                )}
                              </div>
                              <div style={{ color: "#c0392b", fontWeight: 600 }}>
                                {parcial && (
                                  <span
                                    style={{
                                      color: "#999",
                                      fontWeight: 400,
                                      textDecoration: "line-through",
                                      marginRight: 6,
                                    }}
                                  >
                                    {formatAmount(b.monto_bruto, monedaBoleta)}
                                  </span>
                                )}
                                {formatAmount(b._saldoPendiente, monedaBoleta)}
                              </div>
                            </div>
                          );
                        })}
                    </>
                  )}
                </div>

                {/* Exportación (DTE 110): resumen USD (wizard propio, escritura por handleConfirmarMultiple). */}
                {esCobroExportacion && facturasSeleccionadas.length > 0 && (
                  <div
                    style={{
                      marginTop: 14,
                      padding: 10,
                      borderRadius: 4,
                      background: Math.abs(diferenciaUsd) < 0.5 ? "#e8f5e9" : "#fff3cd",
                      border: `1px solid ${Math.abs(diferenciaUsd) < 0.5 ? "#a5d6a7" : "#f0d878"}`,
                    }}
                  >
                    <div style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 8 }}>
                      {facturasSeleccionadas.length} factura(s) — exportación
                    </div>
                    <div style={{ marginBottom: 8 }}>
                      {facturasSeleccionadas.map((f) => (
                        <div
                          key={f.id}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            fontSize: "0.82rem",
                            padding: "3px 0",
                          }}
                        >
                          <span>
                            {`${f.fecha_emision.substring(8, 10)}/${f.fecha_emision.substring(5, 7)}`} |{" "}
                            {f.razon_social_receptor} Folio {f.folio}
                          </span>
                          <span style={{ display: "flex", gap: 8, alignItems: "center" }}>
                            <span>{formatMontoFacturaSII(f, f.moneda || moneda)}</span>
                            <button
                              type="button"
                              onClick={() => handleSeleccionarFactura(f)}
                              style={{
                                border: "none",
                                background: "transparent",
                                color: "#c0392b",
                                cursor: "pointer",
                                fontSize: "1rem",
                                padding: "0 4px",
                              }}
                              title="Quitar de la selección"
                            >
                              ×
                            </button>
                          </span>
                        </div>
                      ))}
                    </div>
                    <div
                      style={{
                        borderTop: "1px solid #ccc",
                        paddingTop: 6,
                        fontSize: "0.82rem",
                        display: "grid",
                        gridTemplateColumns: "1fr auto",
                        gap: "2px 12px",
                      }}
                    >
                      <span>Suma seleccionada</span>
                      <span style={{ textAlign: "right", fontWeight: 600 }}>
                        {formatAmount(sumaUsdSeleccionadas, "USD")}
                      </span>
                      <span>Monto del movimiento</span>
                      <span style={{ textAlign: "right", fontWeight: 600 }}>{formatAmount(monto, moneda)}</span>
                      <span style={{ fontWeight: 600 }}>Diferencia</span>
                      <span
                        style={{
                          textAlign: "right",
                          fontWeight: 700,
                          color: Math.abs(diferenciaUsd) < 0.5 ? "#2e7d32" : "#c0392b",
                        }}
                      >
                        {Math.abs(diferenciaUsd) < 0.5 ? "✓ exacta" : formatAmount(diferenciaUsd, "USD")}
                      </span>
                    </div>
                  </div>
                )}

                {/* SII unificado — una sola pantalla (single o múltiple). Cada factura se despliega con sus
                    cuotas pendientes tildables (monto derivado, no editable) o un campo de monto manual si
                    no tiene plan. Pie fijo: Asignado / Monto del movimiento / Sin asignar (10.185). */}
                {!esCobroExportacion && facturasSeleccionadas.length > 0 && (
                  <div
                    style={{
                      marginTop: 14,
                      padding: 10,
                      borderRadius: 4,
                      background: cuadreSII.cuadra || excesoLegitimoSII ? "#e8f5e9" : "#fff3cd",
                      border: `1px solid ${cuadreSII.cuadra || excesoLegitimoSII ? "#a5d6a7" : "#f0d878"}`,
                    }}
                  >
                    <div style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 8 }}>
                      {facturasSeleccionadas.length === 1
                        ? "Factura seleccionada"
                        : `${facturasSeleccionadas.length} facturas seleccionadas`}
                    </div>
                    {facturasSeleccionadas.map((f) => {
                      const nombre = esCobro ? f.razon_social_receptor : f.razon_social_emisor;
                      const monedaFactura = esCobro ? f.moneda || moneda : pais === "Perú" ? "PEN" : "CLP";
                      const planF = planPorFactura[f.id] || [];
                      const tienePlan = planF.length > 0;
                      const cuotasSel = f.cuotasSeleccionadas || [];
                      const aplicado = montoAplicadoFactura(f);
                      return (
                        <div key={f.id} style={{ borderTop: "1px solid #e0e0e0", padding: "6px 0" }}>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              fontSize: "0.82rem",
                              gap: 8,
                            }}
                          >
                            <span>
                              {`${f.fecha_emision.substring(8, 10)}/${f.fecha_emision.substring(5, 7)}`} | {nombre}{" "}
                              Folio {f.folio}
                            </span>
                            <span style={{ display: "flex", gap: 8, alignItems: "center" }}>
                              {tienePlan ? (
                                <span style={{ fontWeight: 600 }} title="Monto derivado de las cuotas tildadas">
                                  {formatAmount(aplicado, monedaFactura)}
                                </span>
                              ) : (
                                <>
                                  <input
                                    type="number"
                                    value={f.montoAsignado != null ? f.montoAsignado : Number(f.monto_total || 0)}
                                    onChange={(e) => handleMontoAsignado(f.id, e.target.value)}
                                    min={0}
                                    max={Number(f.monto_total || 0)}
                                    style={{
                                      width: 120,
                                      textAlign: "right",
                                      fontSize: "0.8rem",
                                      padding: "2px 6px",
                                      border: "1px solid #bbb",
                                      borderRadius: 3,
                                    }}
                                    title="Monto a aplicar a esta factura (≤ su saldo)"
                                  />
                                  <span style={{ color: "#999", fontSize: "0.72rem", whiteSpace: "nowrap" }}>
                                    / {formatAmount(Number(f.monto_total || 0), monedaFactura)}
                                  </span>
                                </>
                              )}
                              <button
                                type="button"
                                onClick={() => handleSeleccionarFactura(f)}
                                style={{
                                  border: "none",
                                  background: "transparent",
                                  color: "#c0392b",
                                  cursor: "pointer",
                                  fontSize: "1rem",
                                  padding: "0 4px",
                                }}
                                title="Quitar de la selección"
                              >
                                ×
                              </button>
                            </span>
                          </div>
                          {tienePlan && (
                            <div style={{ marginTop: 4, paddingLeft: 8 }}>
                              {planF.map((c) => {
                                const checked = cuotasSel.some((x) => x.id === c.id);
                                return (
                                  <label
                                    key={c.id}
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                      fontSize: "0.8rem",
                                      padding: "3px 6px",
                                      marginBottom: 2,
                                      borderRadius: 3,
                                      cursor: "pointer",
                                      background: checked ? "#e8f4fd" : "transparent",
                                      border: `1px solid ${checked ? "#8ec9f0" : "transparent"}`,
                                    }}
                                  >
                                    <span>
                                      <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={() => handleToggleCuota(f.id, c)}
                                        style={{ marginRight: 6 }}
                                      />
                                      Cuota {c.numero_cuota} · vence {c.fecha_vencimiento}
                                      {c._aplicado > 0.01 && (
                                        <span style={{ color: "#e67e22", fontStyle: "italic" }}>
                                          {" "}
                                          · parcial (de {formatAmount(c._montoOriginal, monedaFactura)} ya se aplicó{" "}
                                          {formatAmount(c._aplicado, monedaFactura)})
                                        </span>
                                      )}
                                    </span>
                                    <span style={{ fontWeight: 600 }}>
                                      {formatAmount(c.monto_cuota, monedaFactura)}
                                    </span>
                                  </label>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    <div
                      style={{
                        borderTop: "1px solid #ccc",
                        paddingTop: 6,
                        marginTop: 6,
                        fontSize: "0.82rem",
                        display: "grid",
                        gridTemplateColumns: "1fr auto",
                        gap: "2px 12px",
                      }}
                    >
                      <span>Asignado</span>
                      <span style={{ textAlign: "right", fontWeight: 600 }}>
                        {formatAmount(cuadreSII.asignado, cuadreSII.moneda)}
                      </span>
                      <span>Monto del movimiento</span>
                      <span style={{ textAlign: "right", fontWeight: 600 }}>
                        {formatAmount(cuadreSII.montoMovCmp, cuadreSII.moneda)}
                      </span>
                      <span style={{ fontWeight: 600 }}>Sin asignar</span>
                      <span
                        style={{
                          textAlign: "right",
                          fontWeight: 700,
                          color: cuadreSII.cuadra || excesoLegitimoSII ? "#2e7d32" : "#c0392b",
                        }}
                      >
                        {cuadreSII.cuadra
                          ? "✓ 0"
                          : excesoLegitimoSII
                            ? `→ propina/OUR ${formatAmount(cuadreSII.sinAsignar, cuadreSII.moneda)}`
                            : formatAmount(cuadreSII.sinAsignar, cuadreSII.moneda)}
                      </span>
                    </div>
                  </div>
                )}

                {/* Boletas de honorarios — resumen multi-select (Σ montoAsignado vs débito). Cada boleta
                    lleva su monto editable (sin plan de cuotas). El cuadre reusa evaluarCuadreAplicaciones
                    (mismo motor que el SII). Excluyente con el panel de facturas. */}
                {!esCobro && boletasSeleccionadas.length > 0 && (
                  <div
                    style={{
                      marginTop: 14,
                      padding: 10,
                      borderRadius: 4,
                      background: cuadreBoletas.cuadra ? "#e8f5e9" : "#fff3cd",
                      border: `1px solid ${cuadreBoletas.cuadra ? "#a5d6a7" : "#f0d878"}`,
                    }}
                  >
                    <div style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 8 }}>
                      {boletasSeleccionadas.length === 1
                        ? "Boleta de honorarios seleccionada"
                        : `${boletasSeleccionadas.length} boletas de honorarios seleccionadas`}
                    </div>
                    {boletasSeleccionadas.map((b) => {
                      const monedaBoleta = pais === "Perú" ? "PEN" : "CLP";
                      const saldo = Number(b._saldoPendiente != null ? b._saldoPendiente : b.monto_bruto || 0);
                      // B2: espejo del render SII. Con plan → checkboxes de cuota (monto derivado); sin
                      // plan → input de monto libre (comportamiento previo).
                      const planB = planPorBoleta[b.id] || [];
                      const tienePlan = planB.length > 0;
                      const cuotasSel = b.cuotasSeleccionadas || [];
                      const aplicado = montoAplicadoFactura(b);
                      return (
                        <div key={b.id} style={{ borderTop: "1px solid #e0e0e0", padding: "6px 0" }}>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              fontSize: "0.82rem",
                              gap: 8,
                            }}
                          >
                            <span>
                              {`${b.fecha_emision.substring(8, 10)}/${b.fecha_emision.substring(5, 7)}`} |{" "}
                              {b.emisor_nombre} Boleta N°{b.folio}
                            </span>
                            <span style={{ display: "flex", gap: 8, alignItems: "center" }}>
                              {tienePlan ? (
                                <span style={{ fontWeight: 600 }} title="Monto derivado de las cuotas tildadas">
                                  {formatAmount(aplicado, monedaBoleta)}
                                </span>
                              ) : (
                                <>
                                  <input
                                    type="number"
                                    value={b.montoAsignado != null ? b.montoAsignado : saldo}
                                    onChange={(e) => handleMontoAsignadoBoleta(b.id, e.target.value)}
                                    min={0}
                                    max={saldo}
                                    style={{
                                      width: 120,
                                      textAlign: "right",
                                      fontSize: "0.8rem",
                                      padding: "2px 6px",
                                      border: "1px solid #bbb",
                                      borderRadius: 3,
                                    }}
                                    title="Monto a aplicar a esta boleta (≤ su saldo)"
                                  />
                                  <span style={{ color: "#999", fontSize: "0.72rem", whiteSpace: "nowrap" }}>
                                    / {formatAmount(saldo, monedaBoleta)}
                                  </span>
                                </>
                              )}
                              <button
                                type="button"
                                onClick={() => handleSeleccionarBoleta(b)}
                                style={{
                                  border: "none",
                                  background: "transparent",
                                  color: "#c0392b",
                                  cursor: "pointer",
                                  fontSize: "1rem",
                                  padding: "0 4px",
                                }}
                                title="Quitar de la selección"
                              >
                                ×
                              </button>
                            </span>
                          </div>
                          {tienePlan && (
                            <div style={{ marginTop: 4, paddingLeft: 8 }}>
                              {planB.map((c) => {
                                const checked = cuotasSel.some((x) => x.id === c.id);
                                return (
                                  <label
                                    key={c.id}
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                      fontSize: "0.8rem",
                                      padding: "3px 6px",
                                      marginBottom: 2,
                                      borderRadius: 3,
                                      cursor: "pointer",
                                      background: checked ? "#e8f4fd" : "transparent",
                                      border: `1px solid ${checked ? "#8ec9f0" : "transparent"}`,
                                    }}
                                  >
                                    <span>
                                      <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={() => handleToggleCuotaBoleta(b.id, c)}
                                        style={{ marginRight: 6 }}
                                      />
                                      Cuota {c.numero_cuota} · vence {c.fecha_vencimiento}
                                      {c._aplicado > 0.01 && (
                                        <span style={{ color: "#e67e22", fontStyle: "italic" }}>
                                          {" "}
                                          · parcial (de {formatAmount(c._montoOriginal, monedaBoleta)} ya se aplicó{" "}
                                          {formatAmount(c._aplicado, monedaBoleta)})
                                        </span>
                                      )}
                                    </span>
                                    <span style={{ fontWeight: 600 }}>{formatAmount(c.monto_cuota, monedaBoleta)}</span>
                                  </label>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    <div
                      style={{
                        borderTop: "1px solid #ccc",
                        paddingTop: 6,
                        marginTop: 6,
                        fontSize: "0.82rem",
                        display: "grid",
                        gridTemplateColumns: "1fr auto",
                        gap: "2px 12px",
                      }}
                    >
                      <span>Asignado</span>
                      <span style={{ textAlign: "right", fontWeight: 600 }}>
                        {formatAmount(cuadreBoletas.asignado, cuadreBoletas.moneda)}
                      </span>
                      <span>Monto del movimiento</span>
                      <span style={{ textAlign: "right", fontWeight: 600 }}>
                        {formatAmount(cuadreBoletas.montoMovCmp, cuadreBoletas.moneda)}
                      </span>
                      <span style={{ fontWeight: 600 }}>Sin asignar</span>
                      <span
                        style={{
                          textAlign: "right",
                          fontWeight: 700,
                          color: cuadreBoletas.cuadra ? "#2e7d32" : "#c0392b",
                        }}
                      >
                        {cuadreBoletas.cuadra ? "✓ 0" : formatAmount(cuadreBoletas.sinAsignar, cuadreBoletas.moneda)}
                      </span>
                    </div>
                  </div>
                )}

                {necesitaSelectorEntidad && (
                  <div
                    style={{
                      marginTop: 14,
                      padding: 10,
                      borderRadius: 4,
                      background: "#fff3cd",
                      border: "1px solid #f0d878",
                    }}
                  >
                    <div style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 6 }}>
                      {boletaSeleccionada
                        ? "Esta boleta de honorarios no tiene proveedor asociado — seleccioná uno:"
                        : `La factura no tiene ${esCobro ? "cliente" : "proveedor"} asociado — seleccioná uno:`}
                    </div>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder={`Buscar ${esCobro ? "cliente" : "proveedor"}...`}
                      value={entidadFallbackBusqueda}
                      onChange={(e) => setEntidadFallbackBusqueda(e.target.value)}
                      style={{ marginBottom: 6, fontSize: "0.82rem" }}
                    />
                    <div
                      style={{
                        maxHeight: 160,
                        overflowY: "auto",
                        border: "1px solid #dee2e6",
                        borderRadius: 4,
                        background: "#fff",
                      }}
                    >
                      {entidadFallbackOptions
                        .filter((opt) => {
                          if (!entidadFallbackBusqueda) return true;
                          const t = entidadFallbackBusqueda.toLowerCase();
                          const nombre = (esCobro ? opt.empresa || opt.nombre : opt.nombre) || "";
                          // RUT: match por clave NORMALIZADA (10.7), no por string crudo — los formatos en
                          // DB son heterogéneos (puntos, guión ASCII/Unicode). Así "20054413" encuentra
                          // "20.054.413-7". El match por nombre sigue por substring visible.
                          const rutNorm = normalizarRut(opt.rut || opt.rut_ruc || "");
                          const queryRutNorm = normalizarRut(entidadFallbackBusqueda);
                          return nombre.toLowerCase().includes(t) || (!!queryRutNorm && rutNorm.includes(queryRutNorm));
                        })
                        .slice(0, 100)
                        .map((opt) => {
                          const sel = entidadFallbackId === opt.id;
                          const nombre = esCobro ? opt.empresa || opt.nombre : opt.nombre;
                          return (
                            <div
                              key={opt.id}
                              onClick={() => {
                                setEntidadFallbackId(opt.id);
                                setEntidadFallbackCreada(false); // seleccionado de la lista → asignado
                              }}
                              style={{
                                padding: "6px 12px",
                                cursor: "pointer",
                                fontSize: "0.82rem",
                                borderBottom: "1px solid #f0f0f0",
                                background: sel ? "#e8f4fd" : "transparent",
                                fontWeight: sel ? 600 : 400,
                              }}
                            >
                              {nombre}
                              {(opt.rut || opt.rut_ruc) && (
                                <span style={{ marginLeft: 8, fontSize: "0.72rem", color: "#888" }}>
                                  {opt.rut || opt.rut_ruc}
                                </span>
                              )}
                            </div>
                          );
                        })}
                    </div>

                    {!esCobro &&
                      (!creandoProvSII ? (
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => {
                            // Precarga: de la boleta (emisor_nombre/emisor_rut) o de la factura SII
                            // (razon_social_emisor/rut_emisor), según qué documento esté seleccionado.
                            setProvSIINombre(
                              (boletaSeleccionada
                                ? boletaSeleccionada.emisor_nombre
                                : facturaSeleccionada?.razon_social_emisor) || ""
                            );
                            setProvSIIRut(
                              (boletaSeleccionada ? boletaSeleccionada.emisor_rut : facturaSeleccionada?.rut_emisor) ||
                                ""
                            );
                            setCreandoProvSII(true);
                          }}
                          style={{ marginTop: 8, fontSize: "0.78rem" }}
                        >
                          + Nuevo proveedor
                        </Button>
                      ) : (
                        <div
                          style={{
                            marginTop: 8,
                            padding: 10,
                            border: "1px solid #dee2e6",
                            borderRadius: 4,
                            background: "#fafbfc",
                          }}
                        >
                          <div style={{ fontSize: "0.72rem", color: "#666", marginBottom: 6 }}>
                            Pre-cargado desde la {boletaSeleccionada ? "boleta de honorarios" : `factura ${enteFiscal}`}
                            . Verificá antes de crear.
                          </div>
                          <div style={{ display: "flex", gap: 8, marginBottom: 6 }}>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              placeholder="Nombre / razón social"
                              value={provSIINombre}
                              onChange={(e) => setProvSIINombre(e.target.value)}
                              style={{ flex: 2, fontSize: "0.82rem" }}
                            />
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              placeholder="RUT"
                              value={provSIIRut}
                              onChange={(e) => setProvSIIRut(e.target.value)}
                              style={{ flex: 1, fontSize: "0.82rem" }}
                            />
                          </div>
                          <div style={{ display: "flex", gap: 6 }}>
                            <Button
                              size="sm"
                              variant="primary"
                              onClick={handleCrearProveedorDesdeSII}
                              disabled={!provSIINombre.trim()}
                              style={{ fontSize: "0.78rem" }}
                            >
                              Crear y seleccionar
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-secondary"
                              onClick={() => {
                                setCreandoProvSII(false);
                                setProvSIINombre("");
                                setProvSIIRut("");
                              }}
                              style={{ fontSize: "0.78rem" }}
                            >
                              Cancelar
                            </Button>
                          </div>
                        </div>
                      ))}

                    {esCobro &&
                      (!creandoClienteSII ? (
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => {
                            setClienteSIIEmpresa(facturaSeleccionada?.razon_social_receptor || "");
                            setClienteSIIRut(facturaSeleccionada?.rut_receptor || "");
                            setCreandoClienteSII(true);
                          }}
                          style={{ marginTop: 8, fontSize: "0.78rem" }}
                        >
                          + Nuevo cliente
                        </Button>
                      ) : (
                        <div
                          style={{
                            marginTop: 8,
                            padding: 10,
                            border: "1px solid #dee2e6",
                            borderRadius: 4,
                            background: "#fafbfc",
                          }}
                        >
                          <div style={{ fontSize: "0.72rem", color: "#666", marginBottom: 6 }}>
                            Pre-cargado desde la factura {enteFiscal}. Verificá antes de crear.
                          </div>
                          <div style={{ display: "flex", gap: 8, marginBottom: 6 }}>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              placeholder="Razón social"
                              value={clienteSIIEmpresa}
                              onChange={(e) => setClienteSIIEmpresa(e.target.value)}
                              style={{ flex: 2, fontSize: "0.82rem" }}
                            />
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              placeholder="RUT"
                              value={clienteSIIRut}
                              onChange={(e) => setClienteSIIRut(e.target.value)}
                              style={{ flex: 1, fontSize: "0.82rem" }}
                            />
                          </div>
                          <div style={{ display: "flex", gap: 6 }}>
                            <Button
                              size="sm"
                              variant="primary"
                              onClick={handleCrearClienteDesdeSII}
                              disabled={!clienteSIIEmpresa.trim()}
                              style={{ fontSize: "0.78rem" }}
                            >
                              Crear y seleccionar
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-secondary"
                              onClick={() => {
                                setCreandoClienteSII(false);
                                setClienteSIIEmpresa("");
                                setClienteSIIRut("");
                              }}
                              style={{ fontSize: "0.78rem" }}
                            >
                              Cancelar
                            </Button>
                          </div>
                        </div>
                      ))}
                  </div>
                )}

                {/* El plan de cuotas por factura se renderiza dentro del bloque unificado de arriba
                    (una fila tildable por cuota pendiente). El cuadre y su descuadre viven en el pie
                    (Sin asignar) — sin aviso inmediato al tildar (10.177: el número está siempre visible). */}

                {/* Comisión transferencia internacional: desglose de Gastos OUR (sin DTE). El débito
                    bancario supera la factura SII porque el cobro del banco corresponsal no lleva IVA
                    ni entra al DTE. Panel informativo, no bloqueante. */}
                {mostrarDesgloseOUR && (
                  <div
                    style={{
                      marginTop: 14,
                      padding: 12,
                      border: "1px solid #198754",
                      borderRadius: 4,
                      background: "#f0f9f4",
                      fontSize: "0.82rem",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                      <span>
                        Factura {enteFiscal} Nº {facturaSeleccionada.folio}
                      </span>
                      <span style={{ fontWeight: 600 }}>{formatAmount(facturaSeleccionada.monto_total, moneda)}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                      <span>Gastos OUR (sin DTE)</span>
                      <span style={{ fontWeight: 600 }}>
                        {formatAmount(Math.abs(monto) - Number(facturaSeleccionada.monto_total || 0), moneda)}
                      </span>
                    </div>
                    <div style={{ borderTop: "1px solid #198754", margin: "6px 0" }} />
                    <div
                      style={{ display: "flex", justifyContent: "space-between", color: "#198754", fontWeight: 700 }}
                    >
                      <span>Débito bancario ✓</span>
                      <span>{formatAmount(Math.abs(monto), moneda)}</span>
                    </div>
                  </div>
                )}

                {/* Guardrails visuales (no bloquean): factura probablemente equivocada. */}
                {warnOURExcesivo && (
                  <div
                    style={{
                      marginTop: 8,
                      padding: "8px 12px",
                      border: "1px solid #e67e22",
                      borderRadius: 4,
                      background: "#fdf3e7",
                      color: "#b35900",
                      fontSize: "0.8rem",
                    }}
                  >
                    ⚠ Gastos OUR parecen excesivos ({Math.round(ourPorcentaje * 100)}%) — ¿es la factura correcta?
                  </div>
                )}
                {warnFechaDesalineada && (
                  <div
                    style={{
                      marginTop: 8,
                      padding: "8px 12px",
                      border: "1px solid #e67e22",
                      borderRadius: 4,
                      background: "#fdf3e7",
                      color: "#b35900",
                      fontSize: "0.8rem",
                    }}
                  >
                    ⚠ La factura es del {facturaSeleccionada.fecha_emision} y el débito del {movimiento.fecha} —
                    ¿corresponden?
                  </div>
                )}

                {/* Propina de restorán (sin DTE): toggle para marcar que el exceso del débito sobre la
                    factura SII es propina. Solo visible cuando hay sobrepago real y NO es OUR (rama
                    paralela e independiente, mutuamente excluyente con OUR). */}
                {puedeMarcarPropina && (
                  <label
                    style={{
                      marginTop: 14,
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      fontSize: "0.82rem",
                      cursor: "pointer",
                    }}
                  >
                    <input type="checkbox" checked={esPropina} onChange={(e) => setEsPropina(e.target.checked)} />
                    <span>El excedente es propina (sin factura)</span>
                  </label>
                )}

                {/* Desglose propina (sin DTE): mismo formato que el panel de OUR, panel propio. El débito
                    bancario supera la factura SII por la propina, que no genera documento. No bloqueante. */}
                {mostrarDesglosePropina && (
                  <div
                    style={{
                      marginTop: 14,
                      padding: 12,
                      border: "1px solid #198754",
                      borderRadius: 4,
                      background: "#f0f9f4",
                      fontSize: "0.82rem",
                    }}
                  >
                    {/* Lista TODAS las facturas seleccionadas (multi-select); con 1 factura, es una sola fila. */}
                    {facturasSeleccionadas.map((f) => (
                      <div key={f.id} style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                        <span>
                          Factura {enteFiscal} Nº {f.folio}
                        </span>
                        <span style={{ fontWeight: 600 }}>{formatAmount(f.monto_total, moneda)}</span>
                      </div>
                    ))}
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                      <span>Propina (sin DTE)</span>
                      <span style={{ fontWeight: 600 }}>{formatAmount(montoPropina, moneda)}</span>
                    </div>
                    <div style={{ borderTop: "1px solid #198754", margin: "6px 0" }} />
                    <div
                      style={{ display: "flex", justifyContent: "space-between", color: "#198754", fontWeight: 700 }}
                    >
                      <span>Débito bancario ✓</span>
                      <span>{formatAmount(Math.abs(monto), moneda)}</span>
                    </div>
                  </div>
                )}

                {/* Forzar: aparece SOLO tras intentar confirmar con saldo sin asignar (intentoForzarConfirm),
                    y solo gerente (1 CHI / 4 PER). Con cuotas tildadas salda lo tildado; sin cuotas y con
                    faltante (Σ facturas > débito) cierra las facturas al 100% y registra el faltante. */}
                {puedeForzar && (
                  <label
                    style={{
                      marginTop: 14,
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      fontSize: "0.82rem",
                      cursor: "pointer",
                    }}
                  >
                    <input type="checkbox" checked={esForzado} onChange={(e) => setEsForzado(e.target.checked)} />
                    <span>
                      {hayCuotasSel
                        ? "Forzar vinculación (descuadre de cuotas)"
                        : cuadreSII.sinAsignar < 0
                          ? "Forzar vinculación (faltante)"
                          : "Forzar vinculación (sobrante sin asignar)"}
                    </span>
                  </label>
                )}

                {puedeForzar && esForzado && (
                  <div
                    style={{
                      marginTop: 14,
                      padding: 12,
                      border: "1px solid #e67e22",
                      borderRadius: 4,
                      background: "#fdf3e7",
                      fontSize: "0.82rem",
                      color: "#b35900",
                    }}
                  >
                    {hayCuotasSel ? (
                      <div>
                        Se vincularán las cuotas tildadas pese al descuadre de{" "}
                        {formatAmount(Math.abs(cuadreSII.sinAsignar), cuadreSII.moneda)} con el débito. Solo salda lo
                        tildado; no cierra la factura al 100% ni registra faltante.
                      </div>
                    ) : cuadreSII.sinAsignar < 0 ? (
                      <div>
                        Se cerrarán las {facturasSeleccionadas.length} factura(s) al 100%. El faltante de{" "}
                        {formatAmount(montoFaltante, moneda)} quedará registrado como forzado (pendiente de
                        regularizar).
                      </div>
                    ) : (
                      <div>
                        El débito supera lo asignado por{" "}
                        {formatAmount(Math.abs(cuadreSII.sinAsignar), cuadreSII.moneda)}. Se vinculará igual; el
                        sobrante queda sin imputar a factura.
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {esPagoExterna && (
              <>
                {/* Sub-toggle: vincular factura externa ya cargada vs cargar una nueva. */}
                <div style={{ display: "flex", gap: 8, marginBottom: 16, width: "fit-content" }}>
                  {[
                    { key: "existente", label: "Vincular existente" },
                    { key: "nueva", label: "Crear nueva" },
                  ].map((m) => {
                    const deshabilitado = false;
                    return (
                      <button
                        key={m.key}
                        type="button"
                        onClick={() => setModoExterno(m.key)}
                        disabled={deshabilitado}
                        style={{
                          width: 140,
                          padding: "6px 0",
                          fontSize: "0.82rem",
                          fontWeight: 600,
                          border: "1px solid #dee2e6",
                          borderRadius: 6,
                          cursor: deshabilitado ? "not-allowed" : "pointer",
                          background: modoExterno === m.key ? "#2c3e50" : "transparent",
                          color: modoExterno === m.key ? "#fff" : "#666",
                          opacity: deshabilitado ? 0.4 : 1,
                        }}
                      >
                        {m.label}
                      </button>
                    );
                  })}
                </div>

                {modoExterno === "existente" ? (
                  facturasExtPendientes.length === 0 ? (
                    <div style={{ padding: 20, textAlign: "center", color: "#999", fontSize: "0.85rem" }}>
                      No hay facturas externas pendientes de vincular
                    </div>
                  ) : (
                    <>
                      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10 }}>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="Buscar por proveedor, doc, concepto o monto..."
                          value={busquedaExt}
                          onChange={(e) => setBusquedaExt(e.target.value)}
                          style={{ flex: 3 }}
                        />
                        <label
                          style={{
                            flex: 1,
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            cursor: "pointer",
                            whiteSpace: "nowrap",
                            fontSize: 13,
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={mostrarDteAnioAnterior}
                            onChange={(e) => setMostrarDteAnioAnterior(e.target.checked)}
                          />
                          Mostrar DTE AA
                        </label>
                      </div>

                      <div style={{ maxHeight: 420, overflowY: "auto", border: "1px solid #dee2e6", borderRadius: 4 }}>
                        {extFiltradas.length === 0 ? (
                          <div style={{ padding: 20, textAlign: "center", color: "#999" }}>Sin resultados</div>
                        ) : (
                          extFiltradas.map((f) => {
                            const seleccionada = externasSeleccionadas.some((x) => x.id === f.id);
                            const label = f.concepto || f.numero_documento || `Doc ${f.id}`;
                            return (
                              <div
                                key={f.id}
                                onClick={() => handleSeleccionarExterna(f)}
                                style={{
                                  padding: "8px 12px",
                                  cursor: "pointer",
                                  fontSize: "0.83rem",
                                  borderBottom: "1px solid #f0f0f0",
                                  background: seleccionada ? "#e8f4fd" : "transparent",
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                }}
                              >
                                <div>
                                  <span>{`${f.fecha_emision.substring(8, 10)}/${f.fecha_emision.substring(5, 7)}`}</span>
                                  <span style={{ margin: "0 10px" }}>|</span>
                                  <span>{f.proveedor_nombre}</span>
                                  <span style={{ margin: "0 10px", color: "#999" }}>|</span>
                                  <span>{label}</span>
                                  {f._parcial && (
                                    <span
                                      style={{ marginLeft: 8, fontSize: "0.7rem", color: "#e67e22", fontWeight: 600 }}
                                    >
                                      (saldo parcial)
                                    </span>
                                  )}
                                </div>
                                <div style={{ color: "#c0392b", fontWeight: 600 }}>
                                  {formatAmount(f.saldoDisplay, f.moneda || moneda)}
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>

                      {extCrossCurrency && externasSeleccionadas.length > 0 && (
                        <div
                          style={{
                            marginTop: 10,
                            padding: "6px 10px",
                            fontSize: "0.78rem",
                            color: extBypassManual ? "#856404" : extCuadra ? "#198754" : "#c62828",
                            background: extBypassManual ? "#fff3cd" : extCuadra ? "#e8f5e9" : "#fdecea",
                            border: `1px solid ${extBypassManual ? "#f0d878" : extCuadra ? "#a5d6a7" : "#f5b7b1"}`,
                            borderRadius: 4,
                          }}
                        >
                          {avisoCrossCurrency}
                        </div>
                      )}

                      {externasSeleccionadas.length >= 2 && (
                        <div
                          style={{
                            marginTop: 14,
                            padding: 10,
                            borderRadius: 4,
                            background: extCuadra && !extBypassManual ? "#e8f5e9" : "#fff3cd",
                            border: `1px solid ${extCuadra && !extBypassManual ? "#a5d6a7" : "#f0d878"}`,
                          }}
                        >
                          <div style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 8 }}>
                            Selección múltiple ({externasSeleccionadas.length} facturas)
                          </div>
                          <div style={{ marginBottom: 8 }}>
                            {externasSeleccionadas.map((f) => {
                              const label = f.concepto || f.numero_documento || `Doc ${f.id}`;
                              return (
                                <div
                                  key={f.id}
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    fontSize: "0.82rem",
                                    padding: "3px 0",
                                  }}
                                >
                                  <span>
                                    {`${f.fecha_emision.substring(8, 10)}/${f.fecha_emision.substring(5, 7)}`} | {label}
                                  </span>
                                  <span style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                    <span>{formatAmount(f.monto_total, f.moneda || moneda)}</span>
                                    <button
                                      type="button"
                                      onClick={() => handleSeleccionarExterna(f)}
                                      style={{
                                        border: "none",
                                        background: "transparent",
                                        color: "#c0392b",
                                        cursor: "pointer",
                                        fontSize: "1rem",
                                        padding: "0 4px",
                                      }}
                                      title="Quitar de la selección"
                                    >
                                      ×
                                    </button>
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                          <div
                            style={{
                              borderTop: "1px solid #ccc",
                              paddingTop: 6,
                              fontSize: "0.82rem",
                              display: "grid",
                              gridTemplateColumns: "1fr auto",
                              gap: "2px 12px",
                            }}
                          >
                            <span>Suma seleccionada</span>
                            <span style={{ textAlign: "right", fontWeight: 600 }}>
                              {formatAmount(sumaExtSeleccionadas, monedaExtSeleccionada || moneda)}
                            </span>
                            {puedeConvertirCrossCurrency && (
                              <>
                                <span>Suma convertida (TC {tcLabelExt})</span>
                                <span style={{ textAlign: "right", fontWeight: 600 }}>
                                  {formatAmount(sumaExtConvertida, moneda)}
                                </span>
                              </>
                            )}
                            <span>Monto del movimiento</span>
                            <span style={{ textAlign: "right", fontWeight: 600 }}>{formatAmount(monto, moneda)}</span>
                            <span style={{ fontWeight: 600 }}>Diferencia</span>
                            <span
                              style={{
                                textAlign: "right",
                                fontWeight: 700,
                                color: extBypassManual ? "#856404" : extCuadra ? "#2e7d32" : "#c0392b",
                              }}
                            >
                              {extBypassManual
                                ? "cuadre manual"
                                : extCuadra && !extCrossCurrency
                                  ? "✓ exacta"
                                  : `${(diferenciaExt * 100).toFixed(2)}%`}
                            </span>
                          </div>
                        </div>
                      )}
                    </>
                  )
                ) : (
                  <FacturaExternaForm form={facturaExt} pais={pais} proveedores={proveedores} />
                )}
              </>
            )}

            {esPagoSinFactura && (
              <>
                {sfProveedorEsExtranjero ? (
                  <div
                    style={{
                      fontSize: "0.8rem",
                      color: "#842029",
                      background: "#f8d7da",
                      border: "1px solid #f5c2c7",
                      borderRadius: 4,
                      padding: "10px 12px",
                      marginBottom: 12,
                    }}
                  >
                    ⚠️ <strong>{sfProveedorNombre || "Este proveedor"}</strong> emite factura — no se puede registrar
                    como “Sin factura”. Cargala en la pestaña <strong>“Factura externa”</strong>.
                  </div>
                ) : (
                  <div
                    style={{
                      fontSize: "0.75rem",
                      color: "#666",
                      background: "#f8f9fa",
                      borderRadius: 4,
                      padding: "8px 10px",
                      marginBottom: 12,
                    }}
                  >
                    Para gastos operativos legítimos sin DTE (café con cliente, taxi, propinas, etc.). Se registra en{" "}
                    <code>movimientos_caja</code> con <code>categoria_pago=&quot;gasto_sin_factura&quot;</code>; sin IVA
                    recuperable.
                  </div>
                )}

                <ProveedorSelector
                  label="Proveedor (opcional)"
                  value={sfProveedorId}
                  onChange={(p) => {
                    setSfProveedorId(p.id);
                    setSfProveedorNombre(p.nombre);
                    if (p.cuenta_er_default && !sfCuentaER) setSfCuentaER(p.cuenta_er_default);
                  }}
                  filter={(p) => p.tipo === "comercial" || p.tipo === "interno"}
                  proveedores={proveedores}
                />

                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 4 }}>
                    Cuenta Estado de Resultados
                  </div>
                  <select
                    className="form-select form-select-sm"
                    value={sfCuentaER}
                    onChange={(e) => setSfCuentaER(e.target.value)}
                    style={{ fontSize: "0.82rem" }}
                  >
                    <option value="">Seleccionar cuenta…</option>
                    {["Costos Fijos", "Costos Variables"].map((grupo) => (
                      <optgroup key={grupo} label={grupo}>
                        {CUENTAS_ER.filter((c) => c.grupo === grupo).map((c) => (
                          <option key={c.value} value={c.value}>
                            {c.label}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </div>

                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 4 }}>Concepto</div>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Ej: Café con cliente DIDI / Taxi a reunión Latam"
                    value={sfConcepto}
                    onChange={(e) => setSfConcepto(e.target.value)}
                    style={{ fontSize: "0.82rem" }}
                  />
                </div>
              </>
            )}

            {esPagoACuenta && (
              <>
                <div
                  style={{
                    fontSize: "0.75rem",
                    color: "#666",
                    background: "#f8f9fa",
                    borderRadius: 4,
                    padding: "8px 10px",
                    marginBottom: 12,
                  }}
                >
                  Movimiento a cuenta corriente del proveedor (anticipo, retiro, devolución). No es un gasto operativo —
                  afecta solo el saldo del proveedor, no el Estado de Resultados.
                </div>

                <ProveedorSelector
                  value={acProveedorId}
                  onChange={(p) => {
                    setAcProveedorId(p.id);
                    setAcProveedorNombre(p.nombre);
                  }}
                  filter={(p) => p.tipo === "comercial" || p.tipo === "interno"}
                  proveedores={proveedores}
                />

                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 4 }}>Nota (opcional)</div>
                  <textarea
                    className="form-control form-control-sm"
                    placeholder="Ej: Anticipo honorarios marzo, retiro contra cuenta personal…"
                    value={acNota}
                    onChange={(e) => setAcNota(e.target.value)}
                    style={{ fontSize: "0.82rem", minHeight: 60 }}
                  />
                </div>

                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: "0.8rem", fontWeight: 600, marginBottom: 6 }}>
                    ¿A qué cuenta corresponde este pago?
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    {["factura", "credito"].map((b) => (
                      <button
                        key={b}
                        type="button"
                        onClick={() => setAcBucket(b)}
                        style={{
                          padding: "6px 18px",
                          borderRadius: 4,
                          border: "1px solid #dee2e6",
                          background: acBucket === b ? "#2c3e50" : "#fff",
                          color: acBucket === b ? "#fff" : "#333",
                          fontWeight: 600,
                          fontSize: "0.8rem",
                          cursor: "pointer",
                        }}
                      >
                        {b === "factura" ? "Factura" : "Crédito"}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}

            {esCobroACuenta && (
              <>
                <div
                  style={{
                    fontSize: "0.75rem",
                    color: "#666",
                    background: "#f8f9fa",
                    borderRadius: 4,
                    padding: "8px 10px",
                    marginBottom: 12,
                  }}
                >
                  Cobro a cuenta corriente del proveedor (devolución, anticipo retornado, refacturación). No es un
                  ingreso operativo — aumenta el saldo a favor del proveedor en el Estado de Cuenta, no el Estado de
                  Resultados.
                </div>

                <ProveedorSelector
                  value={acCobroProveedorId}
                  onChange={(p) => {
                    setAcCobroProveedorId(p.id);
                    setAcCobroProveedorNombre(p.nombre);
                  }}
                  filter={(p) => p.tipo === "comercial" || p.tipo === "interno"}
                  proveedores={proveedores}
                />

                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 4 }}>Nota (opcional)</div>
                  <textarea
                    className="form-control form-control-sm"
                    placeholder="Ej: Devolución pago duplicado, anticipo no aplicado…"
                    value={acCobroNota}
                    onChange={(e) => setAcCobroNota(e.target.value)}
                    style={{ fontSize: "0.82rem", minHeight: 60 }}
                  />
                </div>

                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: "0.8rem", fontWeight: 600, marginBottom: 6 }}>
                    ¿A qué cuenta corresponde este cobro?
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    {["factura", "credito"].map((b) => (
                      <button
                        key={b}
                        type="button"
                        onClick={() => setAcCobroBucket(b)}
                        style={{
                          padding: "6px 18px",
                          borderRadius: 4,
                          border: "1px solid #dee2e6",
                          background: acCobroBucket === b ? "#2c3e50" : "#fff",
                          color: acCobroBucket === b ? "#fff" : "#333",
                          fontWeight: 600,
                          fontSize: "0.8rem",
                          cursor: "pointer",
                        }}
                      >
                        {b === "factura" ? "Factura" : "Crédito"}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}

            {esPagoRecurrente && (
              <>
                <div
                  style={{
                    fontSize: "0.75rem",
                    color: "#666",
                    background: "#f8f9fa",
                    borderRadius: 4,
                    padding: "8px 10px",
                    marginBottom: 12,
                  }}
                >
                  Para costos recurrentes que <strong>nunca facturan</strong> (alquiler de oficina, gastos comunes).
                  Marca la ocurrencia mensual como pagada, la vincula a este movimiento y la saca de Movimientos
                  Futuros. Se registra en <code>movimientos_caja</code> (<code>pago_proveedor</code>) con la cuenta de
                  ER que elijas.
                </div>

                <ProveedorSelector
                  value={recProveedorId}
                  onChange={(p) => {
                    setRecProveedorId(p.id);
                    setRecProveedorNombre(p.nombre);
                    setRecSeleccion(new Map());
                    setRecCuentaER(p.cuenta_er_default || "");
                  }}
                  filter={(p) => p.es_recurrente}
                  proveedores={proveedores}
                />

                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 4 }}>
                    Cuenta Estado de Resultados
                  </div>
                  <select
                    className="form-select form-select-sm"
                    value={recCuentaER}
                    onChange={(e) => setRecCuentaER(e.target.value)}
                    style={{ fontSize: "0.82rem" }}
                  >
                    <option value="">Seleccionar cuenta…</option>
                    {["Costos Fijos", "Costos Variables"].map((grupo) => (
                      <optgroup key={grupo} label={grupo}>
                        {CUENTAS_ER.filter((c) => c.grupo === grupo).map((c) => (
                          <option key={c.value} value={c.value}>
                            {c.label}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </div>

                {recProveedorId && (
                  <div style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 6 }}>
                      Ocurrencias pendientes {recLoadingOcs ? "· cargando…" : `· ${recOcurrencias.length}`}
                    </div>
                    {!recLoadingOcs && recOcurrencias.length === 0 && (
                      <div style={{ fontSize: "0.8rem", color: "#888", padding: "6px 0" }}>
                        No hay ocurrencias recurrentes pendientes para este proveedor (o ya están todas conciliadas).
                      </div>
                    )}
                    {recOcurrencias.map((o) => {
                      const key = keyOcurrencia(o);
                      const sel = recSeleccion.has(key);
                      return (
                        <div
                          key={key}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            padding: "6px 8px",
                            marginBottom: 4,
                            border: "1px solid #dee2e6",
                            borderRadius: 6,
                            background: sel ? "#eef2f7" : "#fff",
                          }}
                        >
                          <input type="checkbox" checked={sel} onChange={() => toggleOcurrenciaRecurrente(o)} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: "0.82rem", fontWeight: 600 }}>{o.concepto}</div>
                            <div style={{ fontSize: "0.72rem", color: "#888" }}>
                              {String(o.periodo_mes).padStart(2, "0")}/{o.periodo_año} · proyectado{" "}
                              {Number(o.monto).toLocaleString("es-CL")} {o.moneda}
                            </div>
                          </div>
                          {sel && (
                            <input
                              type="number"
                              className="form-control form-control-sm"
                              value={recSeleccion.get(key)}
                              onChange={(e) => setMontoOcurrenciaRecurrente(o, e.target.value)}
                              style={{ width: 130, fontSize: "0.82rem" }}
                              placeholder="Monto"
                            />
                          )}
                        </div>
                      );
                    })}
                    {recSeleccion.size > 0 && (
                      <div
                        style={{
                          fontSize: "0.78rem",
                          fontWeight: 600,
                          marginTop: 6,
                          color: recCuadra ? "#0f5132" : "#842029",
                        }}
                      >
                        {recCuadra
                          ? `✓ Cuadra: ${totalRecAsignado.toLocaleString("es-CL")} ${moneda}`
                          : `Falta asignar ${restanteRec.toLocaleString("es-CL")} ${moneda} (Σ debe igualar ${montoAbsMovimiento.toLocaleString("es-CL")})`}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {esPagoActivo && (
              <div style={{ marginBottom: 16 }}>
                {/* ── SECCIÓN A — Saldar proyecciones (CAJA PURA): cuotas de activo + componentes de lote,
                     MEZCLADAS. Multi-select: un pago (adelanto al proveedor) puede cubrir varias. Caja
                     CONSOLIDADA (1 fila, respeta la UNIQUE); el "quién pagó qué" vive en el
                     movimiento_bancario_id de cada target. Se OCULTA si ya hay un activo entero elegido en
                     la Sección B (excluyentes con ocultamiento). ── */}
                {!activoCapexSel && (
                  <>
                    <div style={{ fontSize: "0.85rem", fontWeight: 600, marginBottom: 8 }}>
                      Saldar proyecciones (cuotas, componentes y activos)
                    </div>
                    {capexItems.length === 0 ? (
                      <div style={{ color: "#999", fontSize: "0.85rem" }}>
                        No hay proyecciones CapEx pendientes en {pais}.
                      </div>
                    ) : (
                      <>
                        <BuscadorInput
                          value={capexBusqueda}
                          onChange={setCapexBusqueda}
                          placeholder="Buscar por concepto o monto..."
                        />
                        <div
                          style={{ maxHeight: 220, overflowY: "auto", border: "1px solid #dee2e6", borderRadius: 6 }}
                        >
                          {capexItems
                            .filter((it) =>
                              coincideBusqueda(capexBusqueda, { textos: [it.label], montos: [it.montoBruto] })
                            )
                            .map((it) => {
                              const sel = capexSel.includes(it.key);
                              return (
                                <label
                                  key={it.key}
                                  style={{
                                    display: "flex",
                                    gap: 8,
                                    alignItems: "center",
                                    padding: "8px 12px",
                                    borderBottom: "1px solid #f1f1f1",
                                    cursor: "pointer",
                                    background: sel ? "#eef5ff" : "transparent",
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={sel}
                                    onChange={() => {
                                      setCapexSel((prev) =>
                                        sel ? prev.filter((k) => k !== it.key) : [...prev, it.key]
                                      );
                                      // Al deseleccionar, limpiar su override de "monto a aplicar" (3b-i).
                                      if (sel) {
                                        setCapexMontos((prev) => {
                                          if (!(it.key in prev)) return prev;
                                          const next = { ...prev };
                                          delete next[it.key];
                                          return next;
                                        });
                                      } else {
                                        // Tildar caja-pura limpia el path activo-entero (excluyentes).
                                        setActivoCapexSel(null);
                                        setFacturaActivoSel(null);
                                      }
                                    }}
                                  />
                                  <span style={{ flex: 1, fontSize: "0.82rem" }}>
                                    <span
                                      style={{
                                        color: (CAPEX_ITEM_BADGE[it.tipo] || CAPEX_ITEM_BADGE.cuota).color,
                                        fontWeight: 600,
                                        fontSize: "0.68rem",
                                        marginRight: 6,
                                      }}
                                    >
                                      {(CAPEX_ITEM_BADGE[it.tipo] || CAPEX_ITEM_BADGE.cuota).label}
                                    </span>
                                    {it.label}
                                  </span>
                                  {/* Modo B (multi-ítem): "monto a aplicar" ≤ saldo (incluye FOB, 3b-ii). */}
                                  {sel && capexModoReparto && (
                                    <input
                                      type="text"
                                      inputMode="numeric"
                                      value={
                                        capexMontos[it.key] != null
                                          ? capexMontos[it.key]
                                          : Math.round(capexMontoAsignadoPorKey[it.key] ?? 0)
                                      }
                                      onClick={(e) => e.stopPropagation()}
                                      onChange={(e) => {
                                        e.stopPropagation();
                                        const v = e.target.value;
                                        setCapexMontos((prev) => ({ ...prev, [it.key]: v }));
                                      }}
                                      style={{
                                        width: 110,
                                        textAlign: "right",
                                        fontSize: "0.8rem",
                                        border: "1px solid #ced4da",
                                        borderRadius: 4,
                                        padding: "2px 6px",
                                      }}
                                      title={`Saldo pendiente: ${formatAmount(it._saldoPendiente, it.moneda)}`}
                                    />
                                  )}
                                  <span style={{ fontWeight: 600, fontSize: "0.82rem" }}>
                                    {formatAmount(it.montoBruto, it.moneda)}
                                  </span>
                                </label>
                              );
                            })}
                        </div>
                      </>
                    )}

                    {/* Totales/guard (con selección): Σ neto · IVA (automático desde exento_iva) · total bruto
                        vs banco; bloquea al 5%. Sin toggle manual: el bruto ya viene por ítem (CapEx-IVA F2). */}
                    {capexSel.length > 0 && (
                      <div style={{ marginTop: 12 }}>
                        <div
                          style={{
                            padding: "10px 12px",
                            borderRadius: 6,
                            background: bloqueaMontoCapex ? "#fdecea" : "#e8f5e9",
                            border: `1px solid ${bloqueaMontoCapex ? "#f5b7b1" : "#a5d6a7"}`,
                            fontSize: "0.8rem",
                          }}
                        >
                          <div style={{ display: "flex", justifyContent: "space-between" }}>
                            <span>Σ neto ({capexItemsSel.length})</span>
                            <span>{formatAmount(capexSumaNetoLocal, monedaLocalGuard)}</span>
                          </div>
                          {capexImpuestoLocal > 0 && (
                            <div style={{ display: "flex", justifyContent: "space-between" }}>
                              <span>{pais === "Chile" ? "IVA 19%" : "IGV 18%"}</span>
                              <span>{formatAmount(capexImpuestoLocal, monedaLocalGuard)}</span>
                            </div>
                          )}
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              fontWeight: 600,
                              borderTop: "1px solid rgba(0,0,0,0.1)",
                              marginTop: 4,
                              paddingTop: 4,
                            }}
                          >
                            <span>Total {capexImpuestoLocal > 0 ? "(con impuesto)" : "(neto)"}</span>
                            <span>{formatAmount(capexTotalLocal, monedaLocalGuard)}</span>
                          </div>
                          {/* Transparencia del TC (10.185): cada ítem no-local convertido por SU fecha. Deja
                              ver de dónde sale el total en moneda local (ej. transporte MINIX US$293 ≈ $X). */}
                          {capexItemsSel.some((it) => esMonedaNoLocalGuard(it.moneda)) && (
                            <div
                              style={{
                                marginTop: 4,
                                paddingTop: 4,
                                borderTop: "1px dashed rgba(0,0,0,0.12)",
                                color: "#666",
                                fontSize: "0.72rem",
                              }}
                            >
                              <div style={{ marginBottom: 2 }}>
                                Conversión a {monedaLocalGuard} (TC por fecha del ítem):
                              </div>
                              {capexItemsSel
                                .filter((it) => esMonedaNoLocalGuard(it.moneda))
                                .map((it) => (
                                  <div
                                    key={it.key}
                                    style={{ display: "flex", justifyContent: "space-between", gap: 8 }}
                                  >
                                    <span>
                                      {formatAmount(it.montoBruto, it.moneda)} · TC {it.fecha || "hoy"}
                                    </span>
                                    <span>
                                      ≈{" "}
                                      {formatAmount(
                                        convertirAMonedaLocal(it.montoBruto, it.moneda, pais, it.fecha),
                                        monedaLocalGuard
                                      )}
                                    </span>
                                  </div>
                                ))}
                            </div>
                          )}
                          <div style={{ display: "flex", justifyContent: "space-between", color: "#555" }}>
                            <span>Banco</span>
                            <span>{formatAmount(Math.abs(monto), moneda)}</span>
                          </div>
                          {bloqueaMontoCapex && guardMontoCapex?.mensaje && (
                            <div style={{ marginTop: 6, color: "#c0392b" }}>{guardMontoCapex.mensaje}</div>
                          )}
                        </div>
                        <div style={{ fontSize: "0.75rem", color: "#666", marginTop: 8 }}>
                          Al confirmar: los ítems salen de la proyección de CapEx. Es <strong>caja pura</strong> —
                          activos y lotes siguen planificados; capitalizan/deprecian aparte (factura / ejecución del
                          lote).
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* ── SECCIÓN B — Ejecutar un activo ENTERO contra su factura (DTE). Single, SIN cambios. Solo
                     lista activos sin plan (los que tienen plan aportan sus cuotas a la Sección A). Se OCULTA
                     si hay ítems tildados en la Sección A (excluyentes con ocultamiento). ── */}
                {capexSel.length === 0 && activosCapex.some((a) => !a._tienePlan) && (
                  <>
                    <div style={{ fontSize: "0.85rem", fontWeight: 600, margin: "18px 0 8px" }}>
                      …o ejecutá un activo entero contra su factura (DTE)
                    </div>
                    <BuscadorInput
                      value={capexActivoBusqueda}
                      onChange={setCapexActivoBusqueda}
                      placeholder="Buscar por edificio, tipo o monto..."
                    />
                    <div style={{ maxHeight: 220, overflowY: "auto", border: "1px solid #dee2e6", borderRadius: 6 }}>
                      {activosCapex
                        .filter((a) => !a._tienePlan)
                        .filter((a) =>
                          coincideBusqueda(capexActivoBusqueda, {
                            textos: [a.edificioNombre, a.tipo_activo, a.descripcion],
                            montos: [a.costo_unitario],
                          })
                        )
                        .map((a) => (
                          <label
                            key={a.id}
                            style={{
                              display: "flex",
                              gap: 8,
                              alignItems: "center",
                              padding: "8px 12px",
                              borderBottom: "1px solid #f1f1f1",
                              cursor: "pointer",
                              background: activoCapexSel === a.id ? "#eef5ff" : "transparent",
                            }}
                          >
                            <input
                              type="radio"
                              name="activoCapex"
                              checked={activoCapexSel === a.id}
                              onChange={() => {
                                setActivoCapexSel(a.id);
                                setCapexSel([]); // excluyente con la selección caja-pura de la Sección A
                                setCapexMontos({});
                              }}
                              onClick={() => {
                                // Click sobre el ya-elegido → deselecciona (reaparece la Sección A). Los
                                // radios no disparan onChange al reclickear el activo ya marcado, por eso
                                // el toggle va en onClick.
                                if (activoCapexSel === a.id) {
                                  setActivoCapexSel(null);
                                  setFacturaActivoSel(null);
                                }
                              }}
                            />
                            <span style={{ flex: 1, fontSize: "0.82rem" }}>
                              <strong>{a.edificioNombre}</strong> · {a.tipo_activo}
                              {a.descripcion ? ` — ${a.descripcion}` : ""}
                              {a.fecha_planificada ? ` · estimado ${a.fecha_planificada}` : ""}
                            </span>
                            <span style={{ fontWeight: 600, fontSize: "0.82rem" }}>
                              {formatAmount(
                                brutoCapex(a.costo_unitario, { exento_iva: a.exento_iva, moneda: a.moneda, pais }),
                                a.moneda
                              )}
                            </span>
                          </label>
                        ))}
                    </div>

                    {/* Activo elegido → ejecución entera contra el DTE (comportamiento existente). */}
                    {activoCapexSel && !activoTienePlan && (
                      <>
                        {/* Factura recibida (DTE) — reusa el listado de recibidas pendientes de "Pago a proveedor" */}
                        <div style={{ fontSize: "0.85rem", fontWeight: 600, margin: "16px 0 8px" }}>
                          Elegí la factura de compra (DTE)
                        </div>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="Buscar por nombre, folio o monto..."
                          value={busqueda}
                          onChange={(e) => setBusqueda(e.target.value)}
                          style={{ marginBottom: 8 }}
                        />
                        {facturasFiltradas.length === 0 ? (
                          <div style={{ color: "#999", fontSize: "0.85rem" }}>Sin facturas recibidas pendientes.</div>
                        ) : (
                          <div
                            style={{ maxHeight: 220, overflowY: "auto", border: "1px solid #dee2e6", borderRadius: 6 }}
                          >
                            {facturasFiltradas.map((f) => {
                              // Hint visual: monto de la factura coincide con el del activo elegido (tolerancia $1).
                              // Solo pista de color — NO auto-selecciona ni filtra. El azul (seleccionada) manda.
                              const coincideMonto =
                                montoActivoRef != null && Math.abs(Number(f.monto_total || 0) - montoActivoRef) < 1;
                              return (
                                <label
                                  key={f.id}
                                  style={{
                                    display: "flex",
                                    gap: 8,
                                    alignItems: "center",
                                    padding: "8px 12px",
                                    borderBottom: "1px solid #f1f1f1",
                                    cursor: "pointer",
                                    background:
                                      facturaActivoSel === f.id ? "#eef5ff" : coincideMonto ? "#e8f5e9" : "transparent",
                                  }}
                                >
                                  <input
                                    type="radio"
                                    name="facturaActivo"
                                    checked={facturaActivoSel === f.id}
                                    onChange={() => setFacturaActivoSel(f.id)}
                                  />
                                  <span style={{ flex: 1, fontSize: "0.82rem" }}>
                                    <strong>{f.razon_social_emisor}</strong> · Folio {f.folio}
                                    {f.fecha_emision ? ` · ${f.fecha_emision.substring(0, 10)}` : ""}
                                    {f._parcial && (
                                      <span
                                        style={{
                                          marginLeft: 6,
                                          color: "#e67e22",
                                          fontSize: "0.72rem",
                                          fontWeight: 600,
                                        }}
                                      >
                                        parcial
                                      </span>
                                    )}
                                  </span>
                                  <span style={{ fontWeight: 600, fontSize: "0.82rem" }}>
                                    {formatAmount(Number(f.monto_total), pais === "Perú" ? "PEN" : "CLP")}
                                  </span>
                                </label>
                              );
                            })}
                          </div>
                        )}

                        <div style={{ fontSize: "0.75rem", color: "#666", marginTop: 10 }}>
                          Al confirmar: el movimiento queda vinculado a la factura (sale de “Por pagar”) y el activo
                          pasa a “activo” con fecha de compra = fecha del DTE, arrancando su depreciación. No se crea un
                          activo nuevo.
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
            )}

            {esPagoInstitucional &&
              (() => {
                const inst = INSTITUCIONES.find((i) => i.value === piInstitucion);
                const requierePeriodo = !!inst?.requierePeriodo;
                return (
                  <>
                    <div
                      style={{
                        fontSize: "0.75rem",
                        color: "#666",
                        background: "#f8f9fa",
                        borderRadius: 4,
                        padding: "8px 10px",
                        marginBottom: 12,
                      }}
                    >
                      Para pagos a entidades sin DTE: PREVIRED, {enteFiscal}, Tesorería, comisiones bancarias, etc. Se
                      registra en <code>movimientos_caja</code> sin factura asociada.
                    </div>

                    <div style={{ marginBottom: 8 }}>
                      <div style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 4 }}>Institución</div>
                      <select
                        className="form-select form-select-sm"
                        value={piInstitucion}
                        onChange={(e) => setPiInstitucion(e.target.value)}
                        style={{ fontSize: "0.82rem" }}
                      >
                        <option value="">Seleccionar institución…</option>
                        {INSTITUCIONES.map((i) => (
                          <option key={i.value} value={i.value}>
                            {i.label.replace(/\bSII\b/g, enteFiscal)}
                          </option>
                        ))}
                      </select>
                    </div>

                    {requierePeriodo && (
                      <div style={{ marginBottom: 8 }}>
                        <div style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 4 }}>Período que cubre</div>
                        <div style={{ display: "flex", gap: 8 }}>
                          <select
                            className="form-select form-select-sm"
                            value={piPeriodoMes || ""}
                            onChange={(e) => setPiPeriodoMes(e.target.value ? Number(e.target.value) : null)}
                            style={{ flex: 2, fontSize: "0.82rem" }}
                          >
                            <option value="">Mes…</option>
                            {MESES.map((m, i) => (
                              <option key={i} value={i + 1}>
                                {m}
                              </option>
                            ))}
                          </select>
                          <select
                            className="form-select form-select-sm"
                            value={piPeriodoAño || ""}
                            onChange={(e) => setPiPeriodoAño(e.target.value ? Number(e.target.value) : null)}
                            style={{ flex: 1, fontSize: "0.82rem" }}
                          >
                            {añosPI.map((y) => (
                              <option key={y} value={y}>
                                {y}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}

                    <div style={{ marginBottom: 8 }}>
                      <div style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 4 }}>Notas (opcional)</div>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Ej: Folio 12345, ajuste retroactivo, etc."
                        value={piNotas}
                        onChange={(e) => setPiNotas(e.target.value)}
                        style={{ fontSize: "0.82rem" }}
                      />
                    </div>
                  </>
                );
              })()}

            {esPrestamo &&
              (() => {
                const hoyStr = new Date().toISOString().slice(0, 10);
                const fmtFecha = (s) => (s ? `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)}` : "—");
                const cuotasCred = creditoPrestamo?.cuotas || [];
                const montoMov = Math.abs(monto);
                const montoCuota = cuotaPrestamo ? Number(cuotaPrestamo.monto_total || 0) : null;
                const diff = montoCuota != null ? Math.abs(montoMov - montoCuota) : 0;
                const diffPct = montoCuota ? diff / montoCuota : 0;
                const monedaCred = creditoPrestamo?.moneda || moneda;
                return (
                  <>
                    <div
                      style={{
                        fontSize: "0.75rem",
                        color: "#666",
                        background: "#f8f9fa",
                        borderRadius: 4,
                        padding: "8px 10px",
                        marginBottom: 12,
                      }}
                    >
                      Vinculá este egreso a una cuota pendiente de un crédito. Se registra en{" "}
                      <code>movimientos_caja</code> como <code>pago_banco</code> y la cuota queda marcada como pagada.
                    </div>

                    <div style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 4 }}>Crédito</div>
                      <select
                        className="form-select form-select-sm"
                        value={creditoPrestamo?.id || ""}
                        onChange={(e) => {
                          const c = creditosDisponibles.find((x) => String(x.id) === e.target.value) || null;
                          setCreditoPrestamo(c);
                          setCuotaPrestamo(null);
                        }}
                        style={{ fontSize: "0.82rem" }}
                      >
                        <option value="">Seleccionar crédito…</option>
                        {creditosDisponibles.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.nombre} · {c.moneda} ({c.cuotas.length} pendientes)
                          </option>
                        ))}
                      </select>
                    </div>

                    {creditoPrestamo && (
                      <div>
                        <div style={{ fontSize: "0.78rem", fontWeight: 600, marginBottom: 4 }}>Cuota a pagar</div>
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.8rem" }}>
                          <thead>
                            <tr style={{ background: "#212529", color: "#fff" }}>
                              <th style={{ padding: "4px 8px", textAlign: "center" }}>N°</th>
                              <th style={{ padding: "4px 8px", textAlign: "center" }}>Vence</th>
                              <th style={{ padding: "4px 8px", textAlign: "right" }}>Monto</th>
                              <th style={{ padding: "4px 8px", width: 36 }} />
                            </tr>
                          </thead>
                          <tbody>
                            {cuotasCred.map((q) => {
                              const vencida = q.fecha_vencimiento < hoyStr;
                              const sel = cuotaPrestamo?.id === q.id;
                              return (
                                <tr
                                  key={q.id}
                                  onClick={() => setCuotaPrestamo(q)}
                                  style={{
                                    cursor: "pointer",
                                    background: sel ? "#e8f4fd" : "#fff",
                                    borderBottom: "1px solid #dee2e6",
                                  }}
                                >
                                  <td style={{ padding: "4px 8px", textAlign: "center" }}>{q.numero_cuota}</td>
                                  <td
                                    style={{
                                      padding: "4px 8px",
                                      textAlign: "center",
                                      color: vencida ? "#c0392b" : "inherit",
                                      fontWeight: vencida ? 700 : 400,
                                    }}
                                  >
                                    {fmtFecha(q.fecha_vencimiento)}
                                  </td>
                                  <td style={{ padding: "4px 8px", textAlign: "right" }}>
                                    {formatAmount(q.monto_total, monedaCred)}
                                  </td>
                                  <td style={{ padding: "4px 8px", textAlign: "center" }}>
                                    <input type="radio" checked={sel} onChange={() => setCuotaPrestamo(q)} />
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>

                        {cuotaPrestamo && diff >= 2 && (
                          <div
                            style={{
                              marginTop: 10,
                              fontSize: "0.76rem",
                              borderRadius: 4,
                              padding: "8px 10px",
                              background: diffPct >= 0.1 ? "#f8d7da" : "#fff3cd",
                              color: diffPct >= 0.1 ? "#842029" : "#856404",
                              border: `1px solid ${diffPct >= 0.1 ? "#f5c2c7" : "#ffecb5"}`,
                            }}
                          >
                            {diffPct >= 0.1
                              ? "Diferencia significativa: "
                              : "El monto del movimiento difiere de la cuota: "}
                            movimiento {formatAmount(montoMov, monedaCred)} vs cuota{" "}
                            {formatAmount(montoCuota, monedaCred)}.
                          </div>
                        )}
                      </div>
                    )}
                  </>
                );
              })()}

            {esPagoEdificio && (
              <>
                <div
                  style={{
                    fontSize: "0.75rem",
                    color: "#666",
                    background: "#f8f9fa",
                    borderRadius: 4,
                    padding: "8px 10px",
                    marginBottom: 12,
                  }}
                >
                  Repartí el monto del movimiento entre los edificios; la suma debe cuadrar exacto. El saldo pendiente
                  que se muestra al lado es de referencia (comisión generada − pagada).
                </div>

                {/* Selector + Agregar */}
                <div style={{ display: "flex", gap: 8, marginBottom: 10, alignItems: "center" }}>
                  <select
                    className="form-select form-select-sm"
                    value={edificioSelectorId}
                    onChange={(e) => setEdificioSelectorId(e.target.value)}
                    style={{ flex: 1, fontSize: "0.82rem" }}
                  >
                    <option value="">Seleccionar edificio…</option>
                    {edificiosSaldo
                      .filter((ed) => !edificiosAsignaciones.some((a) => a.edificioId === ed.edificioId))
                      .map((ed) => (
                        <option key={ed.edificioId} value={ed.edificioId}>
                          {ed.edificioNombre} — saldo {formatAmount(ed.saldoPendiente, ed.moneda || moneda)}
                        </option>
                      ))}
                  </select>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={agregarEdificioAsignacion}
                    disabled={!edificioSelectorId}
                  >
                    + Agregar
                  </Button>
                </div>

                {/* Filas de asignación por edificio */}
                {edificiosAsignaciones.length === 0 ? (
                  <div
                    style={{
                      padding: 16,
                      textAlign: "center",
                      color: "#999",
                      fontSize: "0.82rem",
                      border: "1px dashed #dee2e6",
                      borderRadius: 4,
                    }}
                  >
                    Agregá al menos un edificio para repartir el monto.
                  </div>
                ) : (
                  edificiosAsignaciones.map((a) => {
                    const ed = edificiosSaldo.find((e) => e.edificioId === a.edificioId);
                    const saldoRef = ed?.saldoPendiente || 0;
                    // Sanity-check blando: si lo asignado supera el saldo pendiente conocido (>0),
                    // se advierte pero NO se bloquea (sobrepagos legítimos: anticipos, cta. corriente).
                    const montoAsignado = Number(a.monto) || 0;
                    const excedeSaldo = saldoRef > 0 && montoAsignado > saldoRef;
                    return (
                      <div key={a.edificioId} style={{ marginBottom: 8 }}>
                        <div
                          style={{
                            display: "flex",
                            gap: 8,
                            alignItems: "center",
                            border: `1px solid ${excedeSaldo ? "#f0d878" : "#dee2e6"}`,
                            borderRadius: 4,
                            padding: 10,
                            background: excedeSaldo ? "#fff8e6" : "#fafbfc",
                          }}
                        >
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div
                              style={{
                                fontSize: "0.83rem",
                                fontWeight: 600,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {a.edificioNombre}
                            </div>
                            <div style={{ fontSize: "0.72rem", color: "#888" }}>
                              Saldo pendiente: {formatAmount(saldoRef, ed?.moneda || moneda)}
                            </div>
                          </div>
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            placeholder="Monto"
                            value={a.monto}
                            onChange={(e) => actualizarEdificioAsignacion(a.edificioId, e.target.value)}
                            style={{ flex: "0 0 140px", fontSize: "0.82rem", textAlign: "right" }}
                          />
                          <button
                            type="button"
                            onClick={() => quitarEdificioAsignacion(a.edificioId)}
                            style={{
                              border: "none",
                              background: "transparent",
                              color: "#c0392b",
                              cursor: "pointer",
                              fontSize: "1rem",
                              padding: "0 4px",
                            }}
                            title="Quitar edificio"
                          >
                            ×
                          </button>
                        </div>
                        {excedeSaldo && (
                          <div style={{ fontSize: "0.72rem", color: "#a06a00", padding: "3px 2px 0" }}>
                            ⚠ Asignado {formatAmount(montoAsignado, ed?.moneda || moneda)} supera el saldo pendiente{" "}
                            {formatAmount(saldoRef, ed?.moneda || moneda)} — ¿pago adelantado?
                          </div>
                        )}
                      </div>
                    );
                  })
                )}

                {/* Línea de cuadre: Asignado X / Total movimiento Y + restante */}
                <div
                  style={{
                    marginTop: 10,
                    padding: "8px 12px",
                    borderRadius: 4,
                    background: edificiosCuadra ? "#e8f5e9" : "#fdecea",
                    fontSize: "0.82rem",
                    display: "flex",
                    justifyContent: "space-between",
                    fontWeight: 600,
                  }}
                >
                  <span>
                    Asignado {formatAmount(totalEdificiosAsignado, moneda)} / {formatAmount(montoAbsMovimiento, moneda)}
                  </span>
                  <span>
                    {edificiosCuadra ? (
                      "✓ Cuadra"
                    ) : (
                      <span style={{ color: "#c0392b" }}>restante {formatAmount(restanteEdificios, moneda)}</span>
                    )}
                  </span>
                </div>

                {/* Acordeón: adjuntar factura de soporte (opcional). Cerrado por defecto.
                    La lista solo se renderiza si está abierto, y se acota al proveedor del edificio. */}
                <div style={{ marginTop: 14, borderTop: "1px solid #eee", paddingTop: 10 }}>
                  <button
                    type="button"
                    onClick={() => setMostrarFacturaEdificio((v) => !v)}
                    style={{
                      border: "none",
                      background: "transparent",
                      cursor: "pointer",
                      fontSize: "0.82rem",
                      fontWeight: 600,
                      color: "#2c3e50",
                      padding: 0,
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <span style={{ fontSize: "0.7rem" }}>{mostrarFacturaEdificio ? "▾" : "▸"}</span>
                    Adjuntar factura de soporte (opcional)
                    {facturaSeleccionada && (
                      <span style={{ color: "#198754", fontWeight: 500, fontSize: "0.78rem" }}>
                        — Folio {facturaSeleccionada.folio} adjunta
                      </span>
                    )}
                  </button>

                  {mostrarFacturaEdificio && (
                    <div style={{ marginTop: 10 }}>
                      {proveedorIdsEdificioSel.size === 0 ? (
                        <div style={{ padding: 12, textAlign: "center", color: "#999", fontSize: "0.8rem" }}>
                          Elegí un edificio con proveedor vinculado para ver sus facturas.
                        </div>
                      ) : (
                        <>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="Buscar por folio o monto..."
                            value={busqueda}
                            onChange={(e) => setBusqueda(e.target.value)}
                            style={{ marginBottom: 8, fontSize: "0.82rem" }}
                          />
                          <div
                            style={{
                              maxHeight: 200,
                              overflowY: "auto",
                              border: "1px solid #dee2e6",
                              borderRadius: 4,
                            }}
                          >
                            {facturasFiltradasEdificio.length === 0 ? (
                              <div style={{ padding: 16, textAlign: "center", color: "#999", fontSize: "0.8rem" }}>
                                Sin facturas del proveedor de este edificio
                              </div>
                            ) : (
                              facturasFiltradasEdificio.map((f) => {
                                const sel = facturaSeleccionada?.id === f.id;
                                return (
                                  <div
                                    key={f.id}
                                    onClick={() => setFacturaSeleccionada(sel ? null : f)}
                                    style={{
                                      padding: "8px 12px",
                                      cursor: "pointer",
                                      fontSize: "0.82rem",
                                      borderBottom: "1px solid #f0f0f0",
                                      background: sel ? "#e8f4fd" : "transparent",
                                      fontWeight: sel ? 600 : 400,
                                      display: "flex",
                                      justifyContent: "space-between",
                                      gap: 8,
                                    }}
                                  >
                                    <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                                      {sel ? "✓ " : ""}
                                      Folio {f.folio} — {f.razon_social_emisor}
                                    </span>
                                    <span style={{ whiteSpace: "nowrap", color: "#555" }}>
                                      {formatAmount(f.monto_total, pais === "Perú" ? "PEN" : "CLP")}
                                    </span>
                                  </div>
                                );
                              })
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}

            {esEmpleado && !yaEstaVinculado && (
              <div style={{ display: "flex", gap: 8, marginBottom: 16, width: "fit-content" }}>
                {[
                  { key: "remuneracion", label: "Remuneración" },
                  { key: "transporte", label: "Transporte" },
                  { key: "adelanto", label: "Adelanto" },
                  { key: "caja_chica", label: "Caja Chica" },
                ].map((m) => (
                  <button
                    key={m.key}
                    type="button"
                    onClick={() => {
                      setModoEmpleado(m.key);
                      resetBusquedas();
                    }}
                    style={{
                      width: 140,
                      padding: "6px 0",
                      fontSize: "0.82rem",
                      fontWeight: 600,
                      border: "1px solid #dee2e6",
                      borderRadius: 6,
                      cursor: "pointer",
                      background: modoEmpleado === m.key ? "#2c3e50" : "transparent",
                      color: modoEmpleado === m.key ? "#fff" : "#666",
                    }}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            )}

            {esEmpleado && (
              <>
                {esCajaChica && (
                  <div
                    style={{
                      fontSize: "0.75rem",
                      color: "#666",
                      background: "#f8f9fa",
                      borderRadius: 4,
                      padding: "8px 10px",
                      marginBottom: 12,
                    }}
                  >
                    Entrega de fondos de caja chica a un empleado. Se registra en <code>movimientos_caja</code> con{" "}
                    <code>categoria_pago=&quot;caja_chica&quot;</code>; aparece como cargo (DEBE) en su estado de cuenta
                    de caja chica.
                  </div>
                )}
                {esTransporte && (
                  <div
                    style={{
                      fontSize: "0.75rem",
                      color: "#666",
                      background: "#f8f9fa",
                      borderRadius: 4,
                      padding: "8px 10px",
                      marginBottom: 12,
                    }}
                  >
                    Asignación de transporte/movilización: el <strong>movimiento completo</strong> se registra para el
                    empleado. Persiste en <code>pagos_empleados</code> con <code>tipo=&quot;transporte&quot;</code> (sin
                    período — no netea un mes), no en <code>movimientos_caja</code>.
                  </div>
                )}
                {esAdelanto && (
                  <div
                    style={{
                      fontSize: "0.75rem",
                      color: "#666",
                      background: "#f8f9fa",
                      borderRadius: 4,
                      padding: "8px 10px",
                      marginBottom: 12,
                    }}
                  >
                    Adelanto de remuneración: el <strong>movimiento completo</strong> se imputa al período elegido.
                    Persiste en <code>pagos_empleados</code> con <code>tipo=&quot;adelanto&quot;</code> (reduce el saldo
                    de sueldo de ese mes), no en <code>movimientos_caja</code>.
                  </div>
                )}
                <div style={{ fontSize: "0.82rem", fontWeight: 600, marginBottom: 6 }}>Empleado</div>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Buscar empleado..."
                  value={empleadoBusqueda}
                  onChange={(e) => setEmpleadoBusqueda(e.target.value)}
                  style={{ marginBottom: 8 }}
                />
                <div
                  style={{
                    maxHeight: 180,
                    overflowY: "auto",
                    border: "1px solid #dee2e6",
                    borderRadius: 4,
                    marginBottom: 16,
                  }}
                >
                  {loading && empleados.length === 0 ? (
                    <div style={{ padding: 20, textAlign: "center", color: "#999" }}>Cargando...</div>
                  ) : empleadosFiltrados.length === 0 ? (
                    <div style={{ padding: 20, textAlign: "center", color: "#999" }}>Sin empleados</div>
                  ) : (
                    empleadosFiltrados.map((e) => {
                      const seleccionado = empleadoId === e.id;
                      return (
                        <div
                          key={e.id}
                          onClick={() => setEmpleadoId(e.id)}
                          style={{
                            padding: "8px 12px",
                            cursor: "pointer",
                            fontSize: "0.83rem",
                            borderBottom: "1px solid #f0f0f0",
                            background: seleccionado ? "#e8f4fd" : "transparent",
                            fontWeight: seleccionado ? 600 : 400,
                          }}
                        >
                          {e.nombre} {e.apellido}
                          {e._esEgresado && (
                            <span style={{ color: "#999", fontSize: "0.85em", marginLeft: 6 }}>(Egresado)</span>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
                {esAdelanto && empleadoId && (
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: "0.82rem", fontWeight: 600, marginBottom: 6 }}>Período que se imputa</div>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <select
                        className="form-select form-select-sm"
                        value={adelantoMes || ""}
                        onChange={(ev) => setAdelantoMes(ev.target.value ? Number(ev.target.value) : null)}
                        style={{ flex: "0 0 140px", fontSize: "0.82rem" }}
                      >
                        <option value="">Mes…</option>
                        {MESES.map((m, i) => (
                          <option key={i} value={i + 1}>
                            {m}
                          </option>
                        ))}
                      </select>
                      <input
                        type="number"
                        className="form-control form-control-sm"
                        placeholder="Año"
                        value={adelantoAño || ""}
                        onChange={(ev) => setAdelantoAño(ev.target.value ? Number(ev.target.value) : null)}
                        style={{ flex: "0 0 90px", fontSize: "0.82rem" }}
                      />
                      <span style={{ fontSize: "0.8rem", color: "#555" }}>
                        Monto: <strong>{formatAmount(monto, moneda)}</strong> (movimiento completo)
                      </span>
                    </div>
                  </div>
                )}
              </>
            )}

            {esRemuneracion && (
              <>
                {pendientesVisibles.length > 0 && (
                  <div
                    style={{
                      marginBottom: 10,
                      padding: "8px 10px",
                      border: "1px solid #d6e0ea",
                      borderRadius: 4,
                      background: "#f6f9fc",
                      fontSize: "0.78rem",
                    }}
                  >
                    <div style={{ fontWeight: 600, marginBottom: 4, color: "#345" }}>
                      Pendientes del empleado{" "}
                      <span style={{ fontWeight: 400, color: "#789" }}>
                        (tildá lo que cubre esta transferencia · misma fuente que Futuros/RRHH)
                      </span>
                    </div>
                    <div style={{ maxHeight: 180, overflowY: "auto" }}>
                      {pendientesVisibles.map((p) => {
                        const key = keyPendiente(p);
                        const tildado = componentes.some((c) => c.origen_pendiente === key);
                        return (
                          <label
                            key={key}
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              gap: 8,
                              padding: "2px 4px",
                              borderRadius: 3,
                              cursor: "pointer",
                              color: "#455",
                              background: tildado ? "#e8f5e9" : "transparent",
                            }}
                          >
                            <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                              <input type="checkbox" checked={tildado} onChange={() => togglePendiente(p)} />
                              {p.tipo === "sueldo" ? "Sueldo" : p.tipo === "comision" ? "Comisión" : "Bono"} ·{" "}
                              {p.periodoLabel}
                            </span>
                            <strong>{formatAmount(p.monto, moneda)}</strong>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: 6,
                  }}
                >
                  <div style={{ fontSize: "0.82rem", fontWeight: 600 }}>Desglose del pago</div>
                  <Button size="sm" variant="outline-secondary" onClick={agregarComponente}>
                    + Componente
                  </Button>
                </div>

                {componentes.map((c, idx) => {
                  // Los componentes que vienen de un pendiente tildado NO muestran card (el checkbox de
                  // "Pendientes del empleado" ya muestra tipo/período/monto). Siguen en el estado para el
                  // guardado y el cálculo de Total/diferencia/redondeo; solo no se renderiza su card.
                  if (c.origen_pendiente) return null;
                  const requierePeriodo = TIPOS_REQUIEREN_PERIODO.includes(c.tipo);
                  return (
                    <div
                      key={idx}
                      style={{
                        border: "1px solid #dee2e6",
                        borderRadius: 4,
                        padding: 10,
                        marginBottom: 8,
                        background: "#fafbfc",
                      }}
                    >
                      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
                        {/* Solo llegan componentes MANUALES (los tildados devuelven null arriba): dropdowns. */}
                        <select
                          className="form-select form-select-sm"
                          value={c.tipo}
                          onChange={(ev) => actualizarComponente(idx, "tipo", ev.target.value)}
                          style={{ flex: "0 0 130px", fontSize: "0.82rem" }}
                        >
                          {TIPOS_PAGO_EMPLEADO.map((t) => (
                            <option key={t} value={t}>
                              {t.charAt(0).toUpperCase() + t.slice(1)}
                            </option>
                          ))}
                        </select>
                        {requierePeriodo ? (
                          <>
                            <select
                              className="form-select form-select-sm"
                              value={c.periodo_mes || ""}
                              onChange={(ev) =>
                                actualizarComponente(
                                  idx,
                                  "periodo_mes",
                                  ev.target.value ? Number(ev.target.value) : null
                                )
                              }
                              style={{ flex: "0 0 120px", fontSize: "0.82rem" }}
                            >
                              <option value="">Mes…</option>
                              {MESES.map((m, i) => (
                                <option key={i} value={i + 1}>
                                  {m}
                                </option>
                              ))}
                            </select>
                            <input
                              type="number"
                              className="form-control form-control-sm"
                              placeholder="Año"
                              value={c.periodo_año || ""}
                              onChange={(ev) =>
                                actualizarComponente(
                                  idx,
                                  "periodo_año",
                                  ev.target.value ? Number(ev.target.value) : null
                                )
                              }
                              style={{ flex: "0 0 80px", fontSize: "0.82rem" }}
                            />
                          </>
                        ) : (
                          <span style={{ flex: 1, fontSize: "0.78rem", color: "#888" }}>(sin período)</span>
                        )}
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          placeholder="Monto"
                          value={c.monto}
                          onChange={(ev) => actualizarComponente(idx, "monto", ev.target.value)}
                          style={{ flex: 1, fontSize: "0.82rem", textAlign: "right" }}
                        />
                        {componentes.length > 1 && (
                          <button
                            type="button"
                            onClick={() => eliminarComponente(idx)}
                            style={{
                              border: "none",
                              background: "transparent",
                              color: "#c0392b",
                              cursor: "pointer",
                              fontSize: "1rem",
                              padding: "0 4px",
                            }}
                            title="Eliminar componente"
                          >
                            ×
                          </button>
                        )}
                      </div>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Notas (opcional)"
                        value={c.notas}
                        onChange={(ev) => actualizarComponente(idx, "notas", ev.target.value)}
                        style={{ fontSize: "0.78rem" }}
                      />
                    </div>
                  );
                })}

                <div
                  style={{
                    marginTop: 10,
                    padding: "8px 12px",
                    borderRadius: 4,
                    background: cuadra ? "#e8f5e9" : "#fdecea",
                    fontSize: "0.82rem",
                    display: "flex",
                    justifyContent: "space-between",
                    fontWeight: 600,
                  }}
                >
                  <span>Total componentes</span>
                  <span>
                    {formatAmount(totalComponentes, moneda)}{" "}
                    {!cuadra && (
                      <span style={{ color: "#c0392b", marginLeft: 8 }}>
                        (diferencia {formatAmount(diferencia, moneda)})
                      </span>
                    )}
                  </span>
                </div>

                {/* Diferencia de redondeo bancario: si no cuadra por unos pesos (≤ tolerancia) y hay
                    líneas tildadas, un click la absorbe en la última tildada → cuadra exacto. */}
                {puedeAjustarRedondeo && (
                  <div style={{ marginTop: 6, textAlign: "right" }}>
                    <Button size="sm" variant="outline-primary" onClick={ajustarRedondeo}>
                      Ajustar redondeo ({formatAmount(diferencia, moneda)}) en la última línea
                    </Button>
                  </div>
                )}
              </>
            )}

            {esFactoring && yaVinculadoFactoring && (
              <div
                style={{
                  padding: "16px",
                  background: "#f0f3f5",
                  borderRadius: 8,
                  border: "2px solid #8e44ad",
                  marginBottom: 12,
                }}
              >
                <div style={{ fontWeight: 700, color: "#8e44ad", marginBottom: 8 }}>
                  ✓ Movimiento ya vinculado a Factoring
                </div>
                <div style={{ fontSize: "0.85rem", color: "#333", marginBottom: 4 }}>
                  <strong>Cliente:</strong> {yaVinculadoFactoring.factura?.razon_social_receptor || "—"}
                </div>
                <div style={{ fontSize: "0.85rem", color: "#333", marginBottom: 4 }}>
                  <strong>Folio:</strong> {yaVinculadoFactoring.factura?.folio || "—"}
                </div>
                <div style={{ fontSize: "0.85rem", color: "#333", marginBottom: 4 }}>
                  <strong>Tipo:</strong> {factoringTipoVinc === "giro" ? "Giro (anticipo)" : "Excedente"}
                </div>
                <div style={{ fontSize: "0.85rem", color: "#333", marginBottom: 16 }}>
                  <strong>Proveedor:</strong> {yaVinculadoFactoring.proveedor?.nombre || "—"}
                </div>
                <button
                  type="button"
                  onClick={handleDesvincularFactoring}
                  disabled={loading}
                  style={{
                    background: "none",
                    border: "1px solid #c62828",
                    borderRadius: 4,
                    padding: "6px 16px",
                    fontSize: "0.82rem",
                    color: "#c62828",
                    cursor: loading ? "not-allowed" : "pointer",
                  }}
                >
                  {loading ? "Desvinculando..." : "Desvincular"}
                </button>
              </div>
            )}

            {esFactoring && !yaVinculadoFactoring && (
              <div style={{ padding: "8px 0" }}>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ fontSize: "0.78rem", fontWeight: 600, display: "block", marginBottom: 6 }}>
                    Tipo de vinculación
                  </label>
                  <div style={{ display: "flex", gap: 8 }}>
                    {[
                      { key: "giro", label: "Giro (anticipo)" },
                      { key: "excedente", label: "Excedente" },
                    ].map((t) => (
                      <button
                        key={t.key}
                        type="button"
                        onClick={() => {
                          setFactoringTipoVinc(t.key);
                          setFactoringOpIds([]);
                          resetBusquedas();
                        }}
                        style={{
                          padding: "6px 16px",
                          fontSize: "0.8rem",
                          fontWeight: 600,
                          border: "none",
                          borderRadius: 4,
                          cursor: "pointer",
                          background: factoringTipoVinc === t.key ? "#2c3e50" : "#e9ecef",
                          color: factoringTipoVinc === t.key ? "#fff" : "#333",
                        }}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>

                <label style={{ fontSize: "0.78rem", fontWeight: 600, display: "block", marginBottom: 6 }}>
                  {factoringTipoVinc === "giro"
                    ? "Operaciones con giro pendiente de cobro"
                    : "Operaciones con excedente pendiente de cobro"}
                </label>

                {(() => {
                  const candidatas = operacionesFactoring.filter((op) => {
                    const montoOp =
                      factoringTipoVinc === "giro" ? getGiroEfectivo(op) : Number(op.monto_remanente || 0);
                    const pendiente =
                      factoringTipoVinc === "giro"
                        ? !op.movimiento_bancario_adelanto_id
                        : !op.movimiento_bancario_remanente_id;
                    return pendiente && montoOp > 0;
                  });
                  if (candidatas.length === 0) {
                    return (
                      <div style={{ color: "#999", fontSize: "0.82rem", padding: "12px 0" }}>
                        Sin operaciones pendientes. Verificá el tipo de vinculación.
                      </div>
                    );
                  }
                  const candidatasBuscadas = candidatas.filter((op) =>
                    coincideBusqueda(factoringBusqueda, {
                      textos: [op.factura?.razon_social_receptor, op.factura?.folio, op.proveedor?.nombre],
                      montos: [factoringTipoVinc === "giro" ? getGiroEfectivo(op) : op.monto_remanente],
                    })
                  );
                  return (
                    <>
                      <BuscadorInput
                        value={factoringBusqueda}
                        onChange={setFactoringBusqueda}
                        placeholder="Buscar por cliente, folio o monto..."
                      />
                      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        {candidatasBuscadas.map((op) => {
                          const montoOp = factoringTipoVinc === "giro" ? getGiroEfectivo(op) : op.monto_remanente;
                          const selected = factoringOpIds.includes(op.id);
                          return (
                            <div
                              key={op.id}
                              onClick={() =>
                                setFactoringOpIds((prev) =>
                                  prev.includes(op.id) ? prev.filter((id) => id !== op.id) : [...prev, op.id]
                                )
                              }
                              style={{
                                padding: "10px 14px",
                                borderRadius: 6,
                                border: `2px solid ${selected ? "#2c3e50" : "#dee2e6"}`,
                                background: selected ? "#f0f3f5" : "#fff",
                                cursor: "pointer",
                                fontSize: "0.82rem",
                              }}
                            >
                              <div style={{ fontWeight: 600 }}>
                                {op.factura?.razon_social_receptor || "—"}
                                {" — Folio "}
                                {op.factura?.folio || "—"}
                              </div>
                              <div style={{ color: "#666", marginTop: 2 }}>
                                {op.proveedor?.nombre || "—"}
                                {" · Vence "}
                                {op.fecha_cobro_final || "—"}
                                {" · "}
                                <span style={{ color: "#198754", fontWeight: 700 }}>
                                  {formatAmount(montoOp, moneda)}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  );
                })()}

                {factoringOpIds.length > 0 && (
                  <div style={{ marginTop: 12, fontSize: "0.82rem" }}>
                    {matchExacto || matchOk ? (
                      <span
                        style={{
                          display: "inline-block",
                          padding: "4px 10px",
                          borderRadius: 4,
                          background: "#d1e7dd",
                          color: "#0f5132",
                          fontWeight: 600,
                        }}
                      >
                        {matchExacto
                          ? "Match exacto"
                          : `Diferencia: ${formatAmount(diffAbs, moneda)} (${(diffPct * 100).toFixed(2)}%)`}
                      </span>
                    ) : matchWarning ? (
                      <span style={{ color: "#b8730a", fontWeight: 600 }}>
                        Diferencia de {formatAmount(diffAbs, moneda)} ({(diffPct * 100).toFixed(2)}%) — podés continuar
                        pero verificá los montos
                      </span>
                    ) : matchError ? (
                      <span style={{ color: "#c0392b", fontWeight: 600 }}>
                        Diferencia de {formatAmount(diffAbs, moneda)} ({(diffPct * 100).toFixed(2)}%) — no se puede
                        confirmar
                      </span>
                    ) : null}
                  </div>
                )}
              </div>
            )}

            {/* Cobro export (modelo 7.18): costos del giro. Franco copia del SWIFT el monto
                instruido y la comisión del banco internacional; el resto se deriva. El cobro
                export EXIGE estos datos (gate del botón Confirmar). Wizard: Paso 2. */}
            {esCobroExportacion && pasoExport === 2 && (
              <div
                style={{
                  marginTop: 16,
                  padding: "12px 14px",
                  background: "#f3f7fb",
                  border: "1px solid #cfe0f0",
                  borderRadius: 6,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: 10,
                    gap: 10,
                  }}
                >
                  <div style={{ fontSize: "0.85rem", fontWeight: 700 }}>Costos del giro</div>
                  <label
                    className="btn btn-sm btn-outline-primary"
                    style={{
                      fontSize: "0.74rem",
                      marginBottom: 0,
                      cursor: cargandoSwift ? "wait" : "pointer",
                      whiteSpace: "nowrap",
                    }}
                    title="Lee un PDF SWIFT (pacs.008) y autocompleta los montos. El cuadre valida igual."
                  >
                    {cargandoSwift ? "Leyendo…" : "📄 Cargar SWIFT (PDF)"}
                    <input
                      type="file"
                      accept="application/pdf,.pdf"
                      style={{ display: "none" }}
                      disabled={cargandoSwift}
                      onChange={handleCargarSwift}
                    />
                  </label>
                </div>

                <div style={{ display: "flex", gap: 12, marginBottom: 10 }}>
                  <label style={{ flex: 1, fontSize: "0.78rem" }}>
                    Monto instruido (InstdAmt) · US$
                    <input
                      type="number"
                      step="0.01"
                      className="form-control form-control-sm"
                      value={giroInstdAmt}
                      onChange={(e) => setGiroInstdAmt(e.target.value)}
                      placeholder="0.00"
                      style={{ marginTop: 4 }}
                    />
                  </label>
                  <label style={{ flex: 1, fontSize: "0.78rem" }}>
                    Comisión banco internacional (Citibank) · US$
                    <input
                      type="number"
                      step="0.01"
                      className="form-control form-control-sm"
                      value={giroCitibank}
                      onChange={(e) => setGiroCitibank(e.target.value)}
                      placeholder="0.00"
                      style={{ marginTop: 4 }}
                    />
                  </label>
                  <label style={{ flex: 1, fontSize: "0.78rem" }}>
                    Monto recibido Santander (IntrBkSttlmAmt) · US$
                    <input
                      type="number"
                      step="0.01"
                      className="form-control form-control-sm"
                      value={giroSantanderRecibido}
                      onChange={(e) => setGiroSantanderRecibido(e.target.value)}
                      placeholder="(del aviso Santander)"
                      style={{ marginTop: 4 }}
                    />
                  </label>
                </div>

                <button
                  type="button"
                  onClick={() => setGiroSwiftOpen((v) => !v)}
                  style={{
                    border: "none",
                    background: "transparent",
                    color: "#2c3e50",
                    fontSize: "0.76rem",
                    fontWeight: 600,
                    cursor: "pointer",
                    padding: 0,
                    marginBottom: giroSwiftOpen ? 8 : 0,
                  }}
                >
                  {giroSwiftOpen ? "▾" : "▸"} Datos del SWIFT (opcional)
                </button>
                {giroSwiftOpen && (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
                    <label style={{ fontSize: "0.76rem" }}>
                      UETR
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={giroUetr}
                        onChange={(e) => setGiroUetr(e.target.value)}
                        style={{ marginTop: 4 }}
                      />
                    </label>
                    <label style={{ fontSize: "0.76rem" }}>
                      Ordenante
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={giroOrdenante}
                        onChange={(e) => setGiroOrdenante(e.target.value)}
                        style={{ marginTop: 4 }}
                      />
                    </label>
                    <label style={{ fontSize: "0.76rem" }}>
                      BIC banco ordenante
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={giroOrdenanteBic}
                        onChange={(e) => setGiroOrdenanteBic(e.target.value)}
                        style={{ marginTop: 4 }}
                      />
                    </label>
                    <label style={{ fontSize: "0.76rem" }}>
                      Fecha valor
                      <input
                        type="date"
                        className="form-control form-control-sm"
                        value={giroFechaValor}
                        onChange={(e) => setGiroFechaValor(e.target.value)}
                        style={{ marginTop: 4 }}
                      />
                    </label>
                  </div>
                )}

                <div
                  style={{
                    borderTop: "1px solid #cfe0f0",
                    paddingTop: 8,
                    marginTop: 8,
                    fontSize: "0.82rem",
                    display: "grid",
                    gridTemplateColumns: "1fr auto",
                    gap: "3px 12px",
                  }}
                >
                  <span>Facturado (Σ US$ facturas)</span>
                  <span style={{ textAlign: "right", fontWeight: 600 }}>{formatAmount(giroFacturado, "USD")}</span>
                  <span>Comisión Place Exchange</span>
                  <span style={{ textAlign: "right", fontWeight: 600 }}>{formatAmount(giroComisionPlace, "USD")}</span>
                  <span>Comisión bancaria internacional</span>
                  <span style={{ textAlign: "right", fontWeight: 600 }}>{formatAmount(giroCitibankNum, "USD")}</span>
                  <span>Comisión bancaria local (Santander)</span>
                  <span style={{ textAlign: "right", fontWeight: 600 }}>
                    {formatAmount(giroComisionSantander, "USD")}
                  </span>
                  <span style={{ fontWeight: 600 }}>Disponible (movimiento)</span>
                  <span style={{ textAlign: "right", fontWeight: 600 }}>{formatAmount(monto, "USD")}</span>
                  {!giroInstdValido ? (
                    <>
                      <span style={{ fontWeight: 700 }}>Cuadre</span>
                      <span style={{ textAlign: "right", fontWeight: 700, color: "#c0392b" }}>
                        Cargá el monto instruido
                      </span>
                    </>
                  ) : giroModoParcial ? (
                    <>
                      <span style={{ fontWeight: 700 }}>Cuadre</span>
                      <span style={{ textAlign: "right", fontWeight: 700, color: "#e67e22" }}>
                        cuadre parcial — falta monto recibido
                      </span>
                    </>
                  ) : (
                    <>
                      <span>(a) Instruido − Citibank = Recibido</span>
                      <span
                        style={{ textAlign: "right", fontWeight: 700, color: giroCuadreAOk ? "#2e7d32" : "#c0392b" }}
                      >
                        {giroCuadreAOk ? "✓" : `✗ ${formatAmount(giroCuadreA, "USD")}`}
                      </span>
                      <span>(b) Cascada = Disponible</span>
                      <span
                        style={{ textAlign: "right", fontWeight: 700, color: giroCuadreBOk ? "#2e7d32" : "#c0392b" }}
                      >
                        {giroCuadreBOk ? "✓" : `✗ ${formatAmount(giroCuadreB, "USD")}`}
                      </span>
                      <span style={{ fontWeight: 700 }}>Cuadre</span>
                      <span
                        style={{ textAlign: "right", fontWeight: 700, color: giroCuadreReal ? "#2e7d32" : "#c0392b" }}
                      >
                        {giroCuadreReal ? "✓ cuadra (real)" : "✗ no cuadra"}
                      </span>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Transferencia internacional recibida (no vinculada aún): comisión de recepción
                neteada por Santander. Vincular acá la saca de "Por pagar" sin generar débito.
                Wizard export: Paso 3 (opcional). Para transferencias no-export, sin wizard. */}
            {esTransferenciaRecibida && (!esCobroExportacion || pasoExport === 3) && (
              <div
                style={{
                  marginTop: 16,
                  padding: "12px 14px",
                  background: "#f8f9fa",
                  border: "1px solid #dee2e6",
                  borderRadius: 6,
                }}
              >
                <ComisionNeteadaSelector
                  key={`ingreso-${movimiento.id}`}
                  titulo="Comisión por recepción (neteada)"
                  cargarVinculadas={() => getComisionesIngresoNeteadas(movimiento.id)}
                  cargarDisponibles={() => getFacturasComisionIngresoDisponibles(pais)}
                  vincular={(facturaId) => vincularComisionIngreso(movimiento.id, facturaId)}
                  desvincular={(filaId) => desvincularComisionIngreso(filaId)}
                  destacar={(f) =>
                    !!f.fecha_emision &&
                    !!movimiento?.fecha &&
                    Math.abs(new Date(f.fecha_emision) - new Date(movimiento.fecha)) / 86400000 <= 3
                  }
                  formatAmount={formatAmount}
                  formatFecha={(s) => (s ? `${s.substring(8, 10)}/${s.substring(5, 7)}` : "—")}
                  moneda={pais === "Perú" ? "PEN" : "CLP"}
                  onCambio={onRefreshFuturos}
                  mostrarDteAnioAnterior={mostrarDteAnioAnterior}
                  setMostrarDteAnioAnterior={setMostrarDteAnioAnterior}
                />
              </div>
            )}
          </>
        )}
        {guardMontoCapex?.warn && (
          <div
            style={{
              marginTop: 12,
              padding: "8px 12px",
              background: "#fff3cd",
              border: "1px solid #ffe08a",
              borderRadius: 6,
              fontSize: "0.8rem",
              color: "#856404",
            }}
          >
            ⚠ {guardMontoCapex?.mensaje}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={onClose}>
          {yaEstaVinculado || (esFactoring && yaVinculadoFactoring) ? "Cerrar" : "Cancelar"}
        </Button>
        {esCobroExportacion && !yaEstaVinculado ? (
          // Wizard export: Atrás / Siguiente entre los 3 pasos; Confirmar solo en el Paso 3.
          // El gate de habilitación (confirmarDisabled) es el mismo de siempre: exige facturas
          // seleccionadas + cuadre de costos OK (la comisión Santander del Paso 3 es opcional).
          <>
            {pasoExport > 1 && (
              <Button variant="outline-secondary" size="sm" onClick={() => setPasoExport((p) => p - 1)}>
                ← Atrás
              </Button>
            )}
            {pasoExport < 3 && (
              <Button variant="primary" size="sm" onClick={() => setPasoExport((p) => p + 1)}>
                Siguiente →
              </Button>
            )}
            {pasoExport === 3 && (
              <Button variant="primary" size="sm" onClick={handleConfirmar} disabled={confirmarDisabled}>
                {loading ? "Guardando..." : "Confirmar vinculación"}
              </Button>
            )}
          </>
        ) : (
          !(esFactoring && yaVinculadoFactoring) &&
          !yaEstaVinculado && (
            <Button variant="primary" size="sm" onClick={handleConfirmar} disabled={confirmarDisabled}>
              {loading ? "Guardando..." : esPrestamo ? "Vincular a cuota" : "Confirmar vinculación"}
            </Button>
          )
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default VincularMovimientoModal;
